/**
 * TeamPageV2 — proper v2 redesign of Team Management (G108).
 *
 * Applies the canonical Agentlets v2 inventory pattern (per
 * AgentsInventoryPageV2 / ApprovalCenterPageV2):
 *
 *   Page-level TabNav (Users / Groups / Connector Groups / Escalation)
 *     ↓
 *   Per-tab inventory: FacetRail (left) + v2 Table (center) + Slideout (right)
 *     - Slideout for Users folds in Access Review (Profile · Memberships ·
 *       Agent Access · Workflow Access · Effective Permissions)
 *     - Assignments tab is delegated to the v1 page until v2 wiring lands
 *
 * Reuses every backend code path from v1 TeamPage:
 *   - usePlatform() for users/groups/connectorGroups/agents loaders + CRUD
 *   - api.getUserMemberships, api.getWorkflowAssignments for slideout data
 *   - Modals (Add User / Edit User / Add Group / Delete Confirm / Invite User)
 *     dispatch through v1 handlers via shared TeamPageModalsContext (TBD)
 *     OR open the v1 page for those flows in this iteration.
 *
 * What's reimagined (NOT whitewashing):
 *   - Per-tab FacetRail replaces top filter dropdowns
 *   - Single Slideout for triage replaces stacked modals
 *   - Bulk action toolbar (toggle status / delete) appears with selection
 *   - Density toggle persisted to localStorage
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePlatform } from '../contexts/PlatformContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { isRebirthAdministerEnabled } from '../utils/featureFlags';
import * as api from '../services/api';
/* V332 — shared mirror of the backend's Skill.requiresHumanApproval(); marks which skills need
 * an approver named. Same predicate the runtime gate uses, so displayed == enforced. */
import { requiresHumanApproval } from '../constants/autonomy';
import {
  RefreshCw, Plus, Send, Users as UsersIcon, Shield, Layers, AlertTriangle,
  Edit2, Trash2, Power, Mail, Activity, ChevronRight, ChevronDown, CheckCircle2, X, UserPlus,
  Building2, Server, ExternalLink, Search,
  KeyRound,
} from 'lucide-react';
import {
  /* Modal — the platform's own confirm dialog (sticky header/body/footer).
   * Used for the destructive Delete-group confirm so we never fall back to
   * the browser's native window.confirm. */
  Modal,
  Button, Pill, Card, Slideout, Table, FacetRail, EmptyState,
  TabNav, MetricCard,
  /* G586 — admin-initiated password reset modal. Two delivery modes
   * (reset link / temp password); RBAC enforced server-side. */
  PasswordResetModal,
  /* V347 — replaces the free-text approvers box. Selections are user ids, which is what
   * EscalationService.resolveTargetFromTier requires; text was silently unresolvable. */
  ApproverPicker,
  /* Reused for the IGA connector choice — the same searchable single-select CustomerPicker uses.
     A native <select> here would have been a third spelling of the same control. */
  EntityPicker,
} from '../components/v2';
import { selectBulkTargets, summarizeSettled } from '../utils/bulkUsers';
import { useConfirm } from '../hooks/useConfirm';
import { Pagination, usePaginationState } from '../components/common/Pagination';
import EntityLabel from '../components/common/EntityLabel';
/* Shared membership-management surface — the single place a user's org/tenant/instance
   memberships are added, removed, and re-defaulted (reused by Multi-Tenancy too). */
import MembershipManager from '../components/v2/MembershipManager';

import TeamPage from './TeamPage';
import { ROLE_LEVELS, ROLE_OPTIONS as BUILT_IN_ROLE_OPTIONS } from '../data/roleLevels';

// V208 / RBAC v2 — 12-role taxonomy. Ordered by privilege level (descending)
// then by Platform → Customer → Org → Tenant → Instance → Functional →
// Read-only tier. Mirrors RbacService.ROLE_LEVELS on the backend:
//   Platform Admin = Super Admin = 5
//   Org Admin = 4
//   Tenant Admin = 3
//   Instance Admin / Promoter / Developer / Approver / Compliance / Operator
//     / Auditor = 2
//   Viewer = 1
// See docs/RBAC_REDESIGN_V2.md for the full role × resource entitlement
// matrix. The Team filter UI displays every role even if the current org
// has zero users in that role — the count column lets operators see at
// a glance which roles are unassigned.
// The built-in role list and the role hierarchy both live in
// src/data/roleLevels.js. This page carried its own copy of each; a new
// built-in role then had to be remembered in five places, and twice it was
// not. Custom roles are still unioned in below from the server.
const ROLE_OPTIONS = BUILT_IN_ROLE_OPTIONS;
// The shared built-in role→level map, aliased because this page's components
// pass it around by this name. Object.freeze makes it a STABLE reference, so it
// is safe in a hook dep array. A role absent from it (an org CUSTOM role) is
// handled by callers passing the server-authoritative level to canManageRole.
const ROLE_LEVELS_FE = ROLE_LEVELS;
const ROLE_TONE = {
  'Platform Admin': 'danger',
  'Super Admin':    'danger',
  'Org Admin':      'action',
  'Tenant Admin':   'trust',
  'Protect':        'trust',
  'Instance Admin': 'trust',
  'Promoter':       'cyan',
  'Developer':      'cyan',
  // V208 — new functional roles. Distinct tones so filter badges + table
  // role pills are scannable at a glance.
  'Approver':       'warning',
  'Compliance':     'action',
  'Operator':       'cyan',
  'Auditor':        'muted',
  'Viewer':         'muted',
};
const STATUS_OPTIONS = [
  { key: 'active',      label: 'Active',      tone: 'success' },
  { key: 'inactive',    label: 'Inactive',    tone: 'muted' },
  { key: 'pending',     label: 'Pending',     tone: 'warning' },
  { key: 'invited',     label: 'Invited',     tone: 'warning' },
  { key: 'suspended',   label: 'Suspended',   tone: 'danger' },
  // DEF-RBAC-USER-LIFECYCLE-001 — soft-deactivated: visible, recoverable, no login.
  { key: 'deactivated', label: 'Deactivated', tone: 'danger' },
];

function initials(u) {
  const f = (u.firstName || '').trim();
  const l = (u.lastName || '').trim();
  if (f || l) return ((f[0] || '') + (l[0] || '')).toUpperCase();
  if (u.email) return u.email.slice(0, 2).toUpperCase();
  return '??';
}

function fullName(u) {
  return [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email || 'Unknown';
}

/* ── Users tab (canonical inventory) ─────────────────────────────── */

function UsersInventory({ orgs, tenants, pendingAction, onConsumeAction, onNavigateToTab }) {
  const navigate = useNavigate();
  const platform = usePlatform();
  // V208 / RBAC v2 — destructure canManageRole so the role dropdown in the
  // Add/Edit User slideout can scope-filter the options to roles the
  // current user is authorized to grant.
  const { session, user: currentUser, hasPermission, canManageRole } = useAuth();

  /* DEF-FE-TEAM-USER-ACTION-GATING-001 — Reset / Delete row actions
   * must be hidden when the current actor lacks authority over the
   * target user. Backend already 403s the mutation (verified end-to-
   * end via the SA→PA probe matrix), but rendering the buttons
   * surprises operators. Mirror the server-side rule from
   * RbacService.requireCanManageTargetUser:
   *   • self-action always permitted (surface-level decisions block
   *     truly destructive self-actions separately — e.g. password
   *     reset surface rejects self-reset)
   *   • Platform Admin bypass (top-tier authority)
   *   • otherwise strict actorLevel > targetLevel
   * The Team page doesn't load the target user's full grant list
   * here (only the AppUser.role display field is available), so this
   * is a CONSERVATIVE client-side gate — if the display role under-
   * states the target's actual privilege, the BE still blocks. */
  const actorIsPlatformAdmin = (session?.role || currentUser?.role) === 'Platform Admin';
  // actorRoleLevel and canManageTargetUser are defined below, after the
  // custom-role level lookup (levelForRole) they depend on — BOTH the acting
  // user's role AND the target's role must resolve a custom role to its real
  // server level (2), not the unknown-role default, or the row actions mis-gate
  // (a custom-role actor would read level 0; a custom-role target, level 6).
  const { addToast } = useToast();
  const orgId    = session?.orgId;
  const tenantId = session?.tenantId;
  const isSuperAdmin = !!(hasPermission && hasPermission('admin.system'));

  /* RBAC Phase 2 — ORG-SCOPED CUSTOM ROLES must be assignable from the Team
   * surface (Add / Edit / Invite user, and the memberships picker) and
   * filterable in the Users list — not just the hardcoded built-in ROLE_OPTIONS.
   * Load the active org's custom roles once; each catalog entry carries its
   * AUTHORITATIVE level (server CUSTOM_ROLE_LEVEL), so authority is evaluated by
   * the shared canManageRole(role, level) — no duplicate level rule here, and no
   * hardcoded custom-role list. Fails open to built-ins only (empty) on error. */
  const [customRoles, setCustomRoles] = useState([]); // [{ role, level, permissions }]
  useEffect(() => {
    let alive = true;
    Promise.resolve(api.getCustomRoles())
      .then((res) => {
        // apiFetch resolves to { ok, data, etag } — unwrap with the file's
        // standard res.data || res convention before reading the payload.
        const b = res && (res.data || res);
        if (alive && b && Array.isArray(b.customRoles)) setCustomRoles(b.customRoles);
      })
      .catch(() => { /* built-ins remain available; custom roles just won't list */ });
    return () => { alive = false; };
  }, []);
  // Authoritative level for ANY role name: server-reported level for a custom
  // role, else the built-in FE level map. Returned to canManageRole as the
  // override so custom roles are gated correctly instead of resolving to 0.
  const customLevelByName = useMemo(() => {
    const m = {};
    (customRoles || []).forEach((c) => { if (c && c.role) m[c.role] = c.level; });
    return m;
  }, [customRoles]);
  const levelForRole = useCallback(
    (r) => (r in customLevelByName ? customLevelByName[r] : ROLE_LEVELS_FE[r]),
    [customLevelByName],
  );
  // canManageRole bound to authoritative per-role levels — one-arg contract so
  // it drops straight into existing call sites (e.g. MembershipManager).
  const canManageRoleL = useCallback(
    (r) => canManageRole(r, levelForRole(r)),
    [canManageRole, levelForRole],
  );
  // Built-in ∪ org-custom role names (built-ins first, de-duped). The single
  // source for every role picker and the Role filter on this surface.
  const allRoleNames = useMemo(() => {
    const seen = new Set(ROLE_OPTIONS);
    const extra = (customRoles || [])
      .map((c) => c && c.role)
      .filter((r) => r && !seen.has(r));
    return [...ROLE_OPTIONS, ...extra];
  }, [customRoles]);

  /* DEF-FE-TEAM-USER-ACTION-GATING-001 — row-action authority gate (Edit /
   * Reset pwd / Deactivate). Mirrors the server's requireCanManageTargetUser:
   * self + Platform-Admin bypass, else strict actorLevel > targetLevel. The
   * target's level comes from levelForRole, which resolves an org CUSTOM role
   * (e.g. Dealer Manager / Dealer Sales Rep) to its AUTHORITATIVE server level
   * (2) instead of the unknown-role default — without this a custom-role user
   * fell through to level 6 and even a Super Admin saw no row actions. A role
   * that is genuinely unknown still defaults to 6 (conservative: hide; the BE
   * remains the backstop). */
  // Actor's own level, resolved through the same authoritative lookup — a
  // custom-role actor resolves to their server level (2) rather than 0.
  const actorRoleLevel = (() => {
    const lvl = levelForRole(session?.role || currentUser?.role);
    return typeof lvl === 'number' ? lvl : 0;
  })();
  const canManageTargetUser = (target) => {
    if (!target) return false;
    if (currentUser && target.id === currentUser.id) return true;     // self
    if (actorIsPlatformAdmin) return true;                            // PA bypass
    const lvl = levelForRole(target.role);
    const targetLevel = typeof lvl === 'number' ? lvl : 6;           // defensive
    return actorRoleLevel > targetLevel;
  };

  const {
    users = [], loadUsers, deleteUser: deleteUserApi,
    /* G362 — Pull assignments + groups + agents + workflows so the
     * per-user slideout can compute Agent / Workflow access (no more
     * "Manage in v1" bounce). */
    assignments = [], loadAssignments,
    connectorAssignments = [], loadConnectorAssignments,
    agents = [], loadAgents,
    workflows = [], loadWorkflows,
    connectors = [], loadConnectors,
    connectorGroups = [], loadConnectorGroups,
    groups: groupsList = [], loadGroups,
  } = platform;

  /* G362 — Load access-related data once when this surface mounts so
   * the per-user slideout has the join data ready.
   *
   * Task #116 — Per-agent fan-out for assignments + connector
   * assignments. The platform-context loaders only support per-agent
   * fetches (api.getAssignments / getAgentConnectorAssignments take
   * an agentId and short-circuit to an empty array when it's missing,
   * via the G373 UUID guard in api.js). Before this fix the page
   * called loadAssignments() with no arg → guard returned empty →
   * every count column rendered 0. The correct shape is to iterate
   * the agent list and load each agent's assignments — the context
   * loader merges per-agent fetches into the global assignments
   * array. */
  useEffect(() => {
    if (typeof loadAgents === 'function') loadAgents();
    if (typeof loadWorkflows === 'function') loadWorkflows();
    if (typeof loadConnectors === 'function') loadConnectors();
    if (typeof loadConnectorGroups === 'function') loadConnectorGroups();
    if (typeof loadGroups === 'function') loadGroups();
  }, [loadAgents, loadWorkflows, loadConnectors, loadConnectorGroups, loadGroups]);

  /* Task #116 — re-run the per-agent fan-out whenever the agent list
   * updates. This effect chains on the agents array so freshly-loaded
   * agents (from the loadAgents() above) trigger the assignment
   * fetches in a second pass. Fan-out is O(N) requests where N is
   * the tenant agent count — typically 5–50 today; the cost is
   * acceptable until we have a tenant-bulk endpoint. */
  useEffect(() => {
    if (!Array.isArray(agents) || agents.length === 0) return;
    if (typeof loadAssignments !== 'function') return;
    if (typeof loadConnectorAssignments !== 'function') return;
    agents.forEach((a) => {
      if (a && a.id) {
        loadAssignments(a.id);
        loadConnectorAssignments(a.id);
      }
    });
  }, [agents, loadAssignments, loadConnectorAssignments]);

  /* G199 — V2-native Add User / Edit User modals. The page header comment
   * acknowledged these were "open the v1 page in this iteration" — wiring
   * them properly now since user CRUD is a top-traffic admin action.
   * Calls api.createUser / api.updateUser / api.deleteUser. */
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  // The user's DEFAULT (home) grant {orgId, tenantId}, loaded when the edit
  // modal opens. Submit uses it to decide whether the edited scope IS the
  // default — only then may AppUser.role + the default grant move together.
  const [editDefault, setEditDefault] = useState(null);
  // DEF-RBAC-CUSTOMER-MOVE-001 — the raw grant the edit modal is editing
  // ({membershipId, orgId, tenantId}). When the operator changes the scope of an
  // existing grant, we MOVE that grant (atomic, no leftover) instead of adding a
  // second membership the operator then has to hand-delete.
  const [editGrantOrig, setEditGrantOrig] = useState(null);
  const [userForm, setUserForm] = useState({ email: '', name: '', role: 'Developer', status: 'active', orgId: '', tenantId: '' });
  const [userSaving, setUserSaving] = useState(false);
  const [userError, setUserError] = useState(null);
  /* G586 — Password-reset modal target. When non-null, the modal is
   * mounted with this user as its target. Backend re-enforces RBAC on
   * submit, so a stale client-side gate can't open a forbidden flow. */
  const [resetTarget, setResetTarget] = useState(null);
  /* Part 3 — "Email" now sends a chosen template via the platform Email (SMTP)
   * connector instead of opening the OS mail client (mailto). */
  const [emailPicker, setEmailPicker] = useState({ open: false, target: null, templates: [], templateId: '', sending: false, error: null, sent: false });
  const openEmailPicker = async (user) => {
    setEmailPicker({ open: true, target: user, templates: [], templateId: '', sending: false, error: null, sent: false });
    try {
      const res = await api.getTemplates('email');
      const raw = (res && (res.data || res)) || [];
      const list = Array.isArray(raw) ? raw : (raw.items || raw.content || raw.templates || []);
      setEmailPicker((p) => ({ ...p, templates: list, templateId: list[0]?.id || '' }));
    } catch (e) {
      setEmailPicker((p) => ({ ...p, error: e?.message || 'Could not load email templates.' }));
    }
  };
  const submitUserEmail = async () => {
    if (!emailPicker.templateId) { setEmailPicker((p) => ({ ...p, error: 'Pick a template.' })); return; }
    setEmailPicker((p) => ({ ...p, sending: true, error: null }));
    try {
      const res = await api.sendUserEmail(emailPicker.target.id, emailPicker.templateId);
      if (!res || res.ok === false) {
        setEmailPicker((p) => ({ ...p, sending: false, error: res?.error || res?.data?.error || 'Send failed.' }));
        return;
      }
      addToast({ tone: 'success', title: `Email sent to ${emailPicker.target.email}` });
      setEmailPicker((p) => ({ ...p, sending: false, sent: true, open: false }));
    } catch (e) {
      setEmailPicker((p) => ({ ...p, sending: false, error: e?.message || String(e) }));
    }
  };
  const openAddUser = () => {
    setEditUser(null);
    // Default org/tenant to the caller's ambient scope (preselected for
    // single-org admins; a Platform/Super Admin can change it via the picker).
    setUserForm({ email: '', name: '', role: 'Developer', status: 'active', orgId: orgId || '', tenantId: tenantId || '' });
    setUserError(null);
    setAddUserOpen(true);
  };
  const openEditUser = (u) => {
    setEditUser(u);
    // Prefer the grant for the SCOPE the operator is viewing (selected
    // customer/org) so the modal edits THAT grant — not always the default.
    // This is why editing an existing user's role in a specific customer now
    // targets the right membership instead of silently mutating their default.
    const g = displayGrant(u);
    setUserForm({
      email: u.email || '',
      name: u.name || u.fullName || '',
      role: (g && g.role) || u.role || 'Developer',
      status: u.status || 'active',
      orgId: (g && g.orgId) || u.orgId || u.org_id || '',
      tenantId: (g && g.tenantId) || u.tenantId || u.tenant_id || '',
    });
    setEditDefault(null);
    setEditGrantOrig(null);
    setUserError(null);
    setAddUserOpen(true);
    // Load memberships to record the user's DEFAULT grant (so submit knows
    // whether the edited scope is the default) and, if we had no scoped grant
    // to prefill from, fall back to the default placement.
    (async () => {
      try {
        const res = await api.getUserMemberships(u.id);
        const list = (res && (res.data || res)) || [];
        const arr = Array.isArray(list) ? list : [];
        const def = arr.find((m) => m.isDefault) || arr[0] || null;
        // Record the raw grant the modal is editing (the viewed scope, else the
        // default) so a scope change becomes an atomic MOVE, not an add+leftover.
        const editedRaw = (g
          ? arr.find((m) => (m.orgId || m.org_id) === g.orgId && (m.tenantId || m.tenant_id) === g.tenantId)
          : def) || def || null;
        if (editedRaw) {
          setEditGrantOrig({
            membershipId: editedRaw.id || editedRaw.membershipId || null,
            orgId: editedRaw.orgId || editedRaw.org_id || null,
            tenantId: editedRaw.tenantId || editedRaw.tenant_id || null,
          });
        }
        if (def) {
          setEditDefault({
            orgId: def.orgId || def.org_id || null,
            tenantId: def.tenantId || def.tenant_id || null,
          });
          if (!g) {
            setUserForm((f) => ({
              ...f,
              orgId: def.orgId || def.org_id || f.orgId,
              tenantId: def.tenantId || def.tenant_id || f.tenantId,
              role: (f.role && f.role !== 'Developer') ? f.role : (def.role || f.role),
            }));
          }
        }
      } catch { /* non-fatal — fall back to the row's values */ }
    })();
  };
  const submitUser = async () => {
    setUserError(null);
    if (!userForm.email?.trim()) { setUserError('Email is required.'); return; }
    if (!userForm.orgId) { setUserError('Organization is required.'); return; }
    if (!userForm.tenantId) { setUserError('Tenant is required.'); return; }
    setUserSaving(true);
    try {
      /* DEF-RBAC-CUSTOMER-MOVE-001 — the operator changed the SCOPE (org/tenant,
       * i.e. customer) of an existing grant. Repoint that grant atomically via
       * the move endpoint instead of adding a second membership they then have to
       * hand-delete (the sequence that led to an accidental hard-delete of a whole
       * user). Identity first, then move; early-return leaves all non-move edit
       * logic below untouched. */
      const isScopeMove = editUser && editGrantOrig && editGrantOrig.membershipId
        && (editGrantOrig.orgId !== userForm.orgId || editGrantOrig.tenantId !== userForm.tenantId);
      if (isScopeMove) {
        const idRes = await api.updateUser(editUser.id, {
          email: userForm.email.trim(), name: userForm.name.trim() || null, status: userForm.status,
        });
        if (idRes && idRes.ok === false) {
          setUserError(idRes?.error || idRes?.data?.error || `Identity update failed (HTTP ${idRes?.status || '?'}).`);
          return;
        }
        const mvRes = await api.moveUserMembership(editUser.id, editGrantOrig.membershipId, {
          orgId: userForm.orgId, tenantId: userForm.tenantId, role: userForm.role,
        });
        if (!mvRes || mvRes.ok === false) {
          setUserError(mvRes?.error || mvRes?.data?.error || `Move failed (HTTP ${mvRes?.status || '?'}).`);
          return;
        }
        addToast({ tone: 'success', title: 'Membership moved' });
        setAddUserOpen(false);
        await loadUsers?.({ orgId, tenantId });
        return;
      }

      /* DEF-RBAC-TEAM-PROJECTION-001 — a user holds N grants. Editing must
       * touch ONLY the grant for the chosen scope. Is that scope the user's
       * DEFAULT (home)? A new user is always their own default; an edit is the
       * default scope only when the chosen org+tenant match the loaded default.
       * ONLY the default scope may move AppUser.role + the default grant
       * together (via updateUser, which also invalidates the JWT). Editing a
       * NON-default scope must never mutate AppUser.role or the default grant —
       * it only re-roles that specific grant through the membership endpoint
       * (which the BE guards for demotion + JWT invalidation). */
      const editingDefaultScope = !editUser || (
        !!editDefault
        && editDefault.orgId === userForm.orgId
        && editDefault.tenantId === userForm.tenantId
      );

      // Identity is always safe to send; role/placement only for the default scope.
      const identity = {
        email: userForm.email.trim(),
        name: userForm.name.trim() || null,
        status: userForm.status,
      };
      const res = editUser
        ? await api.updateUser(editUser.id, editingDefaultScope
            ? { ...identity, role: userForm.role, orgId: userForm.orgId, tenantId: userForm.tenantId }
            : identity)
        : await api.createUser({ ...identity, role: userForm.role, orgId: userForm.orgId, tenantId: userForm.tenantId });
      if (!res || res.ok === false) {
        setUserError(res?.error || res?.data?.error || `Backend rejected the request (HTTP ${res?.status || '?'}).`);
        return;
      }
      const savedUser = (res && (res.data || res)) || {};
      const targetUserId = editUser ? editUser.id : savedUser.id;
      if (targetUserId) {
        // Upsert the grant for the CHOSEN scope with the chosen role. This is
        // the authoritative RBAC write for a non-default scope; for the default
        // scope updateUser already synced it and this is an idempotent re-assert.
        // BE returns 201 with the grant on success, 400 (with {error}) on a
        // refused demotion or suspended-customer — surface it rather than swallow.
        const mRes = await api.addUserMembership(targetUserId, {
          orgId: userForm.orgId,
          tenantId: userForm.tenantId,
          role: userForm.role,
          isDefault: false,
        });
        if (!mRes || mRes.ok === false) {
          setUserError(mRes?.error || mRes?.data?.error || `Could not assign the role for this scope (HTTP ${mRes?.status || '?'}).`);
          return;
        }
        // Make this grant the user's default (home) ONLY for a new user or when
        // editing the default scope. Editing a non-default scope leaves their
        // home untouched — the fix for silently re-homing a multi-membership user.
        if (editingDefaultScope) {
          let membershipId = (mRes.data && mRes.data.id) || mRes.id || null;
          if (!membershipId) {
            const list = (await api.getUserMemberships(targetUserId)) || {};
            const arr = (list.data || list) || [];
            const match = Array.isArray(arr) ? arr.find((m) =>
              (m.orgId || m.org_id) === userForm.orgId &&
              (m.tenantId || m.tenant_id) === userForm.tenantId) : null;
            membershipId = (match && (match.id || match.membershipId)) || null;
          }
          if (membershipId) {
            await api.setDefaultMembership(targetUserId, membershipId);
          }
        }
      }
      addToast({ tone: 'success', title: editUser ? 'User updated' : 'User created' });
      setAddUserOpen(false);
      await loadUsers?.({ orgId, tenantId });
    } catch (e) {
      setUserError(e?.message || String(e));
    } finally {
      setUserSaving(false);
    }
  };

  /* G211 — V2-native Invite User modal. Calls api.sendInvitation with the
   * payload shape v1 TeamPage uses: firstName, lastName, email, role,
   * orgId, tenantId. On success the backend either creates an invitation
   * record or re-invites an existing user (returned as reInvite flag). */
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({
    firstName: '', lastName: '', email: '', role: 'Developer',
    orgId: '', tenantId: '',
  });
  const [inviteSaving, setInviteSaving] = useState(false);
  const [inviteError, setInviteError] = useState(null);
  const openInvite = () => {
    setInviteForm({
      firstName: '', lastName: '', email: '', role: 'Developer',
      orgId: orgId || (orgs?.[0]?.id || ''),
      tenantId: tenantId || (tenants?.[0]?.id || ''),
    });
    setInviteError(null);
    setInviteOpen(true);
  };
  const submitInvite = async () => {
    setInviteError(null);
    if (!inviteForm.firstName.trim()) { setInviteError('First name is required.'); return; }
    if (!inviteForm.email.trim()) { setInviteError('Email is required.'); return; }
    if (!inviteForm.orgId) { setInviteError('Organization is required.'); return; }
    if (!inviteForm.tenantId) { setInviteError('Tenant is required.'); return; }
    setInviteSaving(true);
    try {
      const res = await api.sendInvitation({
        firstName: inviteForm.firstName.trim(),
        lastName: inviteForm.lastName.trim() || '',
        email: inviteForm.email.trim(),
        role: inviteForm.role,
        orgId: inviteForm.orgId,
        tenantId: inviteForm.tenantId,
      });
      if (!res || res.ok === false) {
        setInviteError(res?.error || res?.data?.error || `Backend rejected the request (HTTP ${res?.status || '?'}).`);
        return;
      }
      // Report what actually happened. The user row + token are always created; the EMAIL is
      // best-effort (emailQueued=false when no relay is configured for this environment). Claiming
      // "sent" when nothing went out is the bug we're fixing — gate the wording on emailQueued and
      // surface the registration link so the invite can still be completed manually.
      const data = res?.data || res || {};
      const reInvite = data.reInvite;
      const emailQueued = data.emailQueued;
      const registrationUrl = data.registrationUrl;
      if (emailQueued) {
        addToast({
          tone: 'success',
          title: reInvite ? 'Re-invitation sent' : 'Invitation sent',
          description: `Invite email delivered to ${inviteForm.email}.`,
        });
      } else {
        addToast({
          tone: 'warning',
          title: reInvite ? 'User re-invited — email not sent' : 'User invited — email not sent',
          description: `${inviteForm.email} was added, but the invite email couldn't be sent from this environment.`
            + (registrationUrl ? ` Share this registration link so they can finish signing up: ${registrationUrl}` : ''),
        });
      }
      setInviteOpen(false);
      await loadUsers?.({ orgId, tenantId });
    } catch (e) {
      setInviteError(e?.message || String(e));
    } finally {
      setInviteSaving(false);
    }
  };

  /* G211 — Consume `pendingAction` from outer header. When the outer Add
   * User / Invite User buttons fire, they switch the tab to 'users' and
   * set pendingAction; we open the matching modal then clear the signal
   * so it doesn't re-fire on re-renders. */
  useEffect(() => {
    if (!pendingAction) return;
    if (pendingAction === 'add') openAddUser();
    else if (pendingAction === 'invite') openInvite();
    if (typeof onConsumeAction === 'function') onConsumeAction();
    // Intentionally watch only pendingAction; openAddUser/openInvite are
    // stable closures inside this component so re-running on their identity
    // would just refire on every render.
  }, [pendingAction]); // eslint-disable-line

  /* Org / tenant lookup maps for nicer labels in table cells */
  const orgMap    = useMemo(() => Object.fromEntries((orgs || []).map(o => [o.id, o.name || o.id])), [orgs]);
  const tenantMap = useMemo(() => Object.fromEntries((tenants || []).map(t => [t.id, t.name || t.id])), [tenants]);
  /* Manager lookup for the Manager column.
   *
   * Built from `users` — the FULL list the backend already scoped to this
   * caller (TeamController.listUsers -> reachableTenantsForUserList) — and
   * deliberately NOT from `filtered`, so narrowing the facets or the search box
   * never blanks out a manager name that is legitimately visible.
   *
   * That also settles the privacy boundary: a manager resolves to a name only
   * when they are inside the set this operator was already allowed to see. A
   * manager_id pointing outside that scope stays unresolved and is labelled as
   * such, rather than being fetched behind the scenes. */
  const managerById = useMemo(
    () => Object.fromEntries((users || []).map(u => [u.id, u])),
    [users]);
  /* Customer facet roster — /customers (PA: all; SA: own customer). Distinct
   * from Organization: a customer and an org can share a name (e.g. "Acme Corp"
   * the customer vs "Acme Corp" the org under PLATFORM-CUSTOMER), so the org
   * facet alone can't tell which customer a user belongs to. */
  const [custList, setCustList] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.listCustomers();
        const raw = (res && (res.data || res)) || [];
        if (!cancelled) setCustList(Array.isArray(raw) ? raw : []);
      } catch (_) { /* non-fatal */ }
    })();
    return () => { cancelled = true; };
  }, []);
  const customerMap = useMemo(() => Object.fromEntries((custList || []).map(c => [c.id, c.displayName || c.name || c.id])), [custList]);
  /* DEF-FE-INVITE-ACTIVE-001 — placing a NEW user (create or invite) targets a
   * customer, so only ACTIVE customers are valid destinations; SUSPENDED /
   * OFFBOARDED must not appear (you can't onboard into a customer that's been
   * suspended or soft-deleted). The customer FACET + customerMap deliberately
   * keep ALL statuses so existing users in an offboarded customer stay
   * viewable/filterable. Missing status defaults to ACTIVE so a future endpoint
   * shape that omits the field never empties the selector. */
  const activeCustList = useMemo(
    () => (custList || []).filter(c => (c.status ?? 'ACTIVE') === 'ACTIVE'),
    [custList],
  );

  /* ── Facets ──────────────────────────────────────────────────── */
  const [facets, setFacets] = useState(() => ({
    role:   new Set(),
    status: new Set(),
    org:    new Set(),
    customer: new Set(),
    mfa:    new Set(),  // 'on' / 'off'
  }));
  const toggleFacet = (groupKey, optionKey, nextChecked) => {
    setFacets(prev => {
      const next = new Set(prev[groupKey] || []);
      if (nextChecked) next.add(optionKey); else next.delete(optionKey);
      return { ...prev, [groupKey]: next };
    });
  };
  const clearFacets = () => setFacets({ role: new Set(), status: new Set(), org: new Set(), customer: new Set(), mfa: new Set() });

  const FACET_GROUPS = useMemo(() => ([
    ...((custList || []).length > 1 ? [{
      key: 'customer', label: 'Customer',
      options: (custList || []).map(c => ({ key: c.id, label: c.displayName || c.name || c.id })),
    }] : []),
    { key: 'role',   label: 'Role',   options: allRoleNames.map(r => ({ key: r, label: r })) },
    { key: 'status', label: 'Status', options: STATUS_OPTIONS.map(s => ({ key: s.key, label: s.label })) },
    ...((orgs || []).length > 1 ? [{
      key: 'org', label: 'Organization',
      options: (orgs || []).map(o => ({ key: o.id, label: o.name || o.id })),
    }] : []),
    { key: 'mfa', label: 'MFA', options: [
      { key: 'on',  label: 'Enabled' },
      { key: 'off', label: 'Disabled' },
    ]},
  ]), [orgs, custList, allRoleNames]);

  /* RC — users carry only tenantId (AppUser has no org column); a user's org
   * is the org that owns their pinned tenant (kept in sync with their default
   * membership by setDefaultMembership). Without deriving it, u.orgId is
   * always undefined, so the ORGANIZATION facet counted 0 for every org, the
   * org filter matched nothing, and the org column rendered "—". Resolve it
   * from the tenants list (1:1 tenant→org) once, then feed the resolved set
   * into the filter, counts, and rows. */
  const tenantOrgById = useMemo(() => {
    const m = {};
    (tenants || []).forEach((t) => { if (t && t.id) m[t.id] = t.orgId || t.org_id || null; });
    return m;
  }, [tenants]);
  const usersWithOrg = useMemo(() => (users || []).map((u) => {
    const resolved = u.orgId || u.org_id || tenantOrgById[u.tenantId] || tenantOrgById[u.tenant_id] || null;
    return (resolved && resolved !== u.orgId) ? { ...u, orgId: resolved } : u;
  }), [users, tenantOrgById]);

  // DEF-PA-001 — V208 stacked role grants: a user can hold multiple roles per
  // scope via user_org_roles. `AppUser.role` is just the primary/displayed
  // role; the full set lives in `allRoles` (BE enrichment). Falling back to
  // `[u.role]` keeps this working before the enrichment lands and never
  // regresses users without stacked grants. Declared BEFORE `filtered` — the
  // role facet consumes it (TDZ if declared later).
  const rolesOf = (u) => (Array.isArray(u?.allRoles) && u.allRoles.length > 0)
      ? u.allRoles
      : (u?.role ? [u.role] : []);

  /* DEF-RBAC-TEAM-PROJECTION-001 — a user holds N user_org_roles grants
   * (customer/org/tenant + role); collapsing them to the single AppUser.role
   * meant a user who is e.g. Super Admin in customer X but Viewer by default
   * displayed as "Viewer" even when the operator had filtered to customer X.
   * `membershipRoles` (BE enrichment, already scoped to the caller) carries the
   * per-grant role so we can show the role for the SCOPE the operator is
   * viewing. When exactly one customer (or org) facet is selected, that is the
   * display scope; otherwise fall back to the user's default grant, then the
   * legacy AppUser.role (keeps working before the BE enrichment deploys). */
  const selectedCustomerId = facets.customer.size === 1 ? [...facets.customer][0] : null;
  const selectedOrgId      = facets.org.size === 1 ? [...facets.org][0] : null;
  const displayGrant = (u) => {
    const ms = Array.isArray(u?.membershipRoles) ? u.membershipRoles : [];
    if (ms.length === 0) return null;
    if (selectedCustomerId) {
      const inCust = ms.filter((m) => m.customerId === selectedCustomerId);
      if (inCust.length) return inCust.find((m) => m.isDefault) || inCust[0];
    }
    if (selectedOrgId) {
      const inOrg = ms.filter((m) => m.orgId === selectedOrgId);
      if (inOrg.length) return inOrg.find((m) => m.isDefault) || inOrg[0];
    }
    return ms.find((m) => m.isDefault) || null;
  };

  /* ── Filter ──────────────────────────────────────────────────── */
  const filtered = useMemo(() => {
    return (usersWithOrg || []).filter(u => {
      // Match against the user's full (scoped) role set, not just the primary
      // AppUser.role — else filtering by "Super Admin" misses a user who holds
      // that role in one customer but has a lower primary/default role.
      if (facets.role.size > 0 && !rolesOf(u).some((role) => facets.role.has(role))) return false;
      if (facets.status.size > 0 && !facets.status.has(u.status || (u.active === false ? 'inactive' : 'active'))) return false;
      if (facets.org.size > 0 && !facets.org.has(u.orgId)) return false;
      if (facets.customer.size > 0 && !(Array.isArray(u.customerIds) && u.customerIds.some(cid => facets.customer.has(cid)))) return false;
      if (facets.mfa.size > 0) {
        const mfaState = u.mfaEnabled ? 'on' : 'off';
        if (!facets.mfa.has(mfaState)) return false;
      }
      return true;
    });
  }, [usersWithOrg, facets]);

  /* G175 — Pagination over the filtered list. Reset to page 1 whenever the
   * filtered length changes so the user doesn't land on an empty page. */
  const pg = usePaginationState(10);
  const visibleRows = pg.paginate(filtered);
  useEffect(() => { pg.setPage(0); }, [filtered.length]);

  const facetCounts = useMemo(() => {
    const counts = {};
    for (const g of FACET_GROUPS) {
      counts[g.key] = {};
      for (const opt of g.options) {
        counts[g.key][opt.key] = (usersWithOrg || []).filter(u => {
          for (const og of FACET_GROUPS) {
            if (og.key === g.key) continue;
            const sel = facets[og.key];
            if (!sel || sel.size === 0) continue;
            // DEF-PA-001 — role facet uses the stacked-role array so a user
            // who is BOTH "Developer" AND "Approver" still counts under
            // each filter independently. All other facets remain single-
            // valued and unchanged.
            if (og.key === 'role') {
              if (!rolesOf(u).some(r => sel.has(r))) return false;
              continue;
            }
            if (og.key === 'customer') {
              if (!(Array.isArray(u.customerIds) && u.customerIds.some(cid => sel.has(cid)))) return false;
              continue;
            }
            const field = og.key === 'mfa'
              ? (u.mfaEnabled ? 'on' : 'off')
              : og.key === 'status' ? (u.status || (u.active === false ? 'inactive' : 'active'))
              : u[og.key === 'org' ? 'orgId' : og.key];
            if (!sel.has(field)) return false;
          }
          if (g.key === 'mfa')    return (u.mfaEnabled ? 'on' : 'off') === opt.key;
          if (g.key === 'status') return (u.status || (u.active === false ? 'inactive' : 'active')) === opt.key;
          if (g.key === 'org')    return u.orgId === opt.key;
          if (g.key === 'customer') return Array.isArray(u.customerIds) && u.customerIds.includes(opt.key);
          if (g.key === 'role')   return rolesOf(u).includes(opt.key);
          return u[g.key] === opt.key;
        }).length;
      }
    }
    return counts;
  }, [usersWithOrg, facets, FACET_GROUPS]);

  const facetGroupsWithCounts = useMemo(() => (
    FACET_GROUPS.map(g => ({
      ...g,
      options: g.options.map(o => ({
        ...o,
        count: facetCounts[g.key]?.[o.key] || 0,
        checked: facets[g.key]?.has(o.key) || false,
      })),
    }))
  ), [facetCounts, facets, FACET_GROUPS]);

  const activeFilterChips = useMemo(() => {
    const chips = [];
    for (const g of FACET_GROUPS) {
      for (const optKey of facets[g.key] || []) {
        const opt = g.options.find(o => o.key === optKey);
        chips.push({ groupKey: g.key, optionKey: optKey, label: `${g.label}: ${opt?.label || optKey}` });
      }
    }
    return chips;
  }, [facets, FACET_GROUPS]);

  /* ── Density ─────────────────────────────────────────────────── */
  const [density, setDensity] = useState(() => {
    try { return localStorage.getItem('team-users-density') || 'comfortable'; }
    catch { return 'comfortable'; }
  });
  const cycleDensity = () => {
    const next = density === 'comfortable' ? 'compact' : 'comfortable';
    setDensity(next);
    try { localStorage.setItem('team-users-density', next); } catch {}
  };

  /* ── Slideout ────────────────────────────────────────────────── */
  const [selected, setSelected] = useState(null);
  const [slideoutTab, setSlideoutTab] = useState('profile');
  const [memberships, setMemberships] = useState([]);
  const [workflowAssign, setWorkflowAssign] = useState([]);
  const [ssoIdentities, setSsoIdentities] = useState([]);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const openSlideout = useCallback(async (row) => {
    setSelected(row);
    setSlideoutTab('profile');
    setMemberships([]);
    setWorkflowAssign([]);
    setSsoIdentities([]);
    setLoadingDetail(true);
    try {
      const [memRes, wfRes, ssoRes] = await Promise.all([
        api.getUserMemberships ? api.getUserMemberships(row.id) : Promise.resolve({ ok: true, data: [] }),
        Promise.resolve({ ok: true, data: [] }),
        // Linked external identities — read-only; absent for a password-only user.
        api.listUserSsoIdentities ? api.listUserSsoIdentities(row.id) : Promise.resolve({ ok: true, data: {} }),
      ]);
      setMemberships(memRes?.data || []);
      setWorkflowAssign(wfRes?.data || []);
      setSsoIdentities(ssoRes?.ok ? (ssoRes.data?.identities || []) : []);
    } catch (err) {
      console.warn('Slideout detail fetch failed', err);
    } finally {
      setLoadingDetail(false);
    }
  }, []);

  const closeSlideout = () => setSelected(null);

  /* ── Bulk selection ──────────────────────────────────────────── */
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const toggleSelected = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const clearSelected = () => setSelectedIds(new Set());

  /* ── Reload ──────────────────────────────────────────────────── */
  const reload = useCallback(() => {
    if (typeof loadUsers === 'function') loadUsers(isSuperAdmin ? {} : { orgId, tenantId });
  }, [loadUsers, isSuperAdmin, orgId, tenantId]);

  useEffect(() => { reload(); }, [reload]);

  /* ── Delete handler (single + bulk) ──────────────────────────── */
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteError, setDeleteError] = useState(null);
  const [deleting, setDeleting] = useState(false);
  // DEF-RBAC-USER-LIFECYCLE-001 — the trash opens a dialog that DEACTIVATES
  // (soft, recoverable) by default; `deletePermanent` flips it to the
  // irreversible hard purge, gated by a type-to-confirm.
  const [deletePermanent, setDeletePermanent] = useState(false);

  const isDeactivated = (r) => (r?.status || '').toLowerCase() === 'deactivated';

  const openDeleteDialog = (r) => {
    setDeleteTarget(r);
    setDeleteConfirmText('');
    setDeleteError(null);
    // Already deactivated → default to the permanent path (deactivating again is a no-op).
    setDeletePermanent(isDeactivated(r));
  };

  const handleReactivate = async (r) => {
    try {
      const res = await api.reactivateUser(r.id);
      if (res && res.ok === false) {
        addToast({ tone: 'danger', title: 'Restore failed', description: res?.error || res?.data?.error || '' });
        return;
      }
      addToast({ tone: 'success', title: `${fullName(r)} restored`, description: 'The user is active again and can log in.' });
      reload();
    } catch (e) {
      addToast({ tone: 'danger', title: 'Restore failed', description: e?.message || String(e) });
    }
  };

  const onDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      const res = deletePermanent
        ? await api.purgeUser(deleteTarget.id)
        : (typeof deleteUserApi === 'function'
            ? await deleteUserApi(deleteTarget.id)
            : await api.deactivateUser(deleteTarget.id));
      if (res?.ok) {
        addToast({
          tone: 'success',
          title: deletePermanent ? `${fullName(deleteTarget)} permanently deleted` : `${fullName(deleteTarget)} deactivated`,
          description: deletePermanent
            ? 'The account and all access were permanently removed. A record is preserved in the audit trail.'
            : 'The user is deactivated (recoverable) — restore them anytime. A record is preserved in the audit trail.',
        });
        setDeleteTarget(null);
        reload();
      } else {
        setDeleteError(res?.error || res?.data?.error || 'Operation failed.');
      }
    } catch (err) {
      setDeleteError(err?.message || String(err));
    } finally {
      setDeleting(false);
    }
  };

  /* ── Bulk delete ─────────────────────────────────────────────────
   * Soft-DEACTIVATE only (recoverable); hard purge stays a single-user
   * action. REUSES the one per-user endpoint (deleteUserApi → api.deleteUser,
   * default soft-delete), which is server-gated by requireCanManageTargetUser —
   * no new BE path, each call scoped to a single user. The FE additionally
   * pre-filters to targets the actor may manage and excludes self, so we never
   * fire a call the BE will 403 (and never deactivate the acting admin). */
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const softDeactivateOne = useCallback(
    (id) => (typeof deleteUserApi === 'function' ? deleteUserApi(id) : api.deactivateUser(id)),
    [deleteUserApi],
  );
  // Selected users the actor may actually manage (and not themselves). Uses the
  // shared, unit-tested selectBulkTargets helper; canManageTargetUser encodes
  // the self / PA-bypass / level rules the BE enforces. Plain derivation —
  // selection is page-bounded.
  const bulkTargets = selectBulkTargets(
    selectedIds,
    new Map((users || []).map((u) => [u.id, u])),
    currentUser?.id,
    canManageTargetUser,
  );
  const bulkSkipped = selectedIds.size - bulkTargets.length;

  const runBulkDelete = async () => {
    if (bulkTargets.length === 0) return;
    setBulkDeleting(true);
    try {
      const results = await Promise.allSettled(bulkTargets.map((u) => softDeactivateOne(u.id)));
      const { ok, failedIdx } = summarizeSettled(results);
      const failed = failedIdx.map((i) => bulkTargets[i]);
      if (ok > 0) {
        addToast({ tone: 'success', title: `Deactivated ${ok} user${ok === 1 ? '' : 's'}`,
          description: 'Recoverable — restore anytime. Each change is sealed to the audit trail.' });
      }
      if (failed.length) {
        const names = failed.map((u) => fullName(u)).slice(0, 5).join(', ');
        addToast({ tone: 'danger', title: `${failed.length} could not be deactivated`,
          description: names + (failed.length > 5 ? '…' : '') });
      }
      clearSelected();
      setBulkDeleteOpen(false);
      reload();
    } catch (e) {
      addToast({ tone: 'danger', title: 'Bulk deactivate failed', description: e?.message || String(e) });
    } finally {
      setBulkDeleting(false);
    }
  };

  /* ── Bulk status toggle ──────────────────────────────────────────
   * Flips each selected user's active/deactivated status using the SAME
   * per-user endpoints as the single-row actions (reactivateUser for a
   * deactivated user, else the soft-deactivate owner) — no new BE path. Same
   * target pre-filter as bulk delete (self + unmanageable excluded). */
  const [bulkToggleOpen, setBulkToggleOpen] = useState(false);
  const [bulkToggling, setBulkToggling] = useState(false);
  const bulkToReactivate = bulkTargets.filter((u) => isDeactivated(u));
  const bulkToDeactivate = bulkTargets.filter((u) => !isDeactivated(u));

  const runBulkToggle = async () => {
    if (bulkTargets.length === 0) return;
    setBulkToggling(true);
    try {
      const results = await Promise.allSettled(
        bulkTargets.map((u) => (isDeactivated(u) ? api.reactivateUser(u.id) : softDeactivateOne(u.id))),
      );
      const { ok, failedIdx } = summarizeSettled(results);
      const failed = failedIdx.map((i) => bulkTargets[i]);
      if (ok > 0) {
        addToast({ tone: 'success', title: `Updated ${ok} user${ok === 1 ? '' : 's'}`,
          description: 'Status toggled (active ↔ deactivated). Each change is sealed to the audit trail.' });
      }
      if (failed.length) {
        const names = failed.map((u) => fullName(u)).slice(0, 5).join(', ');
        addToast({ tone: 'danger', title: `${failed.length} could not be updated`,
          description: names + (failed.length > 5 ? '…' : '') });
      }
      clearSelected();
      setBulkToggleOpen(false);
      reload();
    } catch (e) {
      addToast({ tone: 'danger', title: 'Bulk status toggle failed', description: e?.message || String(e) });
    } finally {
      setBulkToggling(false);
    }
  };

  /* ── KPI tiles for the Users inventory ───────────────────────── */
  const totalUsers   = (users || []).length;
  const activeUsers  = (users || []).filter(u => (u.status === 'active') || u.active !== false).length;
  const mfaEnabled   = (users || []).filter(u => u.mfaEnabled).length;
  const adminUsers   = (users || []).filter(u => /Admin/.test(u.role || '')).length;

  return (
    <div>
      {/* ─── KPI tiles ─────────────────────────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-5">
        <MetricCard label="Total users"    value={totalUsers}  tone="cyan" />
        <MetricCard label="Active"          value={activeUsers} tone="success" />
        <MetricCard label="With MFA"        value={mfaEnabled}  tone="trust"
          delta={{ value: `${totalUsers > 0 ? Math.round((mfaEnabled / totalUsers) * 100) : 0}% coverage`, direction: 'flat', intent: 'neutral' }} />
        <MetricCard label="Admin roles"     value={adminUsers}  tone="warning" />
      </div>

      {/* ─── Active filter chips strip ─────────────────────────── */}
      {activeFilterChips.length > 0 && (
        <div className="bg-v2-surface border border-v2-border-subtle rounded-v2-md shadow-v2-sm px-4 py-2.5 mb-4 flex items-center gap-2 flex-wrap">
          <span className="text-[11px] uppercase tracking-wider text-v2-ink-muted font-semibold">Filters</span>
          {activeFilterChips.map((c) => (
            <button
              key={`${c.groupKey}:${c.optionKey}`}
              onClick={() => toggleFacet(c.groupKey, c.optionKey, false)}
              className="inline-flex items-center gap-1 text-[11.5px] font-medium bg-v2-action-soft text-v2-action-strong rounded-v2-pill px-2.5 py-0.5 hover:bg-v2-action hover:text-white transition-colors duration-v2"
            >
              {c.label} ×
            </button>
          ))}
          <button onClick={clearFacets} className="ml-auto v2-focus-ring text-[11.5px] text-v2-ink-muted hover:text-v2-ink">
            Clear all
          </button>
        </div>
      )}

      {/* ─── Bulk action toolbar ───────────────────────────────── */}
      {selectedIds.size > 0 && (
        <div className="bg-v2-action-soft border border-v2-action rounded-v2-md px-4 py-2.5 mb-4 flex items-center gap-3">
          <span className="text-[12.5px] font-semibold text-v2-action-strong">{selectedIds.size} selected</span>
          <Button variant="ghost" size="sm" iconLeft={<Power size={13} />}
            onClick={() => setBulkToggleOpen(true)}>
            Toggle status
          </Button>
          <Button variant="danger" size="sm" iconLeft={<Trash2 size={13} />}
            onClick={() => setBulkDeleteOpen(true)}>
            Delete {selectedIds.size}
          </Button>
          <button onClick={clearSelected} className="ml-auto text-[11.5px] text-v2-ink-muted hover:text-v2-ink">
            Clear selection
          </button>
        </div>
      )}

      {/* ─── Toolbar (right-aligned actions) ───────────────────── */}
      <div className="flex items-center justify-end gap-2 mb-4">
        <Button variant="ghost" size="sm" iconLeft={<RefreshCw size={13} />} onClick={reload}>Refresh</Button>
        <Button variant="secondary" size="sm" onClick={cycleDensity} title={`Row density: ${density}`}>
          Density: {density.charAt(0).toUpperCase() + density.slice(1)}
        </Button>
      </div>

      {/* ─── Main grid ────────────────────────────────────────── */}
      <div className="grid gap-5 grid-cols-1 lg:grid-cols-[240px_1fr]">
        <FacetRail
          groups={facetGroupsWithCounts}
          onToggle={toggleFacet}
          ariaLabel="User filters"
        />

        {filtered.length === 0 ? (
          <EmptyState
            icon={<UsersIcon size={20} />}
            title="No users match"
            description={activeFilterChips.length > 0
              ? 'No users match the current filters. Adjust filters or clear them to see more.'
              : 'No users found in this organization yet. Use Invite User or Add User to onboard your team.'}
            primaryAction={
              activeFilterChips.length > 0
                ? <Button variant="primary" onClick={clearFacets}>Clear filters</Button>
                : <Button variant="primary" iconLeft={<Plus />} onClick={openAddUser}>Add User</Button>
            }
          />
        ) : (
          <Table
            ariaLabel="Users"
            density={density}
            columns={[
              {
                key: 'select', header: '', render: (r) => (
                  <input type="checkbox" checked={selectedIds.has(r.id)}
                    onChange={(e) => { e.stopPropagation(); toggleSelected(r.id); }}
                    onClick={(e) => e.stopPropagation()}
                    className="v2-focus-ring"
                    aria-label={`Select ${fullName(r)}`} />
                ),
              },
              {
                key: 'name', header: 'Name', sortable: true, render: (r) => (
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-v2-md flex items-center justify-center font-bold text-[11px] bg-v2-cyan-soft text-v2-cyan">
                      {initials(r)}
                    </div>
                    <div>
                      <div className="font-semibold text-v2-ink">{fullName(r)}</div>
                      <div className="text-[10.5px] text-v2-ink-muted font-mono-v2">{r.email || '—'}</div>
                    </div>
                  </div>
                ),
              },
              {
                key: 'role', header: 'Role', render: (r) => {
                  // Show the role for the scope the operator is viewing (the
                  // selected customer/org), not the single primary AppUser.role.
                  const g = displayGrant(r);
                  const role = (g && g.role) || r.role;
                  const others = [...new Set(rolesOf(r))].filter((x) => x !== role);
                  return (
                    <div className="flex items-center gap-1.5">
                      <Pill tone={ROLE_TONE[role] || 'muted'} size="sm">{role || 'Unassigned'}</Pill>
                      {others.length > 0 && (
                        <span className="text-[10px] text-v2-ink-muted cursor-default"
                              title={`Also holds: ${others.join(', ')}`}>+{others.length}</span>
                      )}
                    </div>
                  );
                },
              },
              {
                key: 'org', header: 'Org / Tenant', render: (r) => {
                  /* RC-25 / R1 — EntityLabel renders "{name} ({shortid})" with
                     the full UUID on hover. DEF-RBAC-TEAM-PROJECTION-001 — show
                     the org/tenant of the grant we're displaying the role for
                     (selected customer/org scope), so role and placement stay
                     consistent for multi-membership users. */
                  const g = displayGrant(r);
                  const oId = (g && g.orgId) || r.orgId;
                  const tId = (g && g.tenantId) || r.tenantId;
                  return (
                    <div className="text-[11.5px] space-y-0.5">
                      <div><EntityLabel kind="org"    id={oId} name={orgMap[oId]}       size="sm" /></div>
                      <div><EntityLabel kind="tenant" id={tId} name={tenantMap[tId]} size="sm" tone="muted" /></div>
                    </div>
                  );
                },
              },
              {
                key: 'manager', header: 'Manager', render: (r) => {
                  /* Reporting line — written by ScimService from the SCIM
                   * enterprise manager reference (RFC 7643 4.3) and walked to
                   * build escalation tiers. Three honest states, never a bare
                   * blank cell that an operator could read as "reports to
                   * nobody" when the truth is "we were never told". */
                  if (!r.managerId) {
                    return (
                      <span className="text-[11.5px] text-v2-ink-muted"
                            title="No manager on file for this person. Managers arrive with an identity directory sync — connect an identity governance system under Connectors, then run Sync from IGA.">
                        Not on file
                      </span>
                    );
                  }
                  const mgr = managerById[r.managerId];
                  if (!mgr) {
                    return (
                      <span className="text-[11.5px] text-v2-ink-muted italic"
                            title="A manager is recorded for this person, but they sit outside the organizations you can view, so their name is not shown here.">
                        Outside your scope
                      </span>
                    );
                  }
                  return (
                    <div className="text-[11.5px] leading-tight">
                      <div className="text-v2-ink truncate" title={fullName(mgr)}>{fullName(mgr)}</div>
                      <div className="text-[10.5px] text-v2-ink-muted font-mono-v2 truncate">{mgr.email || '—'}</div>
                    </div>
                  );
                },
              },
              {
                key: 'status', header: 'Status', render: (r) => {
                  const status = r.status || (r.active === false ? 'inactive' : 'active');
                  const opt = STATUS_OPTIONS.find(o => o.key === status);
                  return <Pill tone={opt?.tone || 'muted'} size="sm" dot>{opt?.label || status}</Pill>;
                },
              },
              {
                key: 'mfa', header: 'MFA', render: (r) => (
                  <Pill tone={r.mfaEnabled ? 'success' : 'warning'} size="sm" dot>
                    {r.mfaEnabled ? 'On' : 'Off'}
                  </Pill>
                ),
              },
              {
                key: 'actions', header: '', align: 'right', render: (r) => (
                  <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                    {/* DEF-FE-TEAM-EDIT-GATE-001 — extend the same
                      * canManageTargetUser gate to the Edit user
                      * action. Edit can change name/email/status/role/
                      * tenant — a Super Admin opening Edit on a
                      * Platform Admin and saving still lands a 403 at
                      * the BE (DEF-RBAC-USER-MUTATION-TARGET-CHECK-001),
                      * but the surface should not even offer the
                      * action. Self-edit still passes (helper returns
                      * true for self). */}
                    {canManageTargetUser(r) && (
                      <Button variant="ghost" size="sm"
                        onClick={() => openEditUser(r)}
                        title="Edit user">
                        <Edit2 size={13} />
                      </Button>
                    )}
                    {/* DEF-FE-TEAM-USER-ACTION-GATING-001 — Reset and
                      * Delete actions now hide when the current actor
                      * lacks authority over this row's target user.
                      * Mirrors RbacService.requireCanManageTargetUser
                      * (PA bypass + self + strict actor>target level).
                      * Pre-fix comment explained the buttons rendered
                      * unconditionally because "the BE will 403 a click
                      * anyway" — that turned out to be confusing UX +
                      * exposed the SA-can-attempt-PA-reset attack
                      * surface that DEF-RBAC-PASSWORD-RESET-ESCALATION-
                      * 001 made into a security incident. Hide at the
                      * source. Self-reset is additionally rejected at
                      * the surface (PasswordResetService line 135) and
                      * self-delete is gated by the type-to-confirm. */}
                    {canManageTargetUser(r) && (
                      <Button variant="ghost" size="sm"
                        onClick={() => setResetTarget(r)}
                        title="Reset password">
                        <KeyRound size={13} />
                      </Button>
                    )}
                    {/* DEF-RBAC-USER-LIFECYCLE-001 — Restore for a soft-deactivated user. */}
                    {canManageTargetUser(r) && isDeactivated(r) && (
                      <Button variant="ghost" size="sm"
                        onClick={() => handleReactivate(r)}
                        className="!text-v2-success hover:!bg-v2-success-soft"
                        title="Restore (reactivate) user">
                        <RefreshCw size={13} />
                      </Button>
                    )}
                    {canManageTargetUser(r) && r.id !== currentUser?.id && (
                      <Button variant="ghost" size="sm"
                        onClick={() => openDeleteDialog(r)}
                        className="!text-v2-danger hover:!bg-v2-danger-soft"
                        title={isDeactivated(r) ? 'Delete user permanently' : 'Deactivate user'}>
                        <Trash2 size={13} />
                      </Button>
                    )}
                  </div>
                ),
              },
            ]}
            rows={visibleRows}
            rowKey={(r) => r.id}
            onRowClick={openSlideout}
          />
        )}
      </div>

      {filtered.length > 0 && (
        <div className="grid gap-5 mt-3 grid-cols-1 lg:grid-cols-[240px_1fr]">
          <div />
          <Pagination
            totalItems={filtered.length}
            page={pg.page}
            pageSize={pg.pageSize}
            onPageChange={pg.setPage}
            onPageSizeChange={pg.setPageSize}
            noun="users"
          />
        </div>
      )}

      {/* ─── Slideout — user triage view ──────────────────────── */}
      <Slideout
        isOpen={!!selected}
        onClose={closeSlideout}
        ariaLabel={selected ? `${fullName(selected)} detail` : 'User detail'}
        width={520}
        tabs={[
          { id: 'profile',     label: 'Profile' },
          { id: 'memberships', label: 'Memberships', badge: memberships.length || undefined },
          { id: 'agents',      label: 'Agent access' },
          { id: 'workflows',   label: 'Workflow access' },
          { id: 'chat',        label: 'Chat access' },
          { id: 'permissions', label: 'Permissions' },
        ]}
        activeTabId={slideoutTab}
        onTabChange={setSlideoutTab}
        header={selected && (
          <div>
            <div className="flex items-start gap-3 mb-3">
              <div className="w-12 h-12 rounded-v2-md flex items-center justify-center font-bold text-[14px] bg-v2-cyan-soft text-v2-cyan flex-shrink-0">
                {initials(selected)}
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-[18px] font-bold tracking-tight m-0">{fullName(selected)}</h2>
                <p className="text-[12px] text-v2-ink-secondary font-mono-v2">{selected.email || '—'}</p>
                <div className="flex items-center gap-2 mt-2">
                  <Pill tone={ROLE_TONE[selected.role] || 'muted'} size="sm">{selected.role || 'Unassigned'}</Pill>
                  <Pill tone={(selected.status || (selected.active === false ? 'inactive' : 'active')) === 'active' ? 'success' : 'muted'} size="sm" dot>
                    {selected.status || (selected.active === false ? 'inactive' : 'active')}
                  </Pill>
                  <Pill tone={selected.mfaEnabled ? 'success' : 'warning'} size="sm" dot>
                    MFA {selected.mfaEnabled ? 'on' : 'off'}
                  </Pill>
                  {ssoIdentities.length > 0 && (
                    <Pill tone="cyan" size="sm">Federated</Pill>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* DEF-FE-TEAM-EDIT-GATE-001 — same target-user authority
                * check as the per-row Edit button. The user-detail
                * slideout (Profile / Memberships / Agent Access /
                * Workflow Access / Effective Permissions) stays
                * read-only-viewable for any actor with user.read, but
                * the "Edit user" action button hides when the actor
                * cannot manage this target. */}
              {canManageTargetUser(selected) && (
                <Button variant="primary" size="sm" iconLeft={<Edit2 size={13} />}
                  onClick={() => { closeSlideout(); openEditUser(selected); }}>
                  Edit user
                </Button>
              )}
              <Button variant="ghost" size="sm" iconLeft={<Mail size={13} />}
                onClick={() => { closeSlideout(); openEmailPicker(selected); }}>
                Email
              </Button>
            </div>
          </div>
        )}
      >
        {selected && slideoutTab === 'profile' && (
          <Card padding="sm">
            <Card.Header title="Profile" />
            <Card.Body>
              <dl className="grid grid-cols-2 gap-3 text-[12.5px]">
                <div>
                  <dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">First name</dt>
                  <dd className="text-v2-ink mt-0.5">{selected.firstName || '—'}</dd>
                </div>
                <div>
                  <dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Last name</dt>
                  <dd className="text-v2-ink mt-0.5">{selected.lastName || '—'}</dd>
                </div>
                <div className="col-span-2">
                  <dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Email</dt>
                  <dd className="text-v2-ink mt-0.5 font-mono-v2">{selected.email || '—'}</dd>
                </div>
                <div>
                  <dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Last sign-in</dt>
                  <dd className="text-v2-ink mt-0.5">{selected.lastSignInAt ? new Date(selected.lastSignInAt).toLocaleString() : '—'}</dd>
                </div>
                <div>
                  <dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Created</dt>
                  <dd className="text-v2-ink mt-0.5">{selected.createdAt ? new Date(selected.createdAt).toLocaleString() : '—'}</dd>
                </div>
              </dl>
            </Card.Body>
          </Card>
        )}

        {selected && slideoutTab === 'profile' && ssoIdentities.length > 0 && (
          <Card padding="sm" className="mt-3">
            <Card.Header title="Linked identities" />
            <Card.Body>
              <ul className="space-y-2 text-[12.5px]">
                {ssoIdentities.map((idn, i) => (
                  <li key={i} className="flex items-baseline gap-2">
                    <span className="font-semibold text-v2-ink">{idn.providerDisplayName || idn.providerKind || 'Identity provider'}</span>
                    <span className="text-v2-ink-muted font-mono-v2">{idn.subjectMasked || ''}</span>
                    {idn.lastLoginAt && (
                      <span className="text-v2-ink-muted">· last sign-in {new Date(idn.lastLoginAt).toLocaleDateString()}</span>
                    )}
                  </li>
                ))}
              </ul>
              <p className="text-v2-ink-muted text-[11.5px] mt-2 mb-0">
                Group memberships and role marked as synced are managed by this provider at sign-in.
              </p>
            </Card.Body>
          </Card>
        )}

        {selected && slideoutTab === 'memberships' && (
          <Card padding="sm">
            <Card.Header title="Org / Tenant memberships" />
            <Card.Body>
              {/* One shared surface for viewing + managing all of a user's memberships:
                  add another org/tenant/instance, remove one, or set the primary. The
                  scoped pickers only offer coherent children; the backend validator is
                  the backstop. */}
              <MembershipManager
                userId={selected.id}
                roleOptions={allRoleNames}
                canManageRole={canManageRoleL}
                onChanged={() => {
                  if (api.getUserMemberships) {
                    api.getUserMemberships(selected.id)
                      .then((r) => setMemberships(Array.isArray(r?.data) ? r.data : (r?.data?.memberships || [])))
                      .catch(() => {});
                  }
                }}
              />
            </Card.Body>
          </Card>
        )}

        {selected && slideoutTab === 'agents' && (
          /* G362 — Real V2 access view (was "Manage in v1" stub).
              Computes direct grants from `assignments` where userId
              matches, and indirect grants via groups the user is in.
              Source column tells the operator how the access was
              granted ("direct" / "via group: <name>") so an access
              reviewer can trace the chain. */
          <UserAgentAccessPanel
            user={selected}
            assignments={assignments}
            agents={agents}
            groupsList={groupsList}
            /* DEF-FE-USER-SLIDEOUT-SETTAB-NOT-DEFINED-001 — setTab lives
             * on the parent TeamPageV2Inner; UsersInventory was calling
             * it as if it were in local scope, which crashed on
             * click ("setTab is not defined"). Plumbed through as
             * onNavigateToTab prop. */
            onOpenAssignments={() => { onNavigateToTab && onNavigateToTab('assignments'); }}
          />
        )}

        {selected && slideoutTab === 'workflows' && (
          /* G362 — Real V2 workflow-access view (was "Manage in v1"
              stub). Same chain-tracing as Agent access. */
          <UserWorkflowAccessPanel
            user={selected}
            assignments={assignments}
            workflows={workflows}
            groupsList={groupsList}
          />
        )}

        {selected && slideoutTab === 'chat' && (
          <UserChatAccessPanel user={selected} agents={agents} />
        )}

        {selected && slideoutTab === 'permissions' && (
          <Card padding="sm">
            <Card.Header title={`Effective permissions for ${selected.role || 'this user'}`} />
            <Card.Body>
              <p className="text-v2-ink-secondary text-[12.5px] leading-relaxed mb-3">
                Effective permissions derive from this user's role and any group memberships.
                Each chip below is a permission this user can exercise across the platform.
              </p>
              <div className="flex flex-wrap gap-1.5">
                {(selected.permissions || []).map(p => (
                  <Pill key={p} tone="muted" size="sm">{p}</Pill>
                ))}
                {(selected.permissions || []).length === 0 && (
                  <span className="text-v2-ink-muted text-[12px]">No explicit permissions recorded — inherits role defaults.</span>
                )}
              </div>
            </Card.Body>
          </Card>
        )}
      </Slideout>

      {/* G586 — Admin password reset modal. Mounted at the Users tab
        * level so the same modal can be triggered from any user row
        * action without each row owning the heavy lifecycle. The modal
        * resets all internal state on open/close, so a prior reset's
        * credential never leaks into a subsequent target. */}
      <PasswordResetModal
        isOpen={!!resetTarget}
        onClose={() => setResetTarget(null)}
        user={resetTarget}
      />

      {/* Part 3 — "Email" sends a chosen template via the Email (SMTP)
        * connector instead of opening the OS mail client (mailto). */}
      <Slideout
        isOpen={emailPicker.open}
        onClose={emailPicker.sending ? undefined : () => setEmailPicker((p) => ({ ...p, open: false }))}
        ariaLabel="Send email"
        width={480}
        header={
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Mail size={14} className="text-v2-cyan" />
              <Pill tone="action" size="sm">email</Pill>
            </div>
            <h2 className="text-[16px] font-bold m-0">
              Send email{emailPicker.target ? ` — ${emailPicker.target.email}` : ''}
            </h2>
            <p className="text-[12px] text-v2-ink-secondary mt-1">
              Sends a chosen template through the platform Email (SMTP) connector. Variables
              like name, email, and role are filled in from the user.
            </p>
          </div>
        }
        footer={(
          <div className="flex items-center justify-end gap-2">
            <Button variant="ghost" size="sm" disabled={emailPicker.sending}
              onClick={() => setEmailPicker((p) => ({ ...p, open: false }))}>Cancel</Button>
            <Button variant="primary" size="sm" disabled={emailPicker.sending || !emailPicker.templateId}
              onClick={submitUserEmail}>{emailPicker.sending ? 'Sending…' : 'Send email'}</Button>
          </div>
        )}
      >
        <div className="space-y-3 text-[12.5px]">
          <div>
            <label className="block text-[10.5px] uppercase tracking-wider font-semibold text-v2-ink-muted mb-1">Email template</label>
            <select
              value={emailPicker.templateId}
              onChange={(e) => setEmailPicker((p) => ({ ...p, templateId: e.target.value }))}
              disabled={emailPicker.sending}
              className="v2-focus-ring w-full bg-v2-surface text-v2-ink border border-v2-border rounded-v2-md px-3 py-1.5 text-[12.5px]"
            >
              {emailPicker.templates.length === 0 && <option value="">No email templates found</option>}
              {emailPicker.templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          {emailPicker.error && (
            <div className="rounded-v2-md border border-v2-danger-border bg-v2-danger-soft px-3 py-2 text-[12px] text-v2-danger">
              {emailPicker.error}
            </div>
          )}
        </div>
      </Slideout>

      {/* DEF-FE-DELETE-MODAL-V2-REDESIGN-001 — Convert Delete user
        * confirmation from a centered Modal to a right-side Slideout
        * for V2 design-system consistency. Every other destructive /
        * multi-step user action surface in V2 is a Slideout (Reset
        * Password, Customer Offboard, Add User, Add Intent, Add Skill);
        * the standalone Modal was a V1 regression. Same type-to-
        * confirm pattern preserved — just rendered in the V2 panel
        * affordance. */}
      <Slideout
        isOpen={!!deleteTarget}
        onClose={deleting ? undefined : () => setDeleteTarget(null)}
        ariaLabel={deleteTarget ? `Delete user ${deleteTarget.email}` : 'Delete user'}
        width={520}
        header={
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Trash2 size={14} className={deletePermanent ? 'text-v2-danger' : 'text-v2-warning'} />
              <Pill tone={deletePermanent ? 'danger' : 'warning'} size="sm">{deletePermanent ? 'permanent delete' : 'deactivate'}</Pill>
            </div>
            <h2 className="text-[16px] font-bold m-0">
              {deleteTarget
                ? (deletePermanent ? `Permanently delete ${fullName(deleteTarget)}?` : `Deactivate ${fullName(deleteTarget)}?`)
                : 'Deactivate user'}
            </h2>
            <p className="text-[12px] text-v2-ink-secondary mt-1">
              {deletePermanent
                ? 'Irreversibly removes the account and all access. History is preserved in the audit trail.'
                : 'Blocks login and hides the user from active use. Recoverable — restore them anytime.'}
            </p>
          </div>
        }
        footer={(
          <div className="flex items-center justify-end gap-2">
            <Button variant="ghost" onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
            <Button variant="danger" onClick={onDeleteConfirm}
              disabled={deleting || !deleteTarget || (deletePermanent && deleteConfirmText.trim() !== (deleteTarget?.email || ''))}>
              {deleting
                ? (deletePermanent ? 'Deleting…' : 'Deactivating…')
                : (deletePermanent ? 'Permanently delete' : 'Deactivate')}
            </Button>
          </div>
        )}
      >
        {deleteTarget && (
          <div className="space-y-3 text-[12.5px] text-v2-ink-secondary leading-relaxed">
            {!deletePermanent && (
              <div className="px-3 py-2 rounded-v2-sm bg-v2-warning-soft border border-v2-warning/20 text-[12px] flex items-start gap-2">
                <AlertTriangle size={13} className="mt-0.5 flex-none text-v2-warning" />
                <div>
                  <strong>{fullName(deleteTarget)}</strong> will be blocked from logging in and hidden from
                  active use. Their memberships and history are kept — restore them anytime from the Users list.
                </div>
              </div>
            )}
            {/* Escalate to an irreversible purge. Only offered for a still-active user;
                an already-deactivated user opens this dialog straight in permanent mode. */}
            {!isDeactivated(deleteTarget) && (
              <label className="flex items-center gap-2 text-[12px] text-v2-ink-secondary select-none cursor-pointer">
                <input type="checkbox" checked={deletePermanent}
                  onChange={(e) => { setDeletePermanent(e.target.checked); setDeleteConfirmText(''); setDeleteError(null); }} />
                Permanently delete instead (cannot be undone)
              </label>
            )}
            {deletePermanent && (
              <>
                <div className="px-3 py-2 rounded-v2-sm bg-v2-danger-soft border border-v2-danger/20 text-v2-danger-strong text-[12px] flex items-start gap-2">
                  <AlertTriangle size={13} className="mt-0.5 flex-none" />
                  <div>
                    This action is irreversible. <strong>{fullName(deleteTarget)}</strong>'s account
                    will be permanently removed and they will lose access immediately.
                  </div>
                </div>
                <label className="block text-[10.5px] uppercase tracking-wider font-semibold text-v2-ink-muted mt-3">
                  Type <code className="font-mono-v2 text-v2-ink normal-case tracking-normal">{deleteTarget.email}</code> to confirm
                </label>
                <input
                  type="text" value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  className="v2-focus-ring w-full bg-v2-surface text-v2-ink border border-v2-border rounded-v2-md px-3 py-1.5 text-[12.5px] font-mono-v2"
                  placeholder={deleteTarget.email}
                  autoFocus />
              </>
            )}
            {deleteError && (
              <div className="px-3 py-2 rounded-v2-sm bg-v2-danger-soft text-v2-danger text-[12px]">
                {deleteError}
              </div>
            )}
          </div>
        )}
      </Slideout>

      {/* Bulk deactivate — platform Modal confirm (never window.confirm). Soft,
          recoverable; reuses the per-user endpoint via runBulkDelete. Targets
          are pre-filtered to users the actor may manage (self + unmanageable
          excluded), and the count of skipped rows is surfaced. */}
      <Modal
        isOpen={bulkDeleteOpen}
        onClose={bulkDeleting ? undefined : () => setBulkDeleteOpen(false)}
        title={`Deactivate ${bulkTargets.length} user${bulkTargets.length === 1 ? '' : 's'}?`}
        ariaLabel="Bulk deactivate confirmation"
        size="sm"
        footer={(
          <div className="flex items-center justify-end gap-2">
            <Button variant="ghost" onClick={() => setBulkDeleteOpen(false)} disabled={bulkDeleting}>Cancel</Button>
            <Button variant="danger" onClick={runBulkDelete} loading={bulkDeleting} disabled={bulkDeleting || bulkTargets.length === 0}>
              {bulkDeleting ? 'Deactivating…' : `Deactivate ${bulkTargets.length}`}
            </Button>
          </div>
        )}
      >
        <div className="space-y-3 text-[12.5px] text-v2-ink">
          {bulkTargets.length === 0 ? (
            <p className="text-v2-ink-secondary">
              None of the selected users can be deactivated by you. Nothing will be changed.
            </p>
          ) : (
            <>
              <p className="text-v2-ink-secondary">
                The selected users will be blocked from logging in and hidden from active use. Their
                memberships and history are kept — restore them anytime. Each change is sealed to the
                audit trail. This is not a permanent delete.
              </p>
              <ul className="max-h-40 overflow-auto space-y-1 text-[12px]">
                {bulkTargets.map((u) => (
                  <li key={u.id} className="flex items-center gap-2">
                    <span className="w-6 h-6 rounded-v2-sm flex items-center justify-center text-[9px] font-bold bg-v2-cyan-soft text-v2-cyan">{initials(u)}</span>
                    <span className="text-v2-ink font-semibold truncate">{fullName(u)}</span>
                    {u.email && <span className="text-v2-ink-muted font-mono-v2 text-[11px] truncate">{u.email}</span>}
                  </li>
                ))}
              </ul>
            </>
          )}
          {bulkSkipped > 0 && (
            <div className="px-3 py-2 rounded-v2-sm bg-v2-warning-soft border border-v2-warning/20 text-[12px] flex items-start gap-2">
              <AlertTriangle size={13} className="mt-0.5 flex-none text-v2-warning" />
              <div>
                {bulkSkipped} selected {bulkSkipped === 1 ? 'user is' : 'users are'} skipped — you can't manage
                {bulkSkipped === 1 ? ' it' : ' them'} (or it's your own account). To permanently delete a user, use the single-row action.
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* Bulk status toggle — platform Modal confirm. Flips active ↔ deactivated
          per user via the same per-user endpoints; targets pre-filtered to those
          the actor may manage. */}
      <Modal
        isOpen={bulkToggleOpen}
        onClose={bulkToggling ? undefined : () => setBulkToggleOpen(false)}
        title={`Toggle status for ${bulkTargets.length} user${bulkTargets.length === 1 ? '' : 's'}?`}
        ariaLabel="Bulk status toggle confirmation"
        size="sm"
        footer={(
          <div className="flex items-center justify-end gap-2">
            <Button variant="ghost" onClick={() => setBulkToggleOpen(false)} disabled={bulkToggling}>Cancel</Button>
            <Button variant="primary" onClick={runBulkToggle} loading={bulkToggling} disabled={bulkToggling || bulkTargets.length === 0}>
              {bulkToggling ? 'Updating…' : 'Toggle status'}
            </Button>
          </div>
        )}
      >
        <div className="space-y-3 text-[12.5px] text-v2-ink">
          {bulkTargets.length === 0 ? (
            <p className="text-v2-ink-secondary">None of the selected users can be changed by you. Nothing will happen.</p>
          ) : (
            <>
              <p className="text-v2-ink-secondary">Each selected user's status will be flipped:</p>
              <ul className="space-y-1">
                {bulkToDeactivate.length > 0 && (
                  <li><strong>{bulkToDeactivate.length}</strong> active → <span className="text-v2-warning">deactivated</span> (blocked from login, recoverable)</li>
                )}
                {bulkToReactivate.length > 0 && (
                  <li><strong>{bulkToReactivate.length}</strong> deactivated → <span className="text-v2-success">active</span> (restored, can log in)</li>
                )}
              </ul>
            </>
          )}
          {bulkSkipped > 0 && (
            <div className="px-3 py-2 rounded-v2-sm bg-v2-warning-soft border border-v2-warning/20 text-[12px] flex items-start gap-2">
              <AlertTriangle size={13} className="mt-0.5 flex-none text-v2-warning" />
              <div>{bulkSkipped} selected {bulkSkipped === 1 ? 'row is' : 'rows are'} skipped — you can't manage {bulkSkipped === 1 ? 'it' : 'them'} (or it's your own account).</div>
            </div>
          )}
        </div>
      </Modal>

      {/* G378 — Add / Edit User: Modal → Slideout for V2 consistency. */}
      <Slideout
        isOpen={addUserOpen}
        onClose={userSaving ? undefined : () => setAddUserOpen(false)}
        ariaLabel={editUser ? `Edit user ${editUser.email}` : 'Add user'}
        width={520}
        header={
          <div>
            <div className="flex items-center gap-2 mb-2">
              <UserPlus size={14} className="text-v2-action-strong" />
              <Pill tone="muted" size="sm">{editUser ? 'edit' : 'new'}</Pill>
            </div>
            <h2 className="text-[16px] font-bold m-0">{editUser ? `Edit user — ${editUser.email}` : 'Add user'}</h2>
            <p className="text-[12px] text-v2-ink-secondary mt-1">Create or edit a user record. Group memberships and agent assignments are managed separately.</p>
          </div>
        }
        footer={(
          <div className="flex items-center justify-end gap-2">
            <Button variant="ghost" onClick={() => setAddUserOpen(false)} disabled={userSaving}>Cancel</Button>
            <Button variant="primary" onClick={submitUser} loading={userSaving}>
              {userSaving ? 'Saving…' : (editUser ? 'Save changes' : 'Create user')}
            </Button>
          </div>
        )}
      >
        <div className="space-y-3 text-[12.5px]">
          <div>
            <label className="block text-[10.5px] uppercase tracking-wider font-semibold text-v2-ink-muted mb-1">Email</label>
            <input
              type="email" autoFocus
              value={userForm.email}
              onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
              placeholder="alice@example.com"
              disabled={!!editUser || userSaving}
              className="v2-focus-ring w-full bg-v2-surface text-v2-ink border border-v2-border rounded-v2-md px-3 py-1.5 text-[12.5px]"
            />
          </div>
          <div>
            <label className="block text-[10.5px] uppercase tracking-wider font-semibold text-v2-ink-muted mb-1">Full name</label>
            <input
              type="text"
              value={userForm.name}
              onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
              placeholder="Alice Example"
              disabled={userSaving}
              className="v2-focus-ring w-full bg-v2-surface text-v2-ink border border-v2-border rounded-v2-md px-3 py-1.5 text-[12.5px]"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10.5px] uppercase tracking-wider font-semibold text-v2-ink-muted mb-1">Role</label>
              <select
                value={userForm.role}
                onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}
                disabled={userSaving}
                className="v2-focus-ring w-full bg-v2-surface text-v2-ink border border-v2-border rounded-v2-md px-3 py-1.5 text-[12.5px]"
              >
                {/*
                  V208 / RBAC v2 + RBAC Phase 2 — drive options from allRoleNames
                  (the full built-in taxonomy ∪ the org's CUSTOM roles) instead of
                  a hardcoded subset. Previously this select listed only 6 roles,
                  omitting Super Admin / Instance Admin / Promoter / Approver /
                  Compliance / Auditor AND every custom role — so a Platform Admin
                  (or an Org Admin who had just created a custom role) couldn't
                  grant it even when authority allowed. canManageRoleL supplies
                  each role's authoritative level (custom = server CUSTOM_ROLE_LEVEL)
                  to the shared canManageRole. The current user's role is always
                  included so the edit modal can re-save a user whose current grant
                  outranks the caller (e.g. an Org Admin editing a Super Admin's
                  profile without changing the role).
                */}
                {Array.from(new Set([...(userForm.role ? [userForm.role] : []), ...allRoleNames]))
                  .filter((r) => canManageRoleL(r) || r === userForm.role)
                  .map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
              </select>
            </div>
            <div>
              <label className="block text-[10.5px] uppercase tracking-wider font-semibold text-v2-ink-muted mb-1">Status</label>
              <select
                value={userForm.status}
                onChange={(e) => setUserForm({ ...userForm, status: e.target.value })}
                disabled={userSaving}
                className="v2-focus-ring w-full bg-v2-surface text-v2-ink border border-v2-border rounded-v2-md px-3 py-1.5 text-[12.5px]"
              >
                <option value="active">active</option>
                <option value="invited">invited</option>
                <option value="suspended">suspended</option>
              </select>
            </div>
          </div>
          {/* Customer → Org → Tenant. A Platform Admin picks the target CUSTOMER
              first; the Org list then scopes to that customer so a user can't
              accidentally land in a PLATFORM-CUSTOMER org. (custList: PA=all,
              SA=own customer.) The grant carries org+tenant; the customer is
              derived from the org, so no payload change is needed. */}
          {(activeCustList || []).length > 0 && (
            <div>
              <label className="block text-[10.5px] uppercase tracking-wider font-semibold text-v2-ink-muted mb-1">Customer{(activeCustList || []).length > 1 ? ' *' : ''}</label>
              <select
                value={userForm.customerId || ''}
                onChange={(e) => setUserForm({ ...userForm, customerId: e.target.value, orgId: '', tenantId: '' })}
                disabled={userSaving}
                className="v2-focus-ring w-full bg-v2-surface text-v2-ink border border-v2-border rounded-v2-md px-3 py-1.5 text-[12.5px]"
              >
                <option value="">Select customer…</option>
                {(activeCustList || []).map((c) => <option key={c.id} value={c.id}>{c.displayName || c.name || c.id}</option>)}
              </select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10.5px] uppercase tracking-wider font-semibold text-v2-ink-muted mb-1">Organization *</label>
              <select
                value={userForm.orgId}
                onChange={(e) => setUserForm({ ...userForm, orgId: e.target.value, tenantId: '' })}
                disabled={userSaving}
                className="v2-focus-ring w-full bg-v2-surface text-v2-ink border border-v2-border rounded-v2-md px-3 py-1.5 text-[12.5px]"
              >
                <option value="">Select org…</option>
                {(orgs || [])
                  .filter((o) => { const oc = o.customerId || o.customer_id; return !userForm.customerId || oc === userForm.customerId; })
                  .map((o) => <option key={o.id} value={o.id}>{o.name || o.id}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[10.5px] uppercase tracking-wider font-semibold text-v2-ink-muted mb-1">Tenant *</label>
              <select
                value={userForm.tenantId}
                onChange={(e) => setUserForm({ ...userForm, tenantId: e.target.value })}
                disabled={userSaving || !userForm.orgId}
                className="v2-focus-ring w-full bg-v2-surface text-v2-ink border border-v2-border rounded-v2-md px-3 py-1.5 text-[12.5px]"
              >
                <option value="">Select tenant…</option>
                {(tenants || [])
                  .filter((t) => { const toid = t.orgId || t.org_id; return !userForm.orgId || !toid || toid === userForm.orgId; })
                  .map((t) => <option key={t.id} value={t.id}>{t.name || t.id}</option>)}
              </select>
            </div>
          </div>
          {userError && (
            <div className="rounded-v2-md border border-v2-danger-border bg-v2-danger-soft px-3 py-2 text-[12px] text-v2-danger">
              {userError}
            </div>
          )}
          <div className="text-[11px] text-v2-ink-muted">
            Group memberships and per-agent assignments can be managed from the user's slideout
            once the user record exists.
          </div>
        </div>
      </Slideout>

      {/* G378 — Invite User: Modal → Slideout. */}
      <Slideout
        isOpen={inviteOpen}
        onClose={inviteSaving ? undefined : () => setInviteOpen(false)}
        ariaLabel="Invite user"
        width={560}
        header={
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Send size={14} className="text-v2-action-strong" />
              <Pill tone="muted" size="sm">invite</Pill>
            </div>
            <h2 className="text-[16px] font-bold m-0">Invite User</h2>
            <p className="text-[12px] text-v2-ink-secondary mt-1">Send a registration link to a new operator. They'll land in the org + tenant you select.</p>
          </div>
        }
        footer={(
          <div className="flex items-center justify-end gap-2">
            <Button variant="ghost" onClick={() => setInviteOpen(false)} disabled={inviteSaving}>Cancel</Button>
            <Button variant="primary" onClick={submitInvite} loading={inviteSaving}>
              {inviteSaving ? 'Sending…' : 'Send invitation'}
            </Button>
          </div>
        )}
      >
        <div className="space-y-3 text-[12.5px]">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10.5px] uppercase tracking-wider font-semibold text-v2-ink-muted mb-1">First name *</label>
              <input
                type="text" autoFocus
                value={inviteForm.firstName}
                onChange={(e) => setInviteForm({ ...inviteForm, firstName: e.target.value })}
                disabled={inviteSaving}
                className="v2-focus-ring w-full bg-v2-surface text-v2-ink border border-v2-border rounded-v2-md px-3 py-1.5 text-[12.5px]"
              />
            </div>
            <div>
              <label className="block text-[10.5px] uppercase tracking-wider font-semibold text-v2-ink-muted mb-1">Last name</label>
              <input
                type="text"
                value={inviteForm.lastName}
                onChange={(e) => setInviteForm({ ...inviteForm, lastName: e.target.value })}
                disabled={inviteSaving}
                className="v2-focus-ring w-full bg-v2-surface text-v2-ink border border-v2-border rounded-v2-md px-3 py-1.5 text-[12.5px]"
              />
            </div>
          </div>
          <div>
            <label className="block text-[10.5px] uppercase tracking-wider font-semibold text-v2-ink-muted mb-1">Email *</label>
            <input
              type="email"
              value={inviteForm.email}
              onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
              placeholder="alice@example.com"
              disabled={inviteSaving}
              className="v2-focus-ring w-full bg-v2-surface text-v2-ink border border-v2-border rounded-v2-md px-3 py-1.5 text-[12.5px]"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <div>
              <label className="block text-[10.5px] uppercase tracking-wider font-semibold text-v2-ink-muted mb-1">Role</label>
              <select
                value={inviteForm.role}
                onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value })}
                disabled={inviteSaving}
                className="v2-focus-ring w-full bg-v2-surface text-v2-ink border border-v2-border rounded-v2-md px-3 py-1.5 text-[12.5px]"
              >
                {/* RBAC Phase 2 — invite must offer the same assignable set as
                    Add/Edit: built-in ∪ org custom roles, scoped to what the
                    inviter may grant (canManageRoleL carries each role's
                    authoritative level). Previously a hardcoded six-role list
                    that omitted both functional built-ins and every custom role,
                    so a team could create a custom role but never invite into it.
                    The current inviteForm.role is always kept selectable so a
                    pre-filled value is never silently dropped. */}
                {Array.from(new Set([...(inviteForm.role ? [inviteForm.role] : []), ...allRoleNames]))
                  .filter((r) => canManageRoleL(r) || r === inviteForm.role)
                  .map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
              </select>
            </div>
            {(activeCustList || []).length > 0 && (
              <div>
                <label className="block text-[10.5px] uppercase tracking-wider font-semibold text-v2-ink-muted mb-1">Customer{(activeCustList || []).length > 1 ? ' *' : ''}</label>
                <select
                  value={inviteForm.customerId || ''}
                  onChange={(e) => setInviteForm({ ...inviteForm, customerId: e.target.value, orgId: '', tenantId: '' })}
                  disabled={inviteSaving}
                  className="v2-focus-ring w-full bg-v2-surface text-v2-ink border border-v2-border rounded-v2-md px-3 py-1.5 text-[12.5px]"
                >
                  <option value="">Select customer…</option>
                  {(activeCustList || []).map((c) => <option key={c.id} value={c.id}>{c.displayName || c.name || c.id}</option>)}
                </select>
              </div>
            )}
            <div>
              <label className="block text-[10.5px] uppercase tracking-wider font-semibold text-v2-ink-muted mb-1">Organization *</label>
              <select
                value={inviteForm.orgId}
                onChange={(e) => setInviteForm({ ...inviteForm, orgId: e.target.value, tenantId: '' })}
                disabled={inviteSaving}
                className="v2-focus-ring w-full bg-v2-surface text-v2-ink border border-v2-border rounded-v2-md px-3 py-1.5 text-[12.5px]"
              >
                <option value="">Select org…</option>
                {(orgs || [])
                  .filter((o) => { const oc = o.customerId || o.customer_id; return !inviteForm.customerId || oc === inviteForm.customerId; })
                  .map(o => <option key={o.id} value={o.id}>{o.name || o.id}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[10.5px] uppercase tracking-wider font-semibold text-v2-ink-muted mb-1">Tenant *</label>
              <select
                value={inviteForm.tenantId}
                onChange={(e) => setInviteForm({ ...inviteForm, tenantId: e.target.value })}
                disabled={inviteSaving || !inviteForm.orgId}
                className="v2-focus-ring w-full bg-v2-surface text-v2-ink border border-v2-border rounded-v2-md px-3 py-1.5 text-[12.5px]"
              >
                <option value="">Select tenant…</option>
                {(tenants || [])
                  .filter((t) => { const toid = t.orgId || t.org_id; return !inviteForm.orgId || !toid || toid === inviteForm.orgId; })
                  .map(t => <option key={t.id} value={t.id}>{t.name || t.id}</option>)}
              </select>
            </div>
          </div>
          {inviteError && (
            <div className="rounded-v2-md border border-v2-danger-border bg-v2-danger-soft px-3 py-2 text-[12px] text-v2-danger">
              {inviteError}
            </div>
          )}
        </div>
      </Slideout>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════
 *  USER ACCESS PANELS (G362)
 *
 *  Per-user computed access views shown inside the UsersInventory
 *  slideout. Replaces the previous "Manage in v1" stubs with real
 *  V2-native tables that trace the access chain (direct grant vs
 *  via-group).
 *
 *  Inputs:
 *    user        — selected user record
 *    assignments — all agent assignments for the tenant
 *    agents      — agent inventory (id → name resolution)
 *    workflows   — workflow inventory (id → name resolution)
 *    groupsList  — groups with `members[]` arrays
 *
 *  Algorithm:
 *    1. Direct: assignments where userId === user.id
 *    2. Indirect: collect groupIds the user belongs to; then
 *       assignments where groupId ∈ those groupIds.
 *    3. Merge, dedupe by (resource, accessLevel), label source.
 * ──────────────────────────────────────────────────────────────────────── */
function userGroupIds(user, groupsList) {
  if (!user?.id || !Array.isArray(groupsList)) return [];
  return groupsList
    .filter(g => {
      const members = Array.isArray(g.members) ? g.members
                    : Array.isArray(g.userIds) ? g.userIds : [];
      return members.includes(user.id) || members.some(m => m?.userId === user.id);
    })
    .map(g => ({ id: g.id, name: g.name || g.id }));
}

function UserAgentAccessPanel({ user, assignments, agents, groupsList, onOpenAssignments }) {
  const userGroups = useMemo(() => userGroupIds(user, groupsList), [user, groupsList]);
  const groupIdSet = useMemo(() => new Set(userGroups.map(g => g.id)), [userGroups]);

  const rows = useMemo(() => {
    const out = new Map(); // key = `${agentId}:${accessLevel}` → row
    for (const a of (assignments || [])) {
      if (!a.agentId) continue;
      const matchesUser  = a.userId  === user?.id;
      const matchesGroup = a.groupId && groupIdSet.has(a.groupId);
      if (!matchesUser && !matchesGroup) continue;
      const key = `${a.agentId}:${a.accessLevel || 'read'}`;
      const existing = out.get(key) || {
        agentId: a.agentId, agent: agents.find(x => x.id === a.agentId),
        accessLevel: a.accessLevel || 'read', sources: [],
      };
      const src = matchesUser
        ? 'direct'
        : `via group: ${userGroups.find(g => g.id === a.groupId)?.name || a.groupId}`;
      if (!existing.sources.includes(src)) existing.sources.push(src);
      out.set(key, existing);
    }
    return Array.from(out.values());
  }, [assignments, agents, user, userGroups, groupIdSet]);

  const pg = usePaginationState(10);
  const visible = pg.paginate(rows);

  return (
    <Card padding="sm">
      <Card.Header
        title={`Agent access · ${rows.length}`}
        subtitle="Direct grants and grants inherited via group membership."
        actions={onOpenAssignments && (
          <Button variant="ghost" size="sm" onClick={onOpenAssignments}>Manage all assignments →</Button>
        )}
      />
      <Card.Body>
        {rows.length === 0 ? (
          <p className="text-v2-ink-muted text-[12.5px] italic m-0">No agent access for this user.</p>
        ) : (
          <>
            <Table
              ariaLabel="Agent access for user"
              density="compact"
              columns={[
                { key: 'name', header: 'Agent', render: (r) => (
                  <div>
                    <div className="font-semibold text-v2-ink">{r.agent?.name || r.agentId}</div>
                    {r.agent?.archetype && <div className="text-[10.5px] text-v2-ink-muted">{r.agent.archetype}</div>}
                  </div>
                )},
                { key: 'access', header: 'Access', render: (r) => (
                  <Pill tone={r.accessLevel === 'admin' ? 'danger' : r.accessLevel === 'write' ? 'warning' : 'cyan'} size="sm">{r.accessLevel}</Pill>
                )},
                { key: 'source', header: 'Source', render: (r) => (
                  <span className="text-[11px] text-v2-ink-secondary">{r.sources.join(' + ')}</span>
                )},
              ]}
              rows={visible}
              rowKey={(r) => `${r.agentId}:${r.accessLevel}`}
            />
            {rows.length > pg.pageSize && (
              <div className="mt-3">
                <Pagination totalItems={rows.length} page={pg.page} pageSize={pg.pageSize}
                  onPageChange={pg.setPage} onPageSizeChange={pg.setPageSize}
                  noun="agent grants" />
              </div>
            )}
          </>
        )}
      </Card.Body>
    </Card>
  );
}

function UserWorkflowAccessPanel({ user, assignments, workflows, groupsList }) {
  const userGroups = useMemo(() => userGroupIds(user, groupsList), [user, groupsList]);
  const groupIdSet = useMemo(() => new Set(userGroups.map(g => g.id)), [userGroups]);

  const rows = useMemo(() => {
    const out = new Map();
    for (const a of (assignments || [])) {
      if (!a.workflowId) continue;
      const matchesUser  = a.userId === user?.id;
      const matchesGroup = a.groupId && groupIdSet.has(a.groupId);
      if (!matchesUser && !matchesGroup) continue;
      const key = `${a.workflowId}:${a.accessLevel || 'read'}`;
      const existing = out.get(key) || {
        workflowId: a.workflowId, workflow: workflows.find(w => w.id === a.workflowId),
        accessLevel: a.accessLevel || 'read', sources: [],
      };
      const src = matchesUser
        ? 'direct'
        : `via group: ${userGroups.find(g => g.id === a.groupId)?.name || a.groupId}`;
      if (!existing.sources.includes(src)) existing.sources.push(src);
      out.set(key, existing);
    }
    return Array.from(out.values());
  }, [assignments, workflows, user, userGroups, groupIdSet]);

  const pg = usePaginationState(10);
  const visible = pg.paginate(rows);

  return (
    <Card padding="sm">
      <Card.Header
        title={`Workflow access · ${rows.length}`}
        subtitle="Direct grants and grants inherited via group membership."
      />
      <Card.Body>
        {rows.length === 0 ? (
          <p className="text-v2-ink-muted text-[12.5px] italic m-0">No workflow access for this user.</p>
        ) : (
          <>
            <Table
              ariaLabel="Workflow access for user"
              density="compact"
              columns={[
                { key: 'name', header: 'Workflow', render: (r) => (
                  <span className="font-semibold text-v2-ink">{r.workflow?.name || r.workflowId}</span>
                )},
                { key: 'access', header: 'Access', render: (r) => (
                  <Pill tone={r.accessLevel === 'admin' ? 'danger' : r.accessLevel === 'write' ? 'warning' : 'cyan'} size="sm">{r.accessLevel}</Pill>
                )},
                { key: 'source', header: 'Source', render: (r) => (
                  <span className="text-[11px] text-v2-ink-secondary">{r.sources.join(' + ')}</span>
                )},
              ]}
              rows={visible}
              rowKey={(r) => `${r.workflowId}:${r.accessLevel}`}
            />
            {rows.length > pg.pageSize && (
              <div className="mt-3">
                <Pagination totalItems={rows.length} page={pg.page} pageSize={pg.pageSize}
                  onPageChange={pg.setPage} onPageSizeChange={pg.setPageSize}
                  noun="workflow grants" />
              </div>
            )}
          </>
        )}
      </Card.Body>
    </Card>
  );
}

/* ── Control Chat access (per-user grants) — reuses the Agent/Workflow
 *    access panel shape. Grants here decide which Control Chat tools this
 *    person may use (deny-wins, default-deny), enforced server-side by the
 *    chat access resolver. Direct, per-individual grants only in this view;
 *    role/group grants are authored on those subjects. ─────────────────── */

function UserChatAccessPanel({ user, agents }) {
  const { addToast } = useToast();
  const [grants, setGrants] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editGrant, setEditGrant] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const chatAgents = useMemo(
    () => (agents || []).filter(a => a && a.id).sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [agents]
  );
  const agentName = useCallback(
    (id) => chatAgents.find(a => a.id === id)?.name || 'Selected chat',
    [chatAgents]
  );

  const load = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      // apiFetch wraps every response as { ok, data, etag }; unwrap `.data`.
      const [g, c] = await Promise.all([
        api.getChatAccessGrants(user.id),
        api.getChatAccessCatalog().catch(() => ({ ok: true, data: { tools: [] } })),
      ]);
      if (g && g.ok === false) throw new Error(g.error || 'Could not load grants');
      const grantsData = (g && g.data) || [];
      setGrants(Array.isArray(grantsData) ? grantsData : (grantsData.grants || []));
      const catData = (c && c.data) || {};
      setCatalog(Array.isArray(catData.tools) ? catData.tools : []);
    } catch (e) {
      setGrants([]);
      addToast({ tone: 'danger', title: 'Could not load chat access', description: 'Please try again in a moment.' });
    } finally {
      setLoading(false);
    }
  }, [user?.id, addToast]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = useCallback(async (body) => {
    const res = await api.createChatAccessGrant({ ...body, subjectType: 'USER', userId: user.id });
    if (!res || res.ok === false) {
      addToast({ tone: 'danger', title: 'Could not add access', description: res?.error || 'Please review the selection and try again.' });
      throw new Error(res?.error || 'add failed');
    }
    addToast({ tone: 'success', title: 'Chat access updated', description: 'The grant is now in effect.' });
    setShowAdd(false);
    await load();
  }, [user?.id, addToast, load]);

  // Edit updates the grant in place (subject stays fixed); reuses the same form as Add.
  const handleSave = useCallback(async (id, body) => {
    const res = await api.updateChatAccessGrant(id, { ...body, subjectType: 'USER', userId: user.id });
    if (!res || res.ok === false) {
      addToast({ tone: 'danger', title: 'Could not save changes', description: res?.error || 'Please review the selection and try again.' });
      throw new Error(res?.error || 'update failed');
    }
    addToast({ tone: 'success', title: 'Chat access updated', description: 'The change is now in effect.' });
    setEditGrant(null);
    await load();
  }, [user?.id, addToast, load]);

  const handleRemove = useCallback(async (id) => {
    setBusyId(id);
    try {
      const res = await api.removeChatAccessGrant(id);
      if (!res || res.ok === false) {
        addToast({ tone: 'danger', title: 'Could not remove access', description: res?.error || 'Please try again in a moment.' });
        return;
      }
      addToast({ tone: 'success', title: 'Access removed' });
      setConfirmId(null);
      await load();
    } finally {
      setBusyId(null);
    }
  }, [addToast, load]);

  const pg = usePaginationState(10);
  const visible = pg.paginate(grants);

  const objectLabel = (g) => (ChatObjIsAgent(g) ? 'All tools' : (g.objectRef || '—'));
  const scopeLabel = (g) => (g.agentId ? agentName(g.agentId) : 'All chats');
  const fmtExpiry = (g) => {
    if (!g.expiresAt) return 'No expiry';
    try { return new Date(g.expiresAt).toLocaleDateString(); } catch { return String(g.expiresAt); }
  };

  return (
    <Card padding="sm">
      <Card.Header
        title={`Chat access · ${grants.length}`}
        subtitle="Tools this person may use in Control Chat. Denials always win over grants."
        actions={
          <Button variant="ghost" size="sm" iconLeft={<Plus size={12} />}
            onClick={() => { setEditGrant(null); setShowAdd(v => !v); }}>
            {showAdd ? 'Close' : 'Add access'}
          </Button>
        }
      />
      <Card.Body>
        <InlineAddChatAccessPanel
          key={editGrant ? `edit-${editGrant.id}` : 'add'}
          isOpen={showAdd || !!editGrant}
          editGrant={editGrant}
          onCancel={() => { setShowAdd(false); setEditGrant(null); }}
          agents={chatAgents}
          catalog={catalog}
          existingGrants={grants}
          onAdd={handleAdd}
          onSave={handleSave}
        />
        {loading ? (
          <p className="text-v2-ink-muted text-[12.5px] italic m-0">Loading chat access…</p>
        ) : grants.length === 0 ? (
          <p className="text-v2-ink-muted text-[12.5px] italic m-0">No chat access granted to this person yet.</p>
        ) : (
          <>
            {/* Compact 3-column layout: the user slideout is narrow (~450px), so applies-to /
                data-scope / expiry fold into a subtext line under the tool name — otherwise the
                table overflows and the Edit/Remove actions are clipped off the right edge. */}
            <Table
              ariaLabel="Chat access for user"
              density="compact"
              columns={[
                { key: 'object', header: 'Tool', render: (g) => (
                  <div className="min-w-0">
                    <div className="font-semibold text-v2-ink truncate">{objectLabel(g)}</div>
                    <div className="text-[10.5px] text-v2-ink-muted truncate">
                      {scopeLabel(g)} · {chatDataScopeSummary(g)} · {fmtExpiry(g)}
                    </div>
                  </div>
                )},
                { key: 'effect', header: 'Effect', render: (g) => (
                  <Pill tone={ChatEffectIsDeny(g) ? 'danger' : 'success'} size="sm">
                    {ChatEffectIsDeny(g) ? 'DENY' : 'ALLOW'}
                  </Pill>
                )},
                { key: 'actions', header: '', render: (g) => {
                  if (confirmId === g.id) {
                    return (
                      <div className="flex items-center gap-1.5 justify-end">
                        <span className="text-[11px] text-v2-ink-muted">Remove?</span>
                        <Button size="xs" variant="danger" disabled={busyId === g.id} onClick={() => handleRemove(g.id)}>Yes</Button>
                        <Button size="xs" variant="ghost" disabled={busyId === g.id} onClick={() => setConfirmId(null)}>No</Button>
                      </div>
                    );
                  }
                  return (
                    <div className="flex items-center justify-end gap-0.5">
                      <Button size="xs" variant="ghost" iconLeft={<Edit2 size={12} />} aria-label="Edit access" title="Edit"
                        onClick={() => { setConfirmId(null); setShowAdd(false); setEditGrant(g); }} />
                      <Button size="xs" variant="ghost" iconLeft={<Trash2 size={12} />} aria-label="Remove access" title="Remove"
                        onClick={() => setConfirmId(g.id)} />
                    </div>
                  );
                }},
              ]}
              rows={visible}
              rowKey={(g) => g.id}
            />
            {grants.length > pg.pageSize && (
              <div className="mt-3">
                <Pagination totalItems={grants.length} page={pg.page} pageSize={pg.pageSize}
                  onPageChange={pg.setPage} onPageSizeChange={pg.setPageSize}
                  noun="chat grants" />
              </div>
            )}
          </>
        )}
      </Card.Body>
    </Card>
  );
}

/* Small helpers kept string-tolerant: the API returns effect/objectType as
 * upper-case strings; guard against casing drift without a hard dependency. */
function ChatEffectIsDeny(g) { return String(g?.effect || '').toUpperCase() === 'DENY'; }
function ChatObjIsAgent(g) { return String(g?.objectType || '').toUpperCase() === 'AGENT'; }
/* Compact one-line summary of a grant's data_scope JSON, e.g. "region=EU". Shared by the
 * user + group chat-access tables. "Full" = no scope (the subject sees everything allowed). */
function chatDataScopeSummary(g) {
  const s = g?.dataScope;
  if (!s || typeof s !== 'object' || Array.isArray(s) || Object.keys(s).length === 0) return 'Full';
  return Object.entries(s).map(([k, v]) => `${k}=${v}`).join(' · ');
}

/* ── Group-level Control Chat access. A grant authored here has subjectType=GROUP,
 *    so EVERY member of the group inherits it live via ChatAccessResolver (group
 *    membership is the single source of truth — add a user to EU_Group and they get
 *    the group's tools + data_scope with no per-user grant). Mirrors the per-user
 *    panel and reuses the same add-form + scope helpers. ─────────────────────────── */
function GroupChatAccessPanel({ group, agents }) {
  const { addToast } = useToast();
  const [grants, setGrants] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editGrant, setEditGrant] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const chatAgents = useMemo(
    () => (agents || []).filter(a => a && a.id).sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [agents]
  );
  const agentName = useCallback(
    (id) => chatAgents.find(a => a.id === id)?.name || 'Selected chat',
    [chatAgents]
  );

  const load = useCallback(async () => {
    if (!group?.id) return;
    setLoading(true);
    try {
      const [g, c] = await Promise.all([
        api.getChatAccessGrants(null, group.id),
        api.getChatAccessCatalog().catch(() => ({ ok: true, data: { tools: [] } })),
      ]);
      if (g && g.ok === false) throw new Error(g.error || 'Could not load grants');
      const grantsData = (g && g.data) || [];
      setGrants(Array.isArray(grantsData) ? grantsData : (grantsData.grants || []));
      const catData = (c && c.data) || {};
      setCatalog(Array.isArray(catData.tools) ? catData.tools : []);
    } catch (e) {
      setGrants([]);
      addToast({ tone: 'danger', title: 'Could not load chat access', description: 'Please try again in a moment.' });
    } finally {
      setLoading(false);
    }
  }, [group?.id, addToast]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = useCallback(async (body) => {
    const res = await api.createChatAccessGrant({ ...body, subjectType: 'GROUP', groupId: group.id });
    if (!res || res.ok === false) {
      addToast({ tone: 'danger', title: 'Could not add access', description: res?.error || 'Please review the selection and try again.' });
      throw new Error(res?.error || 'add failed');
    }
    addToast({ tone: 'success', title: 'Group chat access updated', description: 'Every member inherits this grant.' });
    setShowAdd(false);
    await load();
  }, [group?.id, addToast, load]);

  // Edit updates the group grant in place (subject stays GROUP); reuses the same form as Add.
  const handleSave = useCallback(async (id, body) => {
    const res = await api.updateChatAccessGrant(id, { ...body, subjectType: 'GROUP', groupId: group.id });
    if (!res || res.ok === false) {
      addToast({ tone: 'danger', title: 'Could not save changes', description: res?.error || 'Please review the selection and try again.' });
      throw new Error(res?.error || 'update failed');
    }
    addToast({ tone: 'success', title: 'Group chat access updated', description: 'Every member inherits this change.' });
    setEditGrant(null);
    await load();
  }, [group?.id, addToast, load]);

  const handleRemove = useCallback(async (id) => {
    setBusyId(id);
    try {
      const res = await api.removeChatAccessGrant(id);
      if (!res || res.ok === false) {
        addToast({ tone: 'danger', title: 'Could not remove access', description: res?.error || 'Please try again in a moment.' });
        return;
      }
      addToast({ tone: 'success', title: 'Access removed' });
      setConfirmId(null);
      await load();
    } finally {
      setBusyId(null);
    }
  }, [addToast, load]);

  const pg = usePaginationState(10);
  const visible = pg.paginate(grants);

  const objectLabel = (g) => (ChatObjIsAgent(g) ? 'All tools' : (g.objectRef || '—'));
  const scopeLabel = (g) => (g.agentId ? agentName(g.agentId) : 'All chats');
  const memberCount = (group?.members || []).length;

  return (
    <Card padding="sm">
      <Card.Header
        title={`Group chat access · ${grants.length}`}
        subtitle={`Tools + data scope inherited by all ${memberCount} member${memberCount === 1 ? '' : 's'}. Denials always win over grants.`}
        actions={
          <Button variant="ghost" size="sm" iconLeft={<Plus size={12} />}
            onClick={() => { setEditGrant(null); setShowAdd(v => !v); }}>
            {showAdd ? 'Close' : 'Add access'}
          </Button>
        }
      />
      <Card.Body>
        <InlineAddChatAccessPanel
          key={editGrant ? `edit-${editGrant.id}` : 'add'}
          isOpen={showAdd || !!editGrant}
          editGrant={editGrant}
          onCancel={() => { setShowAdd(false); setEditGrant(null); }}
          agents={chatAgents}
          catalog={catalog}
          existingGrants={grants}
          onAdd={handleAdd}
          onSave={handleSave}
          subjectNoun="members of this group"
        />
        {loading ? (
          <p className="text-v2-ink-muted text-[12.5px] italic m-0">Loading chat access…</p>
        ) : grants.length === 0 ? (
          <p className="text-v2-ink-muted text-[12.5px] italic m-0">No chat access granted to this group yet.</p>
        ) : (
          <>
            {/* Compact 3-column layout (same as the user panel): the group slideout is narrow,
                so applies-to / data-scope fold into a subtext line to keep Edit/Remove on-screen. */}
            <Table
              ariaLabel="Chat access for group"
              density="compact"
              columns={[
                { key: 'object', header: 'Tool', render: (g) => (
                  <div className="min-w-0">
                    <div className="font-semibold text-v2-ink truncate">{objectLabel(g)}</div>
                    <div className="text-[10.5px] text-v2-ink-muted truncate">
                      {scopeLabel(g)} · {chatDataScopeSummary(g)}
                    </div>
                  </div>
                )},
                { key: 'effect', header: 'Effect', render: (g) => (
                  <Pill tone={ChatEffectIsDeny(g) ? 'danger' : 'success'} size="sm">
                    {ChatEffectIsDeny(g) ? 'DENY' : 'ALLOW'}
                  </Pill>
                )},
                { key: 'actions', header: '', render: (g) => {
                  if (confirmId === g.id) {
                    return (
                      <div className="flex items-center gap-1.5 justify-end">
                        <span className="text-[11px] text-v2-ink-muted">Remove?</span>
                        <Button size="xs" variant="danger" disabled={busyId === g.id} onClick={() => handleRemove(g.id)}>Yes</Button>
                        <Button size="xs" variant="ghost" disabled={busyId === g.id} onClick={() => setConfirmId(null)}>No</Button>
                      </div>
                    );
                  }
                  return (
                    <div className="flex items-center justify-end gap-0.5">
                      <Button size="xs" variant="ghost" iconLeft={<Edit2 size={12} />} aria-label="Edit access" title="Edit"
                        onClick={() => { setConfirmId(null); setShowAdd(false); setEditGrant(g); }} />
                      <Button size="xs" variant="ghost" iconLeft={<Trash2 size={12} />} aria-label="Remove access" title="Remove"
                        onClick={() => setConfirmId(g.id)} />
                    </div>
                  );
                }},
              ]}
              rows={visible}
              rowKey={(g) => g.id}
            />
            {grants.length > pg.pageSize && (
              <div className="mt-3">
                <Pagination totalItems={grants.length} page={pg.page} pageSize={pg.pageSize}
                  onPageChange={pg.setPage} onPageSizeChange={pg.setPageSize}
                  noun="chat grants" />
              </div>
            )}
          </>
        )}
      </Card.Body>
    </Card>
  );
}

/* Shared data-scope editor: key/value rows → { key: value, … } (or null when empty).
 * No hardcoded keys — an admin scopes on region, department, business_unit, etc.; the
 * value rides the grant as data_scope and is enforced at the data boundary as
 * _prove7DataScope. Reused by both the per-user and per-group grant forms. */
function DataScopeEditor({ value, onChange }) {
  const [rows, setRows] = useState(() => {
    const entries = value && typeof value === 'object' ? Object.entries(value) : [];
    return entries.length ? entries.map(([k, v]) => ({ k, v: String(v) })) : [{ k: '', v: '' }];
  });
  const emit = (next) => {
    setRows(next);
    const obj = {};
    next.forEach(({ k, v }) => { if (k.trim() && v.trim()) obj[k.trim()] = v.trim(); });
    onChange(Object.keys(obj).length ? obj : null);
  };
  const setRow = (i, patch) => emit(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const addRow = () => emit([...rows, { k: '', v: '' }]);
  const removeRow = (i) => emit(rows.length > 1 ? rows.filter((_, j) => j !== i) : [{ k: '', v: '' }]);
  const inputCls = 'v2-focus-ring border border-v2-border rounded-v2-md px-2 py-1 bg-v2-surface text-[12px]';
  return (
    <div className="space-y-1.5">
      {rows.map((r, i) => (
        <div key={i} className="flex items-center gap-1.5">
          <input className={`${inputCls} w-[38%]`} placeholder="key · e.g. region" value={r.k}
            onChange={(e) => setRow(i, { k: e.target.value })} />
          <span className="text-v2-ink-muted text-[12px]">=</span>
          <input className={`${inputCls} flex-1`} placeholder="value · e.g. EU" value={r.v}
            onChange={(e) => setRow(i, { v: e.target.value })} />
          <button type="button" onClick={() => removeRow(i)} aria-label="Remove scope field"
            className="v2-focus-ring rounded-v2-sm p-0.5 text-v2-ink-muted hover:text-v2-ink hover:bg-v2-muted"><X size={12} /></button>
        </div>
      ))}
      <button type="button" onClick={addRow}
        className="v2-focus-ring rounded-v2-sm text-[11px] text-v2-action-strong font-semibold px-1 py-0.5 hover:bg-v2-action-soft">+ Add field</button>
    </div>
  );
}

// Add OR edit a chat-access grant. When `editGrant` is supplied the form prefills from it and saves
// through `onSave(id, body)` (a PUT that updates the grant in place); otherwise it creates through
// `onAdd(body)`. The subject (who the grant is for) is immutable and owned by the parent panel — this
// form only edits the object, scope, effect, and data scope, so re-targeting a subject is delete+create.
function InlineAddChatAccessPanel({ isOpen, onCancel, agents, catalog, onAdd, onSave, editGrant = null, subjectNoun = 'this person', existingGrants = [] }) {
  const isEdit = !!editGrant;
  const [scope, setScope] = useState('');        // agentId or '' = all chats
  const [grantKind, setGrantKind] = useState('tool'); // 'tool' | 'agent'
  const [toolName, setToolName] = useState('');
  const [effect, setEffect] = useState('ALLOW');
  const [dataScope, setDataScope] = useState(null);
  const [saving, setSaving] = useState(false);
  // The grantable tool list follows the chosen chat scope: an agent scope shows
  // THAT agent's live-verified tools (connector-manifest-derived), "All chats"
  // shows the union catalog. Falls back to the parent-supplied union while the
  // scoped fetch is in flight so the dropdown is never briefly empty.
  const [scopedCatalog, setScopedCatalog] = useState(null);
  const toolCatalog = scopedCatalog || catalog;
  // Distinct connector/system groups present in the catalog, for the picker's grouped tabs
  // (Platform Governance first, then connectors alphabetically). EntityPicker needs both `groups`
  // and `getGroupId` to render grouped.
  const toolGroups = useMemo(() => {
    const seen = new Map();
    (toolCatalog || []).forEach((t) => { const g = t.group || 'Other'; if (!seen.has(g)) seen.set(g, { id: g, label: g }); });
    const arr = Array.from(seen.values());
    arr.sort((a, b) => (a.id === 'Platform Governance' ? -1 : b.id === 'Platform Governance' ? 1 : a.label.localeCompare(b.label)));
    return arr;
  }, [toolCatalog]);

  useEffect(() => {
    if (!isOpen) return;
    // Prefill from the grant under edit, else reset to add-mode defaults.
    if (editGrant) {
      setScope(editGrant.agentId || '');
      const asAgent = String(editGrant.objectType || '').toUpperCase() === 'AGENT';
      setGrantKind(asAgent ? 'agent' : 'tool');
      setToolName(asAgent ? '' : (editGrant.objectRef || ''));
      setEffect(String(editGrant.effect || 'ALLOW').toUpperCase());
      setDataScope(editGrant.dataScope && typeof editGrant.dataScope === 'object' ? editGrant.dataScope : null);
    } else {
      setScope(''); setGrantKind('tool'); setToolName(''); setEffect('ALLOW'); setDataScope(null);
    }
    setScopedCatalog(null);
  }, [isOpen, editGrant]);

  // Refetch the agent-scoped catalog whenever the scope changes; clear any tool
  // pick that isn't in the new agent's tool set.
  useEffect(() => {
    if (!isOpen) return;
    let live = true;
    api.getChatAccessCatalog(scope || undefined)
      .then((c) => { if (!live) return;
        const tools = Array.isArray(c?.data?.tools) ? c.data.tools : [];
        setScopedCatalog(tools);
        setToolName((prev) => (prev && tools.some(t => t.name === prev) ? prev : ''));
      })
      .catch(() => { if (live) setScopedCatalog(null); });
    return () => { live = false; };
  }, [scope, isOpen]);

  if (!isOpen) return null;

  // #4 — one non-contradicting effect per skill. A specific-tool grant conflicts when the SAME
  // (chat scope, tool) already has a grant, OR when the same scope already has an "all tools" grant
  // that would double-cover this skill (the Allow-All-then-Deny-specific contradiction). An all-tools
  // grant conflicts when one already exists for the scope. Editing the offending grant is excluded.
  const _norm = (s) => String(s || '').toUpperCase();
  const _scopeKey = scope || '';
  const _sameScope = (g) => (g.agentId || '') === _scopeKey && g.id !== (editGrant && editGrant.id);
  const conflict = (() => {
    if (grantKind === 'tool') {
      if (!toolName) return null;
      const dup = existingGrants.find((g) => _sameScope(g) && _norm(g.objectType) === 'TOOL' && g.objectRef === toolName);
      if (dup) return { kind: 'tool', effect: _norm(dup.effect) };
      const all = existingGrants.find((g) => _sameScope(g) && _norm(g.objectType) === 'AGENT');
      if (all) return { kind: 'all', effect: _norm(all.effect) };
      return null;
    }
    const dupAll = existingGrants.find((g) => _sameScope(g) && _norm(g.objectType) === 'AGENT');
    if (dupAll) return { kind: 'all', effect: _norm(dupAll.effect) };
    // Symmetric with the backend: an all-skills grant also conflicts when per-skill grants already
    // exist on this chat (either authoring order), so it can't silently override or double-cover them.
    const anySpecific = existingGrants.find((g) => _sameScope(g) && (_norm(g.objectType) === 'TOOL' || _norm(g.objectType) === 'SKILL'));
    return anySpecific ? { kind: 'specific-exists' } : null;
  })();

  const canSave = !saving && !conflict && (grantKind === 'agent' || !!toolName);
  const handle = async () => {
    setSaving(true);
    try {
      const body = {
        agentId: scope || null,
        objectType: grantKind === 'agent' ? 'AGENT' : 'TOOL',
        objectRef: grantKind === 'agent' ? null : toolName,
        effect,
        // Scope only narrows an ALLOW; a DENY is absolute, so never attach scope to it.
        dataScope: effect === 'ALLOW' ? dataScope : null,
      };
      if (isEdit) await onSave(editGrant.id, body);
      else await onAdd(body);
    } catch (_) { /* toast already shown */ }
    finally { setSaving(false); }
  };

  const selectCls = 'v2-focus-ring w-full border border-v2-border rounded-v2-md px-2.5 py-1.5 bg-v2-surface text-[12.5px]';
  const labelCls = 'block text-[10.5px] uppercase tracking-wider text-v2-ink-muted font-semibold mb-1';

  return (
    <div className="rounded-v2-md border border-v2-action-soft bg-v2-action-soft/40 p-3 mb-3 space-y-3 text-[12.5px]">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Activity size={13} className="text-v2-action-strong" />
          <span className="font-semibold text-v2-ink">{isEdit ? 'Edit chat access' : 'Grant chat access'}</span>
        </div>
        <button type="button" onClick={onCancel} aria-label="Close add form"
          className="v2-focus-ring rounded-v2-sm p-0.5 text-v2-ink-muted hover:text-v2-ink hover:bg-v2-muted">
          <X size={12} />
        </button>
      </div>

      <label className="block">
        <span className={labelCls}>Applies to</span>
        <select value={scope} onChange={(e) => setScope(e.target.value)} className={selectCls}>
          <option value="">All chats</option>
          {agents.map(a => (
            <option key={a.id} value={a.id}>{a.name}{a.archetype ? ` · ${a.archetype}` : ''}</option>
          ))}
        </select>
      </label>

      <label className="block">
        <span className={labelCls}>Grant</span>
        <select value={grantKind} onChange={(e) => setGrantKind(e.target.value)} className={selectCls}>
          <option value="tool">A specific tool</option>
          <option value="agent">All tools in the chat</option>
        </select>
      </label>

      {grantKind === 'tool' && (
        <div className="block">
          <span className={labelCls}>Tool</span>
          {/* #2/#3 — smart, searchable picker GROUPED by the connector/system that provides each skill
              ("Salesforce", "ServiceNow", "Platform Governance"), so it is obvious which skill belongs
              to which tool. Items come from the scope-aware catalog (agent-scoped when an agent is
              chosen; the COMPLETE cross-agent union otherwise), each carrying its `group`. */}
          <EntityPicker
            items={toolCatalog}
            value={toolName || null}
            onChange={(name) => setToolName(name || '')}
            getId={(t) => t.name}
            getLabel={(t) => t.name}
            groups={toolGroups}
            getGroupId={(t) => t.group || 'Other'}
            allTabLabel="All skills"
            placeholder="Pick a skill…"
            searchPlaceholder="Search skills or connector…"
            ariaLabel="Chat access skill" />
          {conflict && (
            <p className="text-[11px] text-v2-danger mt-1 leading-snug">
              {conflict.kind === 'tool'
                ? <>“{toolName}” already has a <b>{conflict.effect}</b> grant{_scopeKey ? ' for this chat' : ''}. A skill can carry only one effect — edit the existing grant instead of adding a conflicting one.</>
                : <>An <b>all-tools {conflict.effect}</b> grant already covers this chat, so a per-skill grant would contradict it. Edit the all-tools grant, or remove it before adding per-skill effects.</>}
            </p>
          )}
        </div>
      )}

      <label className="block">
        <span className={labelCls}>Effect</span>
        <select value={effect} onChange={(e) => setEffect(e.target.value)} className={selectCls}>
          <option value="ALLOW">Allow</option>
          <option value="DENY">Deny</option>
        </select>
      </label>
      {grantKind === 'agent' && conflict && (
        <p className="text-[11px] text-v2-danger leading-snug">
          {conflict.kind === 'specific-exists'
            ? <>This chat already has per-skill grants, so an all-skills grant would conflict with them. Remove or edit the per-skill grants first.</>
            : <>An <b>all-tools {conflict.effect}</b> grant already exists for this chat. Edit it instead of adding a second, contradicting one.</>}
        </p>
      )}

      {effect === 'ALLOW' && (
        <div className="block">
          <span className={labelCls}>Data scope <span className="normal-case font-normal text-v2-ink-muted">· optional</span></span>
          <DataScopeEditor value={dataScope} onChange={setDataScope} />
          <p className="text-[11px] text-v2-ink-muted mt-1 leading-snug">
            Limits which rows {subjectNoun} can see through this access, e.g. region = EU. Leave empty for full scope.
            On a group, every member inherits this scope.
          </p>
        </div>
      )}

      <div className="flex items-center justify-end gap-2 pt-1">
        <Button variant="ghost" size="sm" onClick={onCancel} disabled={saving}>Cancel</Button>
        <Button variant="primary" size="sm" onClick={handle} disabled={!canSave} iconLeft={saving ? null : <CheckCircle2 size={11} />}>
          {saving ? 'Saving…' : (isEdit ? 'Save changes' : 'Add access')}
        </Button>
      </div>
    </div>
  );
}

/* ── Groups tab — G134 full inventory with FacetRail + Slideout ───── */

function GroupsInventory({ onNavigateToTab }) {
  const navigate = useNavigate();
  const platform = usePlatform();
  const { addToast } = useToast();
  const {
    groups = [], loadGroups, deleteGroup: deleteGroupApi,
    addGroupMember: addGroupMemberApi, removeGroupMember: removeGroupMemberApi,
    users = [], loadUsers,
    agents = [], loadAgents,
  } = platform;

  useEffect(() => {
    if (typeof loadGroups === 'function') loadGroups();
    if (typeof loadUsers === 'function') loadUsers();
    if (typeof loadAgents === 'function') loadAgents();
  }, [loadGroups, loadUsers, loadAgents]);

  /* Delete group — destructive, so it confirms through the platform Modal
   * (never window.confirm). deleteGroupApi (platform.deleteGroup) refreshes
   * the group list on success. */
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const confirmDeleteGroup = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = typeof deleteGroupApi === 'function' ? await deleteGroupApi(deleteTarget.id) : null;
      if (res && res.ok === false) {
        addToast({ tone: 'danger', title: 'Delete failed', description: res.error || res.data?.error || `HTTP ${res.status || '?'}` });
        return;
      }
      addToast({ tone: 'success', title: `Group "${deleteTarget.name}" deleted` });
      if (selected && selected.id === deleteTarget.id) setSelected(null);
      setDeleteTarget(null);
      await loadGroups?.();
    } catch (e) {
      addToast({ tone: 'danger', title: 'Delete failed', description: e?.message || String(e) });
    } finally {
      setDeleting(false);
    }
  };

  /* Add / remove members — both call the shared platform helpers, which POST/
   * DELETE /api/v1/team/groups/{groupId}/members/{userId} and reload groups. */
  const [memberToAdd, setMemberToAdd] = useState('');
  const [memberBusy, setMemberBusy] = useState(false);
  const addMember = async (group, userId) => {
    if (!group || !userId) return;
    setMemberBusy(true);
    try {
      const res = typeof addGroupMemberApi === 'function' ? await addGroupMemberApi(group.id, userId) : null;
      if (res && res.ok === false) {
        addToast({ tone: 'danger', title: 'Could not add member', description: res.error || res.data?.error || `HTTP ${res.status || '?'}` });
        return;
      }
      setMemberToAdd('');
      addToast({ tone: 'success', title: 'Member added' });
      await loadGroups?.();
    } catch (e) {
      addToast({ tone: 'danger', title: 'Could not add member', description: e?.message || String(e) });
    } finally {
      setMemberBusy(false);
    }
  };
  const removeMember = async (group, userId) => {
    if (!group || !userId) return;
    setMemberBusy(true);
    try {
      const res = typeof removeGroupMemberApi === 'function' ? await removeGroupMemberApi(group.id, userId) : null;
      if (res && res.ok === false) {
        addToast({ tone: 'danger', title: 'Could not remove member', description: res.error || res.data?.error || `HTTP ${res.status || '?'}` });
        return;
      }
      addToast({ tone: 'success', title: 'Member removed' });
      await loadGroups?.();
    } catch (e) {
      addToast({ tone: 'danger', title: 'Could not remove member', description: e?.message || String(e) });
    } finally {
      setMemberBusy(false);
    }
  };

  /* G199 — V2-native Add Group / Edit Group modal. Calls api.createGroup
   * / api.updateGroup (POST/PUT /api/v1/team/groups). */
  const [addGroupOpen, setAddGroupOpen] = useState(false);
  const [editGroup, setEditGroup] = useState(null);
  const [groupForm, setGroupForm] = useState({ name: '', description: '' });
  const [groupSaving, setGroupSaving] = useState(false);
  const [groupError, setGroupError] = useState(null);
  const openAddGroup = () => {
    setEditGroup(null);
    setGroupForm({ name: '', description: '' });
    setGroupError(null);
    setAddGroupOpen(true);
  };
  const openEditGroup = (g) => {
    setEditGroup(g);
    setGroupForm({ name: g.name || '', description: g.description || '' });
    setGroupError(null);
    setAddGroupOpen(true);
  };
  const submitGroup = async () => {
    setGroupError(null);
    if (!groupForm.name?.trim()) { setGroupError('Group name is required.'); return; }
    setGroupSaving(true);
    try {
      const payload = { name: groupForm.name.trim(), description: groupForm.description.trim() || null };
      const res = editGroup
        ? await api.updateGroup(editGroup.id, payload)
        : await api.createGroup(payload);
      if (!res || res.ok === false) {
        setGroupError(res?.error || res?.data?.error || `Backend rejected the request (HTTP ${res?.status || '?'}).`);
        return;
      }
      addToast({ tone: 'success', title: editGroup ? 'Group updated' : 'Group created' });
      setAddGroupOpen(false);
      await loadGroups?.();
    } catch (e) {
      setGroupError(e?.message || String(e));
    } finally {
      setGroupSaving(false);
    }
  };

  const [facets, setFacets] = useState(() => ({ size: new Set(), source: new Set() }));
  const toggleFacet = (gKey, oKey, c) => setFacets(p => { const n = new Set(p[gKey] || []); c ? n.add(oKey) : n.delete(oKey); return { ...p, [gKey]: n }; });
  const clearFacets = () => setFacets({ size: new Set(), source: new Set() });

  const sizeBand = (g) => {
    const n = (g.members || []).length;
    if (n === 0) return 'empty';
    if (n < 5) return 'small';
    if (n < 20) return 'medium';
    return 'large';
  };
  const sources = useMemo(() => Array.from(new Set(groups.map(g => g.source || 'manual'))).sort(), [groups]);

  const FACET_GROUPS = useMemo(() => ([
    { key: 'size', label: 'Size', options: [
      { key: 'empty',  label: 'Empty (0)' },
      { key: 'small',  label: 'Small (1-4)' },
      { key: 'medium', label: 'Medium (5-19)' },
      { key: 'large',  label: 'Large (20+)' },
    ]},
    ...(sources.length > 1 ? [{ key: 'source', label: 'Source', options: sources.map(s => ({ key: s, label: s })) }] : []),
  ]), [sources]);

  const filtered = useMemo(() => groups.filter(g => {
    if (facets.size.size > 0 && !facets.size.has(sizeBand(g))) return false;
    if (facets.source.size > 0 && !facets.source.has(g.source || 'manual')) return false;
    return true;
  }), [groups, facets]);

  /* G175 — Pagination over the filtered list. Reset to page 1 whenever the
   * filtered length changes so the user doesn't land on an empty page. */
  const pg = usePaginationState(10);
  const visibleRows = pg.paginate(filtered);
  useEffect(() => { pg.setPage(0); }, [filtered.length]);

  const facetCounts = useMemo(() => {
    const counts = {};
    for (const fg of FACET_GROUPS) {
      counts[fg.key] = {};
      for (const opt of fg.options) {
        counts[fg.key][opt.key] = groups.filter(g => {
          for (const og of FACET_GROUPS) {
            if (og.key === fg.key) continue;
            const sel = facets[og.key]; if (!sel || sel.size === 0) continue;
            const v = og.key === 'size' ? sizeBand(g) : (g.source || 'manual');
            if (!sel.has(v)) return false;
          }
          const v = fg.key === 'size' ? sizeBand(g) : (g.source || 'manual');
          return v === opt.key;
        }).length;
      }
    }
    return counts;
  }, [groups, facets, FACET_GROUPS]);

  const facetGroupsWithCounts = useMemo(() => FACET_GROUPS.map(g => ({
    ...g, options: g.options.map(o => ({ ...o, count: facetCounts[g.key]?.[o.key] || 0, checked: facets[g.key]?.has(o.key) || false })),
  })), [facetCounts, facets, FACET_GROUPS]);

  const activeFilterChips = useMemo(() => {
    const chips = [];
    for (const g of FACET_GROUPS) for (const optKey of facets[g.key] || []) {
      const opt = g.options.find(o => o.key === optKey);
      chips.push({ groupKey: g.key, optionKey: optKey, label: `${g.label}: ${opt?.label || optKey}` });
    }
    return chips;
  }, [facets, FACET_GROUPS]);

  const [selected, setSelected] = useState(null);
  const [slideoutTab, setSlideoutTab] = useState('overview');
  const openSlideout = (row) => { setSelected(row); setSlideoutTab('overview'); setMemberToAdd(''); };
  const closeSlideout = () => setSelected(null);
  // The open slideout holds a snapshot; after a member add/remove reloads the
  // groups list, re-derive the live row by id so the Members tab reflects the
  // change without a sync-effect loop. Open/close still keys on `selected`.
  const liveSelected = useMemo(
    () => (selected ? (groups.find((g) => g.id === selected.id) || selected) : null),
    [groups, selected],
  );
  // Users not already in the live group — the Add-member picker's options.
  const nonMembers = useMemo(() => {
    const memberIds = new Set((liveSelected?.members || []).map((m) => m.userId || m.id || m));
    return (users || []).filter((u) => u && u.id && !memberIds.has(u.id));
  }, [liveSelected, users]);

  const kpis = useMemo(() => ({
    total: groups.length,
    withMembers: groups.filter(g => (g.members || []).length > 0).length,
    totalMembers: groups.reduce((s, g) => s + (g.members || []).length, 0),
    empty: groups.filter(g => (g.members || []).length === 0).length,
  }), [groups]);

  return (
    <div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-5">
        <MetricCard label="Total groups"      value={kpis.total} tone="action" />
        <MetricCard label="With members"      value={kpis.withMembers} tone="success" />
        <MetricCard label="Total memberships" value={kpis.totalMembers} tone="cyan" />
        <MetricCard label="Empty groups"      value={kpis.empty} tone={kpis.empty > 0 ? 'warning' : 'muted'} />
      </div>

      {activeFilterChips.length > 0 && (
        <div className="bg-v2-surface border border-v2-border-subtle rounded-v2-md shadow-v2-sm px-4 py-2.5 mb-4 flex items-center gap-2 flex-wrap">
          <span className="text-[11px] uppercase tracking-wider text-v2-ink-muted font-semibold">Filters</span>
          {activeFilterChips.map((c) => (
            <button key={`${c.groupKey}:${c.optionKey}`} onClick={() => toggleFacet(c.groupKey, c.optionKey, false)}
              className="inline-flex items-center gap-1 text-[11.5px] font-medium bg-v2-action-soft text-v2-action-strong rounded-v2-pill px-2.5 py-0.5 hover:bg-v2-action hover:text-white">
              {c.label} ×
            </button>
          ))}
          <button onClick={clearFacets} className="ml-auto text-[11.5px] text-v2-ink-muted hover:text-v2-ink">Clear all</button>
        </div>
      )}

      <div className="flex items-center justify-end gap-2 mb-4">
        <Button variant="ghost" size="sm" iconLeft={<RefreshCw size={13} />} onClick={() => loadGroups && loadGroups()}>Refresh</Button>
        <Button variant="primary" size="sm" iconLeft={<Plus size={13} />} onClick={openAddGroup}>Add Group</Button>
      </div>

      <div className="grid gap-5 grid-cols-1 lg:grid-cols-[240px_1fr]">
        <FacetRail groups={facetGroupsWithCounts} onToggle={toggleFacet} ariaLabel="Group filters" />

        {filtered.length === 0 ? (
          <EmptyState
            icon={<Shield size={20} />}
            title={groups.length === 0 ? 'No groups yet' : 'No groups match filters'}
            description={groups.length === 0
              ? 'Groups bundle users for shared access to agents and workflows.'
              : 'Adjust filters or clear them to see more.'}
            primaryAction={groups.length === 0
              ? <Button variant="primary" iconLeft={<Plus />} onClick={openAddGroup}>Add Group</Button>
              : <Button variant="primary" onClick={clearFacets}>Clear filters</Button>}
          />
        ) : (
          <Table
            ariaLabel="Groups"
            columns={[
              { key: 'name', header: 'Group', sortable: true, render: (g) => (
                <div>
                  <div className="font-semibold text-v2-ink">{g.name}</div>
                  <div className="text-[10.5px] text-v2-ink-muted">{g.description?.slice(0, 80) || 'No description'}</div>
                </div>
              )},
              { key: 'members', header: 'Members', render: (g) => (
                <Pill tone={(g.members || []).length > 0 ? 'cyan' : 'muted'} size="sm">{(g.members || []).length}</Pill>
              )},
              { key: 'source', header: 'Source', render: (g) => (
                <Pill tone="muted" size="sm">{g.source || 'manual'}</Pill>
              )},
              { key: 'created', header: 'Created', align: 'right', render: (g) => (
                <span className="font-mono-v2 text-[11px] text-v2-ink-muted">{g.createdAt ? new Date(g.createdAt).toLocaleDateString() : '—'}</span>
              )},
              { key: 'actions', header: '', align: 'right', render: (g) => (
                <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                  {/* G362 — Was a /team?legacy=1 bounce. The V2 group
                       modal (openEditGroup) handles name + description
                       + member list inline. */}
                  <Button variant="ghost" size="sm" onClick={() => openEditGroup(g)} title="Edit group">
                    <Edit2 size={13} />
                  </Button>
                  <Button variant="ghost" size="sm" className="!text-v2-danger" onClick={() => setDeleteTarget(g)} title="Delete group" aria-label={`Delete group ${g.name}`}>
                    <Trash2 size={13} />
                  </Button>
                </div>
              )},
            ]}
            rows={visibleRows}
            rowKey={(g) => g.id}
            onRowClick={openSlideout}
          />
        )}
      </div>

      {filtered.length > 0 && (
        <div className="grid gap-5 mt-3 grid-cols-1 lg:grid-cols-[240px_1fr]">
          <div />
          <Pagination
            totalItems={filtered.length}
            page={pg.page}
            pageSize={pg.pageSize}
            onPageChange={pg.setPage}
            onPageSizeChange={pg.setPageSize}
            noun="groups"
          />
        </div>
      )}

      <Slideout
        isOpen={!!selected}
        onClose={closeSlideout}
        ariaLabel={selected ? `${selected.name} detail` : 'Group detail'}
        width={520}
        tabs={[
          { id: 'overview', label: 'Overview' },
          { id: 'members',  label: 'Members', badge: (selected?.members || []).length || undefined },
          { id: 'access',   label: 'Granted access' },
          { id: 'chat',     label: 'Chat access' },
        ]}
        activeTabId={slideoutTab}
        onTabChange={setSlideoutTab}
        header={selected && (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Pill tone="action" size="sm">{((liveSelected || selected).members || []).length} member{((liveSelected || selected).members || []).length === 1 ? '' : 's'}</Pill>
              <Pill tone="muted" size="sm">{selected.source || 'manual'}</Pill>
            </div>
            <h2 className="text-[18px] font-bold tracking-tight m-0">{selected.name}</h2>
            <p className="text-[12px] text-v2-ink-secondary">{selected.description || 'No description'}</p>
            <div className="flex items-center gap-2 mt-3">
              <Button variant="primary" size="sm" iconLeft={<Edit2 size={13} />} onClick={() => openEditGroup(selected)}>
                Manage in v1
              </Button>
            </div>
          </div>
        )}
      >
        {selected && slideoutTab === 'overview' && (
          <Card padding="sm">
            <Card.Header title="Group overview" />
            <Card.Body>
              <dl className="grid grid-cols-2 gap-3 text-[12.5px]">
                <div className="col-span-2"><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Description</dt><dd className="text-v2-ink mt-0.5">{selected.description || '—'}</dd></div>
                <div><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Created</dt><dd className="text-v2-ink mt-0.5">{selected.createdAt ? new Date(selected.createdAt).toLocaleString() : '—'}</dd></div>
                <div><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Source</dt><dd className="text-v2-ink mt-0.5">{selected.source || 'manual'}</dd></div>
              </dl>
            </Card.Body>
          </Card>
        )}
        {selected && slideoutTab === 'members' && liveSelected && (
          <Card padding="sm">
            <Card.Header title={`Members · ${(liveSelected.members || []).length}`} />
            <Card.Body>
              {/* Add member — picks a tenant user not already in the group and
                  POSTs the membership. Reuses platform.addGroupMember. */}
              <div className="flex items-end gap-2 mb-3">
                <div className="flex-1">
                  <label className="block text-[10.5px] uppercase tracking-wider font-semibold text-v2-ink-muted mb-1">Add member</label>
                  <select
                    value={memberToAdd}
                    onChange={(e) => setMemberToAdd(e.target.value)}
                    disabled={memberBusy || nonMembers.length === 0}
                    className="v2-focus-ring w-full bg-v2-surface text-v2-ink border border-v2-border rounded-v2-md px-3 py-1.5 text-[12.5px]"
                  >
                    <option value="">{nonMembers.length === 0 ? 'All tenant users are members' : 'Select a user…'}</option>
                    {nonMembers.map((u) => (
                      <option key={u.id} value={u.id}>{fullName(u)}{u.email ? ` — ${u.email}` : ''}</option>
                    ))}
                  </select>
                </div>
                <Button variant="primary" size="sm" iconLeft={<UserPlus size={13} />}
                  disabled={!memberToAdd || memberBusy}
                  onClick={() => addMember(liveSelected, memberToAdd)}>
                  Add
                </Button>
              </div>

              {(liveSelected.members || []).length === 0 ? (
                <p className="text-v2-ink-muted text-[12.5px]">No members yet.</p>
              ) : (
                <ul className="space-y-2 text-[12.5px]">
                  {(liveSelected.members || []).map((m, i) => {
                    const uid = m.userId || m.id || m;
                    const u = users.find(u => u.id === uid);
                    return (
                      <li key={uid || i} className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-v2-md flex items-center justify-center text-[10px] font-bold bg-v2-cyan-soft text-v2-cyan">
                          {u ? initials(u) : '??'}
                        </div>
                        <div className="min-w-0">
                          <div className="text-v2-ink font-semibold truncate">{u ? fullName(u) : uid}</div>
                          {u?.email && <div className="text-v2-ink-muted text-[11px] font-mono-v2 truncate">{u.email}</div>}
                        </div>
                        <Button variant="ghost" size="xs" className="!text-v2-danger ml-auto" iconLeft={<Trash2 size={12} />}
                          disabled={memberBusy}
                          aria-label={`Remove ${u ? fullName(u) : uid} from group`}
                          title="Remove from group"
                          onClick={() => removeMember(liveSelected, uid)}>
                          Remove
                        </Button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </Card.Body>
          </Card>
        )}
        {selected && slideoutTab === 'access' && (
          <Card padding="sm">
            <Card.Header title="Granted access" actions={
              /* G362 — Was a /team?legacy=1 bounce. Now points to the
                  V2 Assignments tab — that's where group-level grants
                  are managed in V2.
                  DEF-FE-USER-SLIDEOUT-SETTAB-NOT-DEFINED-001 — same
                  setTab scope-leak as the user detail slideout. This
                  occurrence is inside GroupsInventory; plumb the parent
                  setTab through onNavigateToTab so the button works
                  instead of throwing ReferenceError on click. */
              <Button variant="ghost" size="sm" onClick={() => { closeSlideout(); onNavigateToTab && onNavigateToTab('assignments'); }}>
                Open Assignments →
              </Button>
            } />
            <Card.Body>
              <p className="text-v2-ink-muted text-[12.5px]">
                Agent and workflow assignments granted to this group. Use the Assignments tab to add/remove.
              </p>
            </Card.Body>
          </Card>
        )}
        {selected && slideoutTab === 'chat' && (
          <GroupChatAccessPanel group={selected} agents={agents} />
        )}
      </Slideout>

      {/* G378 — Add / Edit Group: converted from Modal → Slideout for V2
          consistency. The same side-panel pattern used for record drill-ins
          now handles the create/edit flow so every Team page action lives
          on the right rail. */}
      <Slideout
        isOpen={addGroupOpen}
        onClose={groupSaving ? undefined : () => setAddGroupOpen(false)}
        ariaLabel={editGroup ? `Edit group ${editGroup.name}` : 'Add group'}
        width={520}
        header={
          <div>
            <div className="flex items-center gap-2 mb-2">
              <UsersIcon size={14} className="text-v2-action-strong" />
              <Pill tone="muted" size="sm">{editGroup ? 'edit' : 'new'}</Pill>
            </div>
            <h2 className="text-[16px] font-bold m-0">{editGroup ? `Edit group — ${editGroup.name}` : 'Add group'}</h2>
            <p className="text-[12px] text-v2-ink-secondary mt-1">Bundle users for shared agent / workflow access.</p>
          </div>
        }
        footer={(
          <div className="flex items-center justify-end gap-2">
            <Button variant="ghost" onClick={() => setAddGroupOpen(false)} disabled={groupSaving}>Cancel</Button>
            <Button variant="primary" onClick={submitGroup} loading={groupSaving}>
              {groupSaving ? 'Saving…' : (editGroup ? 'Save changes' : 'Create group')}
            </Button>
          </div>
        )}
      >
        <div className="space-y-3 text-[12.5px]">
          <div>
            <label className="block text-[10.5px] uppercase tracking-wider font-semibold text-v2-ink-muted mb-1">Name</label>
            <input
              type="text" autoFocus
              value={groupForm.name}
              onChange={(e) => setGroupForm({ ...groupForm, name: e.target.value })}
              placeholder="e.g. Engineering · SecOps · Platform Ops"
              disabled={groupSaving}
              className="v2-focus-ring w-full bg-v2-surface text-v2-ink border border-v2-border rounded-v2-md px-3 py-1.5 text-[12.5px]"
            />
          </div>
          <div>
            <label className="block text-[10.5px] uppercase tracking-wider font-semibold text-v2-ink-muted mb-1">Description</label>
            <textarea
              rows={3}
              value={groupForm.description}
              onChange={(e) => setGroupForm({ ...groupForm, description: e.target.value })}
              placeholder="Short description of who this group represents and what they can do."
              disabled={groupSaving}
              className="v2-focus-ring w-full bg-v2-surface text-v2-ink border border-v2-border rounded-v2-md px-3 py-1.5 text-[12.5px] resize-none"
            />
          </div>
          {groupError && (
            <div className="rounded-v2-md border border-v2-danger-border bg-v2-danger-soft px-3 py-2 text-[12px] text-v2-danger">
              {groupError}
            </div>
          )}
          <div className="text-[11px] text-v2-ink-muted">
            After creating the group, open it and use the <strong>Members</strong> tab to add or remove members.
          </div>
        </div>
      </Slideout>

      {/* Delete group — platform Modal confirm (never window.confirm). The
          backend DELETE /api/v1/team/groups/{id} is irreversible, so the
          action is gated behind an explicit danger confirm. */}
      <Modal
        isOpen={!!deleteTarget}
        onClose={deleting ? undefined : () => setDeleteTarget(null)}
        title="Delete group"
        ariaLabel="Delete group confirmation"
        size="sm"
        footer={(
          <div className="flex items-center justify-end gap-2">
            <Button variant="ghost" onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
            <Button variant="danger" onClick={confirmDeleteGroup} loading={deleting}>
              {deleting ? 'Deleting…' : 'Delete group'}
            </Button>
          </div>
        )}
      >
        <div className="text-[12.5px] text-v2-ink space-y-2">
          <p>
            Delete the group <strong>{deleteTarget?.name}</strong>? This removes the group and its{' '}
            {(deleteTarget?.members || []).length} membership{(deleteTarget?.members || []).length === 1 ? '' : 's'}.
          </p>
          <p className="text-v2-ink-muted text-[11.5px]">
            Members themselves are not deleted, and any access granted directly to those users is unaffected —
            only access they inherited through this group is removed. This cannot be undone.
          </p>
        </div>
      </Modal>
    </div>
  );
}

/* ── Connector Groups (G134 inventory) ─────────────────────────────── */

function ConnectorGroupsInventory() {
  const navigate = useNavigate();
  const platform = usePlatform();
  const { addToast } = useToast();
  const { confirm, confirmModal } = useConfirm();
  const {
    connectorGroups = [], loadConnectorGroups,
    connectors = [], loadConnectors,
    createConnectorGroup, updateConnectorGroup, deleteConnectorGroup,
    addConnectorGroupMember, removeConnectorGroupMember,
  } = platform;

  useEffect(() => {
    if (typeof loadConnectorGroups === 'function') loadConnectorGroups();
    if (typeof loadConnectors === 'function') loadConnectors();
  }, [loadConnectorGroups, loadConnectors]);

  const [selected, setSelected] = useState(null);
  const [slideoutTab, setSlideoutTab] = useState('overview');
  const openSlideout = (row) => { setSelected(row); setSlideoutTab('overview'); };
  const closeSlideout = () => setSelected(null);

  /* G359 — V2-native CRUD state. `editing` is the group being edited
   * (null = creating new). Replaces the previous legacy=1 bounces. */
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const openCreate = () => { setEditing(null); setModalOpen(true); };
  const openEdit   = (cg) => { setEditing(cg); setModalOpen(true); };
  const closeModal = () => { setModalOpen(false); setEditing(null); };

  const handleSave = async (form) => {
    try {
      if (editing?.id) {
        await updateConnectorGroup?.(editing.id, form);
        addToast({ tone: 'success', title: 'Connector group updated' });
      } else {
        await createConnectorGroup?.(form);
        addToast({ tone: 'success', title: 'Connector group created' });
      }
      closeModal();
      if (typeof loadConnectorGroups === 'function') loadConnectorGroups();
    } catch (err) {
      addToast({ tone: 'danger', title: 'Save failed', description: err?.message });
      throw err;
    }
  };

  const handleDelete = async (cg) => {
    if (!(await confirm({ title: 'Delete connector group', message: `Delete connector group "${cg.name}"? This does not delete the underlying connectors.`, confirmLabel: 'Delete' }))) return;
    try {
      await deleteConnectorGroup?.(cg.id);
      addToast({ tone: 'success', title: 'Connector group deleted' });
      if (selected?.id === cg.id) closeSlideout();
      if (typeof loadConnectorGroups === 'function') loadConnectorGroups();
    } catch (err) {
      addToast({ tone: 'danger', title: 'Delete failed', description: err?.message });
    }
  };

  /* G359 — Member adder + remover. Refresh the selected group state
   * by re-reading from the freshly-loaded connectorGroups list so the
   * slideout's "connectors" tab updates without re-opening. */
  const refreshSelectedFromList = useCallback((groups) => {
    if (!selected?.id) return;
    const next = groups.find(g => g.id === selected.id);
    if (next) setSelected(next);
  }, [selected?.id]);
  useEffect(() => { refreshSelectedFromList(connectorGroups); }, [connectorGroups, refreshSelectedFromList]);

  const handleAddMember = async (groupId, connectorId) => {
    try {
      await addConnectorGroupMember?.(groupId, connectorId);
      addToast({ tone: 'success', title: 'Connector added' });
      if (typeof loadConnectorGroups === 'function') loadConnectorGroups();
    } catch (err) {
      addToast({ tone: 'danger', title: 'Add failed', description: err?.message });
    }
  };
  const handleRemoveMember = async (groupId, connectorId) => {
    if (!(await confirm({ title: 'Remove connector', message: 'Remove this connector from the group?', confirmLabel: 'Remove' }))) return;
    try {
      await removeConnectorGroupMember?.(groupId, connectorId);
      addToast({ tone: 'success', title: 'Connector removed' });
      if (typeof loadConnectorGroups === 'function') loadConnectorGroups();
    } catch (err) {
      addToast({ tone: 'danger', title: 'Remove failed', description: err?.message });
    }
  };

  const kpis = useMemo(() => ({
    total: connectorGroups.length,
    withConnectors: connectorGroups.filter(cg => (cg.connectors || []).length > 0).length,
    totalConnectors: connectors.length,
  }), [connectorGroups, connectors]);

  return (
    <div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-5">
        <MetricCard label="Connector groups"     value={kpis.total} tone="trust" />
        <MetricCard label="With connectors"      value={kpis.withConnectors} tone="success" />
        <MetricCard label="Connectors available" value={kpis.totalConnectors} tone="cyan" />
      </div>

      <div className="flex items-center justify-end gap-2 mb-4">
        <Button variant="ghost" size="sm" iconLeft={<RefreshCw size={13} />} onClick={() => loadConnectorGroups && loadConnectorGroups()}>Refresh</Button>
        <Button variant="primary" size="sm" iconLeft={<Plus size={13} />} onClick={openCreate}>Add Connector Group</Button>
      </div>

      {connectorGroups.length === 0 ? (
        <EmptyState
          icon={<Layers size={20} />}
          title="No connector groups yet"
          description="Connector groups bundle related connectors (e.g. all SIEM connectors) for shared assignment to agents."
          primaryAction={<Button variant="primary" iconLeft={<Plus />} onClick={openCreate}>Add Connector Group</Button>}
        />
      ) : (
        <Table
          ariaLabel="Connector groups"
          columns={[
            { key: 'name', header: 'Group', sortable: true, render: (cg) => (
              <div>
                <div className="font-semibold text-v2-ink">{cg.name}</div>
                <div className="text-[10.5px] text-v2-ink-muted">{cg.description || 'No description'}</div>
              </div>
            )},
            { key: 'connectorCount', header: 'Connectors', render: (cg) => (
              <Pill tone={(cg.connectors || []).length > 0 ? 'cyan' : 'muted'} size="sm">{(cg.connectors || []).length}</Pill>
            )},
            { key: 'created', header: 'Created', align: 'right', render: (cg) => (
              <span className="font-mono-v2 text-[11px] text-v2-ink-muted">{cg.createdAt ? new Date(cg.createdAt).toLocaleDateString() : '—'}</span>
            )},
            { key: 'actions', header: '', align: 'right', render: (cg) => (
              <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                <Button variant="ghost" size="sm" onClick={() => openEdit(cg)} title="Edit"><Edit2 size={13} /></Button>
                <Button variant="ghost" size="sm" className="!text-v2-danger hover:!bg-v2-danger-soft"
                  onClick={() => handleDelete(cg)} title="Delete"><Trash2 size={13} /></Button>
              </div>
            )},
          ]}
          rows={connectorGroups}
          rowKey={(cg) => cg.id}
          onRowClick={openSlideout}
        />
      )}

      <Slideout
        isOpen={!!selected}
        onClose={closeSlideout}
        ariaLabel={selected ? `${selected.name} detail` : 'Connector group detail'}
        width={520}
        tabs={[
          { id: 'overview',   label: 'Overview' },
          { id: 'connectors', label: 'Connectors', badge: (selected?.connectors || []).length || undefined },
          { id: 'usage',      label: 'Assigned agents' },
        ]}
        activeTabId={slideoutTab}
        onTabChange={setSlideoutTab}
        header={selected && (
          <div>
            <h2 className="text-[18px] font-bold tracking-tight m-0">{selected.name}</h2>
            <p className="text-[12px] text-v2-ink-secondary">{selected.description || 'No description'}</p>
            <div className="flex items-center gap-2 mt-3">
              <Button variant="primary" size="sm" iconLeft={<Edit2 size={13} />} onClick={() => { closeSlideout(); openEdit(selected); }}>
                Edit
              </Button>
              <Button variant="ghost" size="sm" className="!text-v2-danger" iconLeft={<Trash2 size={13} />} onClick={() => handleDelete(selected)}>
                Delete
              </Button>
            </div>
          </div>
        )}
      >
        {selected && slideoutTab === 'overview' && (
          <Card padding="sm">
            <Card.Header title="Connector group overview" />
            <Card.Body>
              <dl className="grid grid-cols-2 gap-3 text-[12.5px]">
                <div className="col-span-2"><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Description</dt><dd className="text-v2-ink mt-0.5">{selected.description || '—'}</dd></div>
                <div><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Connectors</dt><dd className="text-v2-ink mt-0.5">{(selected.connectors || []).length}</dd></div>
                <div><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Created</dt><dd className="text-v2-ink mt-0.5">{selected.createdAt ? new Date(selected.createdAt).toLocaleString() : '—'}</dd></div>
              </dl>
            </Card.Body>
          </Card>
        )}
        {selected && slideoutTab === 'connectors' && (
          <Card padding="sm">
            <Card.Header title="Connectors in group" />
            <Card.Body>
              {/* G359 — V2-native member CRUD: add via dropdown, remove
                  inline per row. Replaces the previous read-only view
                  that pushed everything to V1. */}
              <ConnectorGroupMemberEditor
                group={selected}
                connectors={connectors}
                onAdd={(connectorId) => handleAddMember(selected.id, connectorId)}
                onRemove={(connectorId) => handleRemoveMember(selected.id, connectorId)}
              />
            </Card.Body>
          </Card>
        )}
        {selected && slideoutTab === 'usage' && (
          <Card padding="sm">
            <Card.Header title="Assigned agents" />
            <Card.Body>
              <p className="text-v2-ink-muted text-[12.5px]">Use the Assignments tab to see which agents have access to this connector group.</p>
            </Card.Body>
          </Card>
        )}
      </Slideout>

      {/* G359 — V2-native Create/Edit modal (was a legacy=1 bounce). */}
      <ConnectorGroupModal
        isOpen={modalOpen}
        onClose={closeModal}
        group={editing}
        onSave={handleSave}
      />
      {confirmModal}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════
 *  ConnectorGroupModal (G359)
 *
 *  Minimal V2 modal — name + description. Save calls back into the
 *  parent's handleSave which dispatches create vs update. */
function ConnectorGroupModal({ isOpen, onClose, group, onSave }) {
  const isEdit = !!group?.id;
  const [form, setForm] = useState({ name: '', description: '' });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!isOpen) return;
    setForm({ name: group?.name || '', description: group?.description || '' });
  }, [isOpen, group]);
  const canSave = form.name.trim().length > 0 && !saving;
  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({ name: form.name.trim(), description: form.description.trim() || null });
    } catch (_) { /* parent toasts */ }
    finally { setSaving(false); }
  };
  return (
    /* G378 — Converted from Modal → Slideout to align with V2's right-rail
       create/edit pattern. Same form, sticky Cancel/Save footer. */
    <Slideout
      isOpen={isOpen}
      onClose={onClose}
      width={520}
      ariaLabel={isEdit ? `Edit connector group ${group?.name}` : 'Create connector group'}
      header={
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Layers size={14} className="text-v2-action-strong" />
            <Pill tone="muted" size="sm">{isEdit ? 'edit' : 'new'}</Pill>
          </div>
          <h2 className="text-[16px] font-bold m-0">{isEdit ? `Edit — ${group?.name}` : 'Create connector group'}</h2>
          <p className="text-[12px] text-v2-ink-secondary mt-1">Bundle connectors so they can be assigned together.</p>
        </div>
      }
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="primary" onClick={handleSave} disabled={!canSave} iconLeft={saving ? null : <CheckCircle2 size={13} />}>
            {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create group'}
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        <label className="block">
          <span className="block text-[10.5px] uppercase tracking-wider text-v2-ink-muted font-semibold mb-1">Name *</span>
          <input type="text" value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
            placeholder="e.g. SIEM connectors"
            className="v2-focus-ring w-full text-[12.5px] border border-v2-border rounded-v2-md px-2.5 py-1.5 bg-v2-surface" />
        </label>
        <label className="block">
          <span className="block text-[10.5px] uppercase tracking-wider text-v2-ink-muted font-semibold mb-1">Description</span>
          <textarea value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
            rows={3}
            placeholder="What connectors this group bundles and why."
            className="v2-focus-ring w-full text-[12.5px] border border-v2-border rounded-v2-md px-2.5 py-1.5 bg-v2-surface" />
        </label>
      </div>
    </Slideout>
  );
}

function ConnectorGroupMemberEditor({ group, connectors, onAdd, onRemove }) {
  const memberIds = useMemo(() => new Set(
    (group?.connectors || []).map(c => c.connectorId || c.id || c)
  ), [group]);
  const nonMembers = useMemo(() => connectors.filter(c => !memberIds.has(c.id)), [connectors, memberIds]);
  const [pick, setPick] = useState('');
  /* G374 — Free-text filter so the non-member <select> stays usable past
   * a few dozen connectors. With thousands of connectors the dropdown was
   * effectively unnavigable. */
  const [memberFilter, setMemberFilter] = useState('');
  useEffect(() => { setPick(''); setMemberFilter(''); }, [group?.id]);

  const memberRows = useMemo(() => (group?.connectors || []).map((c) => {
    const connectorId = c.connectorId || c.id || c;
    const conn = connectors.find(cn => cn.id === connectorId);
    return { connectorId, name: conn?.name || connectorId, type: conn?.type };
  }), [group, connectors]);

  /* G374 — Free-text filter applied to the rendered roster. */
  const filteredMembers = useMemo(() => {
    const q = memberFilter.trim().toLowerCase();
    if (!q) return memberRows;
    return memberRows.filter(m =>
      (m.name || '').toLowerCase().includes(q) ||
      (m.type || '').toLowerCase().includes(q)
    );
  }, [memberRows, memberFilter]);

  /* G374 — Pagination so large groups don't render hundreds of rows
   * inside a slideout. Reset to page 0 whenever the group or the filter
   * changes so the user always lands on a populated page. */
  const pg = usePaginationState(10);
  useEffect(() => { pg.setPage(0); }, [group?.id, memberFilter]); // eslint-disable-line

  return (
    <div className="space-y-3">
      {/* Add member dropdown */}
      <div className="flex items-center gap-2">
        <select value={pick} onChange={(e) => setPick(e.target.value)}
          className="flex-1 text-[12px] border border-v2-border-subtle rounded-v2-sm px-2 py-1.5 bg-v2-surface focus:border-v2-action focus:outline-none">
          <option value="">Pick a connector to add…</option>
          {nonMembers.map(c => (
            <option key={c.id} value={c.id}>{c.name}{c.type ? ` · ${c.type}` : ''}</option>
          ))}
          {nonMembers.length === 0 && <option value="" disabled>All connectors already in group</option>}
        </select>
        <Button variant="primary" size="sm" iconLeft={<Plus size={13} />}
          disabled={!pick}
          onClick={() => { onAdd(pick); setPick(''); }}>
          Add
        </Button>
      </div>

      {/* Member list */}
      {memberRows.length === 0 ? (
        <p className="text-[12px] text-v2-ink-muted italic m-0">No connectors in this group yet. Use the picker above to add some.</p>
      ) : (
        <>
          {/* G374 — Filter input only when the roster is non-trivial. */}
          {memberRows.length > 5 && (
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={memberFilter}
                onChange={(e) => setMemberFilter(e.target.value)}
                placeholder="Filter members by name or type…"
                aria-label="Filter group members"
                className="v2-focus-ring flex-1 text-[12px] border border-v2-border-subtle rounded-v2-sm px-2 py-1.5 bg-v2-surface"
              />
              {memberFilter && (
                <span className="text-[10.5px] text-v2-ink-muted">
                  {filteredMembers.length} / {memberRows.length}
                </span>
              )}
            </div>
          )}
          <ul className="space-y-1.5 text-[12.5px] m-0 p-0 list-none">
            {pg.paginate(filteredMembers).map((m) => (
              <li key={m.connectorId} className="flex items-center justify-between rounded-v2-sm border border-v2-border-subtle bg-v2-surface px-2.5 py-1.5">
                <div>
                  <strong className="text-v2-ink">{m.name}</strong>
                  {m.type && <span className="text-v2-ink-muted text-[11px]"> · {m.type}</span>}
                </div>
                <Button variant="ghost" size="xs" className="!text-v2-danger hover:!bg-v2-danger-soft"
                  onClick={() => onRemove(m.connectorId)} title="Remove">
                  <X size={12} />
                </Button>
              </li>
            ))}
          </ul>
          {/* G374 — Pagination footer. Only renders when there's more than
           * one page to avoid visual noise for small groups. */}
          {filteredMembers.length > pg.pageSize && (
            <div className="pt-1">
              <Pagination
                totalItems={filteredMembers.length}
                page={pg.page}
                pageSize={pg.pageSize}
                onPageChange={pg.setPage}
                onPageSizeChange={pg.setPageSize}
                noun="members"
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ── Assignments tab (G134) — 3 sub-tabs (Agents / Workflows / Infra) ── */

function AssignmentsInventory() {
  const navigate = useNavigate();
  const platform = usePlatform();
  const { addToast } = useToast();
  const { confirm, confirmModal } = useConfirm();
  const {
    agents = [], loadAgents,
    assignments = [], loadAssignments,
    workflows = [], loadWorkflows,
    deployments = [], loadDeployments,
    connectorAssignments = [], loadConnectorAssignments,
    connectors = [], loadConnectors,
    connectorGroups = [], loadConnectorGroups,
    users = [], loadUsers,
    groups: groupsList = [], loadGroups,
    /* G369 — Org/Tenant context for the Infrastructure tree view. */
    orgs = [], loadOrganizations,
    tenants = [], loadTenants,
  } = platform;

  const [subTab, setSubTab] = useState('agents');

  /* G369 — Infrastructure sub-tab toggle: tree (hierarchy) vs table (flat). */
  const [viewMode, setViewMode] = useState('tree');

  /* G363 — Free-text filter + pagination state per sub-tab. */
  const [searchAgents, setSearchAgents] = useState('');
  const [searchWorkflows, setSearchWorkflows] = useState('');
  const [searchDeployments, setSearchDeployments] = useState('');
  const pgAgents = usePaginationState(10);
  const pgWorkflows = usePaginationState(10);
  const pgInfra = usePaginationState(10);

  /* G370 — Workflow assignments source.
   *
   * `platform.workflows` reads from /api/v1/workflows (the workflow
   * REGISTRY — definitions). For tenants whose workflows are managed
   * by the runtime (Conductor OSS / Conductor / stress harness) the registry
   * table is empty even though Workflow Intelligence lists 78+ flows
   * with real run history. From the operator's point of view that's
   * "broken" because they see workflows everywhere else but can't
   * assign access to them.
   *
   * Fix: also pull from /api/v1/workflow-intelligence/workflows (the
   * run-history join — which IS the canonical "workflows you've
   * actually used" list). Merge by id with the registry, dedupe, and
   * present the union. Operators can now manage access on any
   * workflow they've ever executed, regardless of whether it was
   * pre-registered. */
  const [intelligenceWorkflows, setIntelligenceWorkflows] = useState([]);
  useEffect(() => {
    let cancelled = false;
    const hours = 720; // 30 days — wide enough to capture anything recent
    const fromInstant = new Date(Date.now() - hours * 3600 * 1000).toISOString();
    api.getWorkflowIntelligenceWorkflows?.({ from: fromInstant })
      .then((res) => {
        if (cancelled) return;
        const items = Array.isArray(res?.data?.items) ? res.data.items
                    : Array.isArray(res?.items) ? res.items
                    : Array.isArray(res?.data) ? res.data
                    : Array.isArray(res) ? res : [];
        setIntelligenceWorkflows(items);
      })
      .catch(() => { /* non-fatal — registry source still works */ });
    return () => { cancelled = true; };
  }, []);

  const mergedWorkflows = useMemo(() => {
    const byId = new Map();
    /* Registry first — wins on field collisions because it's the
     * canonical authoring source. */
    for (const w of (workflows || [])) if (w?.id) byId.set(w.id, w);
    for (const w of intelligenceWorkflows) {
      if (!w?.id) continue;
      if (byId.has(w.id)) {
        const reg = byId.get(w.id);
        byId.set(w.id, { ...w, ...reg, totalRunCount: w.totalRunCount ?? w.runCount });
      } else {
        byId.set(w.id, { ...w, status: w.status || (w.totalRunCount > 0 ? 'active' : 'unknown') });
      }
    }
    return Array.from(byId.values()).sort((a, b) => (b.totalRunCount ?? b.runCount ?? 0) - (a.totalRunCount ?? a.runCount ?? 0));
  }, [workflows, intelligenceWorkflows]);

  const filteredAgents = useMemo(() => {
    const q = searchAgents.trim().toLowerCase();
    if (!q) return agents;
    return agents.filter(a => (a.name || '').toLowerCase().includes(q)
      || (a.id || '').toLowerCase().includes(q)
      || (a.archetype || a.type || '').toLowerCase().includes(q));
  }, [agents, searchAgents]);
  const filteredWorkflows = useMemo(() => {
    const q = searchWorkflows.trim().toLowerCase();
    if (!q) return mergedWorkflows;
    return mergedWorkflows.filter(w => (w.name || w.id || '').toLowerCase().includes(q));
  }, [mergedWorkflows, searchWorkflows]);
  const filteredDeployments = useMemo(() => {
    const q = searchDeployments.trim().toLowerCase();
    if (!q) return deployments;
    return deployments.filter(d => (d.agentName || d.agentId || '').toLowerCase().includes(q)
      || (d.tenantId || '').toLowerCase().includes(q)
      || (d.environment || '').toLowerCase().includes(q));
  }, [deployments, searchDeployments]);

  useEffect(() => { pgAgents.setPage(0); }, [searchAgents]); // eslint-disable-line
  useEffect(() => { pgWorkflows.setPage(0); }, [searchWorkflows]); // eslint-disable-line
  useEffect(() => { pgInfra.setPage(0); }, [searchDeployments]); // eslint-disable-line

  /* G359 / G379 — V2-native drill-in slideout. Click an agent row → opens
   * a slideout listing current user/group + connector assignments, with
   * inline Add accordion panels and Remove buttons. The Add forms now
   * live inside the slideout itself (no more modal-on-slideout). */
  const [selectedAgentId, setSelectedAgentId] = useState(null);
  const openAgentDetail = (a) => { setSelectedAgentId(a.id); };
  const closeAgentDetail = () => { setSelectedAgentId(null); };
  const selectedAgent = useMemo(() => agents.find(a => a.id === selectedAgentId), [agents, selectedAgentId]);

  /* V332 — The agent's skills, so a grant can be scoped to one (and an approver can be named
   * for a human-gated skill). The agents LIST doesn't carry skills, so fetch them when the
   * slideout opens. `humanGated` mirrors the backend's canonical predicate
   * (Skill.requiresHumanApproval): autonomy HUMAN_CONTROLLED, or inferenceMode HUMAN_APPROVAL,
   * or an approval guardrail — kept in lockstep via constants/autonomy.js so the UI can never
   * mark a skill as un-gated that the runtime will in fact refuse to run. */
  const [agentSkills, setAgentSkills] = useState([]);
  useEffect(() => {
    let cancelled = false;
    if (!selectedAgentId) { setAgentSkills([]); return undefined; }
    (async () => {
      try {
        const res = await api.getAgentSkills?.(selectedAgentId);
        const rows = res?.ok ? res.data : res;
        const list = Array.isArray(rows) ? rows : (rows?.skills || []);
        if (!cancelled) {
          setAgentSkills(list.map(s => ({
            id: s.id,
            name: s.name || s.id,
            humanGated: requiresHumanApproval(s),
          })));
        }
      } catch (_) {
        if (!cancelled) setAgentSkills([]);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedAgentId]);

  /* G380 — Deep-link consumer for `?agent=<id>` (the contract G365 left
   * unwired). Callers like AgentDetailPageV2's "Manage in Teams" button
   * arrive here to manage ONE specific agent's assignments. Land on the
   * Agents sub-tab and open that agent's drill-in slideout directly,
   * instead of dropping the operator on an unfiltered list to hunt for the
   * agent by hand. The slideout is guarded by `if (!agent) return null`, so
   * if the id is still loading it opens once `agents` resolves, and if the
   * id is out of scope it simply stays closed (graceful — no crash). Runs
   * once on mount: closing the slideout will NOT reopen it, and a later
   * manual sub-tab switch is unaffected. Agent-type agnostic — `agents`
   * comes from /api/v1/agents, which returns every agent type in scope
   * (Lets / AutoLets / ExteLets / SysLets). */
  useEffect(() => {
    let agentParam = null;
    try { agentParam = new URLSearchParams(window.location.search).get('agent'); }
    catch { /* SSR / malformed search — ignore */ }
    if (!agentParam) return;
    setSubTab('agents');
    setSelectedAgentId(agentParam);
    // eslint-disable-next-line
  }, []);

  /* G368 / G379 — Workflow assignments drill-in. Same inline-panel pattern. */
  const [selectedWorkflowId, setSelectedWorkflowId] = useState(null);
  const [workflowAssignments, setWorkflowAssignments] = useState([]);
  const [loadingWorkflowAssignments, setLoadingWorkflowAssignments] = useState(false);
  const openWorkflowDetail = (w) => { setSelectedWorkflowId(w.id); };
  const closeWorkflowDetail = () => { setSelectedWorkflowId(null); };
  /* G370 — Look up the selected workflow in the MERGED list (registry
   * + executed) — earlier this used `workflows.find` which silently
   * returned undefined when the operator clicked an intelligence-only
   * workflow, leaving the slideout permanently closed. */
  const selectedWorkflow = useMemo(() =>
    mergedWorkflows.find(w => w.id === selectedWorkflowId), [mergedWorkflows, selectedWorkflowId]);

  const reloadWorkflowAssignments = useCallback(async () => {
    if (!selectedWorkflowId) { setWorkflowAssignments([]); return; }
    setLoadingWorkflowAssignments(true);
    try {
      const res = await api.getWorkflowAssignments?.(selectedWorkflowId);
      const items = Array.isArray(res?.data?.items) ? res.data.items
                  : Array.isArray(res?.items) ? res.items
                  : Array.isArray(res?.data) ? res.data
                  : Array.isArray(res) ? res : [];
      setWorkflowAssignments(items);
    } catch (err) {
      addToast({ tone: 'danger', title: 'Failed to load workflow assignments', description: err?.message });
    } finally { setLoadingWorkflowAssignments(false); }
    // eslint-disable-next-line
  }, [selectedWorkflowId]);
  useEffect(() => { reloadWorkflowAssignments(); }, [reloadWorkflowAssignments]);

  const handleAddWorkflowAssignment = async (payload) => {
    if (!selectedWorkflowId) return;
    try {
      await api.createWorkflowAssignment?.(selectedWorkflowId, payload);
      addToast({ tone: 'success', title: 'Workflow access granted' });
      /* G379 — Inline panel inside the slideout closes itself on success. */
      reloadWorkflowAssignments();
    } catch (err) {
      addToast({ tone: 'danger', title: 'Add failed', description: err?.message });
    }
  };
  const handleRemoveWorkflowAssignment = async (assignmentId) => {
    if (!(await confirm({ title: 'Remove workflow access', message: 'Remove this workflow access grant?', confirmLabel: 'Remove' }))) return;
    try {
      await api.removeWorkflowAssignment?.(assignmentId);
      addToast({ tone: 'success', title: 'Access revoked' });
      reloadWorkflowAssignments();
    } catch (err) {
      addToast({ tone: 'danger', title: 'Remove failed', description: err?.message });
    }
  };

  useEffect(() => {
    if (typeof loadAgents === 'function') loadAgents();
    if (typeof loadWorkflows === 'function') loadWorkflows();
    if (typeof loadDeployments === 'function') loadDeployments();
    if (typeof loadConnectors === 'function') loadConnectors();
    if (typeof loadConnectorGroups === 'function') loadConnectorGroups();
    if (typeof loadUsers === 'function') loadUsers();
    if (typeof loadGroups === 'function') loadGroups();
    /* G369 — Hydrate the org/tenant axes used by InfrastructureTreeView. */
    if (typeof loadOrganizations === 'function') loadOrganizations();
    if (typeof loadTenants === 'function') loadTenants();
  }, [loadAgents, loadWorkflows, loadDeployments, loadConnectors, loadConnectorGroups, loadUsers, loadGroups, loadOrganizations, loadTenants]);

  /* Task #116 — per-agent fan-out for the assignments inventory. The
   * platform-context loaders take an agentId (api.getAssignments and
   * api.getAgentConnectorAssignments short-circuit to empty when the
   * arg is missing, via api.js's G373 UUID guard). The previous
   * mount-effect called both with NO arg, so the global assignments
   * arrays stayed empty and the per-row count pills all read 0. The
   * correct shape is to chain off the agents list — once agents
   * loads, fan out one assignment + connector-assignment fetch per
   * agent. PlatformContext.loadAssignments merges per-agent results
   * into the global array, so this populates correctly. Cost is
   * O(N) requests; acceptable for tenant agent counts in the dozens. */
  useEffect(() => {
    if (!Array.isArray(agents) || agents.length === 0) return;
    if (typeof loadAssignments !== 'function') return;
    if (typeof loadConnectorAssignments !== 'function') return;
    agents.forEach((a) => {
      if (a && a.id) {
        loadAssignments(a.id);
        loadConnectorAssignments(a.id);
      }
    });
  }, [agents, loadAssignments, loadConnectorAssignments]);

  const handleAddAssignment = async (payload) => {
    if (!selectedAgentId) return;
    try {
      await api.createAssignment?.(selectedAgentId, payload);
      addToast({ tone: 'success', title: 'Assignment added' });
      /* G379 — Inline panel closes itself; no modal state to clear here.
       * Task #116 — pass agentId so the per-agent fetch actually fires. */
      if (typeof loadAssignments === 'function') loadAssignments(selectedAgentId);
    } catch (err) {
      addToast({ tone: 'danger', title: 'Add failed', description: err?.message });
    }
  };
  const handleRemoveAssignment = async (assignmentId) => {
    if (!selectedAgentId) return;
    if (!(await confirm({ title: 'Remove assignment', message: 'Remove this user/group assignment?', confirmLabel: 'Remove' }))) return;
    try {
      await api.removeAssignment?.(selectedAgentId, assignmentId);
      addToast({ tone: 'success', title: 'Assignment removed' });
      /* Task #116 — pass agentId so the per-agent fetch actually fires. */
      if (typeof loadAssignments === 'function') loadAssignments(selectedAgentId);
    } catch (err) {
      addToast({ tone: 'danger', title: 'Remove failed', description: err?.message });
    }
  };
  const handleAddConnectorAssignment = async (payload) => {
    if (!selectedAgentId) return;
    try {
      await api.createAgentConnectorAssignment?.(selectedAgentId, payload);
      addToast({ tone: 'success', title: 'Connector access granted' });
      /* G379 — Inline panel closes itself; no modal state to clear here.
       * Task #116 — pass agentId so the per-agent fetch actually fires. */
      if (typeof loadConnectorAssignments === 'function') loadConnectorAssignments(selectedAgentId);
    } catch (err) {
      addToast({ tone: 'danger', title: 'Add failed', description: err?.message });
    }
  };
  const handleRemoveConnectorAssignment = async (assignmentId) => {
    if (!selectedAgentId) return;
    if (!(await confirm({ title: 'Revoke connector access', message: 'Revoke this connector access?', confirmLabel: 'Revoke' }))) return;
    try {
      /* Reuses the same DELETE pattern; backend distinguishes via path. */
      await api.removeAssignment?.(selectedAgentId, assignmentId);
      addToast({ tone: 'success', title: 'Connector access revoked' });
      /* Task #116 — pass agentId so the per-agent fetch actually fires. */
      if (typeof loadConnectorAssignments === 'function') loadConnectorAssignments(selectedAgentId);
    } catch (err) {
      addToast({ tone: 'danger', title: 'Remove failed', description: err?.message });
    }
  };

  const kpis = useMemo(() => ({
    agents:     agents.length,
    assigned:   assignments.length,
    /* G370 — KPI counts the merged set so the figure matches
     * what's actually visible in the workflows table below. */
    workflows:  mergedWorkflows.length,
    deployments: deployments.length,
  }), [agents, assignments, mergedWorkflows, deployments]);

  return (
    <div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-5">
        <MetricCard label="Agents"             value={kpis.agents} tone="cyan" />
        <MetricCard label="User assignments"   value={kpis.assigned} tone="action" />
        <MetricCard label="Workflows"          value={kpis.workflows} tone="trust" />
        <MetricCard label="Deployments"        value={kpis.deployments} tone="success" />
      </div>

      <TabNav
        tabs={[
          { id: 'agents',     label: 'Agent assignments' },
          { id: 'workflows',  label: 'Workflow assignments' },
          { id: 'infra',      label: 'Infrastructure / deployments' },
        ]}
        activeId={subTab}
        onChange={setSubTab}
        ariaLabel="Assignment scope"
      />
      <div className="my-4" />

      {subTab === 'agents' && (
        agents.length === 0 ? (
          <EmptyState
            icon={<UsersIcon size={20} />}
            title="No agents yet"
            description="Once agents exist, their user/group assignments will appear here. Each row shows an agent with its current access grants."
          />
        ) : (
          <>
            {/* G363 — Free-text search + per-table pagination. */}
            <div className="mb-3 flex items-center justify-end gap-2">
              <input type="text" value={searchAgents} onChange={(e) => setSearchAgents(e.target.value)}
                placeholder="Search agents by name / id / archetype…"
                aria-label="Search agents"
                className="v2-focus-ring w-[280px] bg-v2-surface text-v2-ink border border-v2-border rounded-v2-md px-3 py-1.5 text-[12.5px]" />
            </div>
            <Table
              ariaLabel="Agent assignments"
              columns={[
                { key: 'name', header: 'Agent', sortable: true, render: (a) => (
                  <div>
                    <div className="font-semibold text-v2-ink">{a.name}</div>
                    <div className="text-[10.5px] text-v2-ink-muted">{a.archetype || a.type || '—'}</div>
                  </div>
                )},
                { key: 'users', header: 'User/Group access', render: (a) => {
                  const count = assignments.filter(x => x.agentId === a.id).length;
                  return <Pill tone={count > 0 ? 'success' : 'muted'} size="sm">{count}</Pill>;
                }},
                { key: 'connectors', header: 'Connector access', render: (a) => {
                  const count = connectorAssignments.filter(x => x.agentId === a.id).length;
                  return <Pill tone={count > 0 ? 'cyan' : 'muted'} size="sm">{count}</Pill>;
                }},
                { key: 'actions', header: '', align: 'right', render: (a) => (
                  <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                    <Button variant="ghost" size="sm" onClick={() => navigate(`/agents/${a.id}`)}>Open agent</Button>
                    <Button variant="primary" size="sm" onClick={() => openAgentDetail(a)}>Manage access</Button>
                  </div>
                )},
              ]}
              rows={pgAgents.paginate(filteredAgents)}
              rowKey={(a) => a.id}
              onRowClick={openAgentDetail}
            />
            <div className="mt-3">
              <Pagination totalItems={filteredAgents.length} page={pgAgents.page} pageSize={pgAgents.pageSize}
                onPageChange={pgAgents.setPage} onPageSizeChange={pgAgents.setPageSize}
                noun="agents" />
            </div>
          </>
        )
      )}

      {subTab === 'workflows' && (
        mergedWorkflows.length === 0 ? (
          <EmptyState
            icon={<Activity size={20} />}
            title="No workflows yet"
            description="Once workflows exist (registered or executed), their user/group/agent access grants will appear here."
          />
        ) : (
          <>
            <div className="mb-3 flex items-center justify-end gap-2">
              <input type="text" value={searchWorkflows} onChange={(e) => setSearchWorkflows(e.target.value)}
                placeholder="Search workflows…"
                aria-label="Search workflows"
                className="v2-focus-ring w-[280px] bg-v2-surface text-v2-ink border border-v2-border rounded-v2-md px-3 py-1.5 text-[12.5px]" />
            </div>
            <Table
              ariaLabel="Workflow assignments"
              columns={[
                { key: 'name', header: 'Workflow', render: (w) => (
                  <div>
                    <div className="font-semibold text-v2-ink">{w.name || w.id}</div>
                    <div className="text-[10.5px] text-v2-ink-muted font-mono-v2">{w.id}</div>
                  </div>
                )},
                { key: 'status', header: 'Status', render: (w) => (
                  <Pill tone={w.status === 'active' ? 'success' : 'muted'} size="sm" dot>{w.status || 'unknown'}</Pill>
                )},
                { key: 'actions', header: '', align: 'right', render: (w) => (
                  <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                    {/* G368 — Was nav to /workflows/:id only. Now opens
                         V2 slideout with inline user/group/agent CRUD. */}
                    <Button variant="ghost" size="sm" onClick={() => navigate(`/workflows/${w.id}`)}>Open workflow</Button>
                    <Button variant="primary" size="sm" onClick={() => openWorkflowDetail(w)}>Manage access</Button>
                  </div>
                )},
              ]}
              rows={pgWorkflows.paginate(filteredWorkflows)}
              rowKey={(w) => w.id}
              onRowClick={openWorkflowDetail}
            />
            <div className="mt-3">
              <Pagination totalItems={filteredWorkflows.length} page={pgWorkflows.page} pageSize={pgWorkflows.pageSize}
                onPageChange={pgWorkflows.setPage} onPageSizeChange={pgWorkflows.setPageSize}
                noun="workflows" />
            </div>
          </>
        )
      )}

      {subTab === 'infra' && (
        deployments.length === 0 ? (
          <EmptyState
            icon={<Server size={20} />}
            title="No deployments yet"
            description="Deployments map agents to specific tenant environments. Add one in the Deployments workspace to start tracking infrastructure."
            primaryAction={<Button variant="primary" iconLeft={<Plus />} onClick={() => navigate('/deployments')}>Open Deployments</Button>}
          />
        ) : (
          /* G369 — Org → Tenant → Deployment tree view. The flat
             table did not communicate the hierarchy enterprise
             operators reason about (which Org owns which Tenant
             owns which Deployment). Replaced with an expandable
             tree with per-node KPIs and deep-links into the
             Deployments workspace for CRUD. */
          <>
            <div className="mb-3 flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Button variant={viewMode === 'tree' ? 'primary' : 'ghost'} size="sm" onClick={() => setViewMode('tree')}>
                  <Layers size={13} className="inline mr-1" /> Tree
                </Button>
                <Button variant={viewMode === 'table' ? 'primary' : 'ghost'} size="sm" onClick={() => setViewMode('table')}>
                  <Activity size={13} className="inline mr-1" /> Table
                </Button>
              </div>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-v2-ink-muted pointer-events-none" />
                  <input type="text" value={searchDeployments} onChange={(e) => setSearchDeployments(e.target.value)}
                    placeholder="Search by agent / tenant / env…"
                    aria-label="Search deployments"
                    className="v2-focus-ring w-full sm:w-[320px] bg-v2-surface text-v2-ink border border-v2-border rounded-v2-md pl-8 pr-3 py-1.5 text-[12.5px]" />
                </div>
                <Button variant="ghost" size="sm" iconLeft={<ExternalLink size={13} />} onClick={() => navigate('/deployments')}>
                  Open Deployments
                </Button>
                <Button variant="primary" size="sm" iconLeft={<Plus size={13} />} onClick={() => navigate('/deployments?action=new')}>
                  New deployment
                </Button>
              </div>
            </div>

            {viewMode === 'tree' ? (
              <InfrastructureTreeView
                orgs={orgs}
                tenants={tenants}
                deployments={filteredDeployments}
                onOpenDeployment={(d) => navigate(`/deployments?id=${d.id}`)}
                onOpenOrg={(o) => navigate(`/deployments?orgId=${o.id}`)}
                onOpenTenant={(t) => navigate(`/deployments?orgId=${t.orgId || ''}&tenantId=${t.id}`)}
              />
            ) : (
              <>
                <Table
                  ariaLabel="Infrastructure deployments"
                  columns={[
                    { key: 'agent', header: 'Agent', render: (d) => (
                      <div className="font-semibold text-v2-ink">{d.agentName || d.agentId || '—'}</div>
                    )},
                    { key: 'org', header: 'Org', render: (d) => {
                      const o = orgs.find(x => x.id === d.orgId);
                      return <span className="text-[11.5px]">{o?.name || d.orgId || '—'}</span>;
                    }},
                    { key: 'tenant', header: 'Tenant', render: (d) => {
                      const t = tenants.find(x => x.id === d.tenantId);
                      return <span className="text-[11.5px]">{t?.name || d.tenantId || '—'}</span>;
                    }},
                    { key: 'env', header: 'Env', render: (d) => (
                      <Pill tone={(d.environment || '') === 'production' ? 'danger' : 'cyan'} size="sm">{d.environment || '—'}</Pill>
                    )},
                    { key: 'status', header: 'Status', render: (d) => (
                      <Pill tone={d.status === 'healthy' ? 'success' : d.status === 'critical' ? 'danger' : 'warning'} size="sm" dot>{d.status || 'unknown'}</Pill>
                    )},
                    { key: 'actions', header: '', align: 'right', render: (d) => (
                      <Button variant="ghost" size="sm" onClick={() => navigate(`/deployments?id=${d.id}`)}>Open</Button>
                    )},
                  ]}
                  rows={pgInfra.paginate(filteredDeployments)}
                  rowKey={(d) => d.id}
                />
                <div className="mt-3">
                  <Pagination totalItems={filteredDeployments.length} page={pgInfra.page} pageSize={pgInfra.pageSize}
                    onPageChange={pgInfra.setPage} onPageSizeChange={pgInfra.setPageSize}
                    noun="deployments" />
                </div>
              </>
            )}
          </>
        )
      )}

      {/* G359 / G379 — Agent assignments drill-in slideout. The Add buttons
          inside it now reveal inline accordion panels (no modal-on-slideout).
          Parent only owns the actual add/remove handlers + data; the slideout
          manages its own panel-open state. */}
      <AgentAssignmentsSlideout
        agent={selectedAgent}
        onClose={closeAgentDetail}
        userAssignments={assignments.filter(a => a.agentId === selectedAgentId)}
        connectorAssignments={connectorAssignments.filter(a => a.agentId === selectedAgentId)}
        users={users}
        groups={groupsList}
        /* V332 — the agent's skills, so a grant can be scoped to one and an approver named for a
         * human-gated skill. Fetched in THIS component (AssignmentsInventory); the slideout is a
         * separate component, so it must arrive as a prop — React does not share scope across the
         * boundary, and a stray reference only blows up at render time. */
        agentSkills={agentSkills}
        connectors={connectors}
        connectorGroups={connectorGroups}
        onAddUserGroup={handleAddAssignment}
        onAddConnector={handleAddConnectorAssignment}
        onRemoveUserGroup={handleRemoveAssignment}
        onRemoveConnector={handleRemoveConnectorAssignment}
      />

      {/* G368 / G379 — Workflow assignments drill-in slideout. Same inline-
          panel pattern as the agent slideout. */}
      <WorkflowAssignmentsSlideout
        workflow={selectedWorkflow}
        onClose={closeWorkflowDetail}
        assignments={workflowAssignments}
        loading={loadingWorkflowAssignments}
        users={users}
        groups={groupsList}
        agents={agents}
        onAddUserGroup={handleAddWorkflowAssignment}
        onAddAgent={handleAddWorkflowAssignment}
        onRemove={handleRemoveWorkflowAssignment}
      />
      {confirmModal}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════
 *  InfrastructureTreeView (G369)
 *
 *  Expandable Org → Tenant → Deployment tree for the Assignments →
 *  Infrastructure sub-tab. Replaces a flat table that lost the
 *  hierarchy enterprise operators reason about. Each node shows a
 *  count + roll-up status; deployment leaves show env + status pills
 *  and deep-link into the Deployments workspace for CRUD.
 *
 *  Notes
 *  - Orgs / tenants come from PlatformContext (already hydrated).
 *  - Empty branches (a tenant with no deployments under the current
 *    filter) are hidden so the tree never lies about scope.
 *  - "Orphan" deployments (no orgId / tenantId mapping) are bucketed
 *    under "Unassigned" so they're never silently lost.
 *  - Expand-all / collapse-all controls so an operator triaging
 *    incidents can fan everything open in one click.
 * */
function InfrastructureTreeView({ orgs, tenants, deployments, onOpenDeployment, onOpenOrg, onOpenTenant }) {
  /* Bucket deployments by orgId → tenantId. */
  const tree = useMemo(() => {
    const byOrg = new Map();
    const ensureOrg = (orgId) => {
      if (!byOrg.has(orgId)) byOrg.set(orgId, new Map());
      return byOrg.get(orgId);
    };
    for (const d of deployments) {
      const orgKey = d.orgId || '__unassigned__';
      const tenantKey = d.tenantId || '__unassigned__';
      const orgBucket = ensureOrg(orgKey);
      if (!orgBucket.has(tenantKey)) orgBucket.set(tenantKey, []);
      orgBucket.get(tenantKey).push(d);
    }
    /* Materialise as ordered arrays for stable rendering. */
    const rows = [];
    for (const [orgId, tenantMap] of byOrg.entries()) {
      const org = orgs.find((o) => o.id === orgId) || { id: orgId, name: orgId === '__unassigned__' ? 'Unassigned org' : orgId };
      const tenantRows = [];
      for (const [tenantId, deps] of tenantMap.entries()) {
        const tenant = tenants.find((t) => t.id === tenantId) || { id: tenantId, name: tenantId === '__unassigned__' ? 'Unassigned tenant' : tenantId };
        tenantRows.push({ tenant, deployments: deps });
      }
      tenantRows.sort((a, b) => (a.tenant.name || '').localeCompare(b.tenant.name || ''));
      const flat = tenantRows.flatMap((t) => t.deployments);
      rows.push({ org, tenants: tenantRows, deployments: flat });
    }
    rows.sort((a, b) => (a.org.name || '').localeCompare(b.org.name || ''));
    return rows;
  }, [orgs, tenants, deployments]);

  /* Expanded state — keyed by orgId / `${orgId}/${tenantId}`. Default
     to first org expanded so the view isn't a wall of collapsed
     rows. */
  const initialExpanded = useMemo(() => {
    const s = new Set();
    if (tree.length > 0) {
      s.add(tree[0].org.id);
      for (const t of tree[0].tenants) s.add(`${tree[0].org.id}/${t.tenant.id}`);
    }
    return s;
  }, [tree]);
  const [expanded, setExpanded] = useState(initialExpanded);
  useEffect(() => { setExpanded(initialExpanded); }, [initialExpanded]);

  const toggle = (key) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };
  const expandAll = () => {
    const s = new Set();
    for (const row of tree) {
      s.add(row.org.id);
      for (const t of row.tenants) s.add(`${row.org.id}/${t.tenant.id}`);
    }
    setExpanded(s);
  };
  const collapseAll = () => setExpanded(new Set());

  /* Roll up status: any "critical" → critical, else any non-healthy → warning, else healthy. */
  const rollupStatus = (deps) => {
    if (!deps || deps.length === 0) return 'unknown';
    if (deps.some((d) => d.status === 'critical' || d.status === 'failed')) return 'critical';
    if (deps.some((d) => d.status && d.status !== 'healthy' && d.status !== 'active')) return 'warning';
    if (deps.every((d) => d.status === 'healthy' || d.status === 'active')) return 'healthy';
    return 'unknown';
  };
  const toneFor = (status) =>
    status === 'healthy' ? 'success'
    : status === 'critical' ? 'danger'
    : status === 'warning' ? 'warning'
    : 'muted';

  if (tree.length === 0) {
    return (
      <Card>
        <Card.Body>
          <p className="text-[12.5px] text-v2-ink-muted italic m-0">No deployments match your filter.</p>
        </Card.Body>
      </Card>
    );
  }

  return (
    <div>
      <div className="mb-2 flex items-center justify-end gap-2 text-[11.5px]">
        <button type="button" onClick={expandAll}
          className="v2-focus-ring text-v2-action-strong hover:underline">Expand all</button>
        <span className="text-v2-ink-muted">·</span>
        <button type="button" onClick={collapseAll}
          className="v2-focus-ring text-v2-action-strong hover:underline">Collapse all</button>
      </div>

      <ul className="m-0 p-0 list-none space-y-3">
        {tree.map((row) => {
          const orgKey = row.org.id;
          const orgOpen = expanded.has(orgKey);
          const orgStatus = rollupStatus(row.deployments);
          return (
            <li key={orgKey} className="rounded-v2-md border border-v2-border bg-v2-surface overflow-hidden">
              {/* Org header */}
              <div className="flex items-center gap-2 px-3 py-2.5 bg-v2-muted/40 hover:bg-v2-muted/60 transition-colors">
                <button
                  type="button"
                  onClick={() => toggle(orgKey)}
                  className="v2-focus-ring rounded-v2-sm p-0.5 -ml-0.5"
                  aria-expanded={orgOpen}
                  aria-label={orgOpen ? 'Collapse organization' : 'Expand organization'}
                >
                  {orgOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </button>
                <Building2 size={14} className="text-v2-trust-strong" />
                <div className="flex-1 min-w-0">
                  <div className="text-[13.5px] font-bold text-v2-ink truncate">{row.org.name}</div>
                  {row.org.domain && (
                    <div className="text-[10.5px] text-v2-ink-muted truncate">{row.org.domain}</div>
                  )}
                </div>
                <Pill tone="muted" size="sm">{row.tenants.length} tenant{row.tenants.length === 1 ? '' : 's'}</Pill>
                <Pill tone="cyan" size="sm">{row.deployments.length} deploy{row.deployments.length === 1 ? '' : 's'}</Pill>
                <Pill tone={toneFor(orgStatus)} size="sm" dot>{orgStatus}</Pill>
                <Button variant="ghost" size="xs" iconLeft={<ExternalLink size={11} />}
                  onClick={() => onOpenOrg?.(row.org)} title="Open in Deployments">
                  Open
                </Button>
              </div>

              {orgOpen && (
                <ul className="m-0 p-0 list-none border-t border-v2-border-subtle divide-y divide-v2-border-subtle">
                  {row.tenants.map(({ tenant, deployments: tenantDeps }) => {
                    const tenantKey = `${row.org.id}/${tenant.id}`;
                    const tenantOpen = expanded.has(tenantKey);
                    const tenantStatus = rollupStatus(tenantDeps);
                    return (
                      <li key={tenantKey}>
                        {/* Tenant header */}
                        <div className="flex items-center gap-2 px-3 py-2 pl-8 hover:bg-v2-muted/30 transition-colors">
                          <button
                            type="button"
                            onClick={() => toggle(tenantKey)}
                            className="v2-focus-ring rounded-v2-sm p-0.5 -ml-0.5"
                            aria-expanded={tenantOpen}
                            aria-label={tenantOpen ? 'Collapse tenant' : 'Expand tenant'}
                          >
                            {tenantOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                          </button>
                          <Layers size={13} className="text-v2-action-strong" />
                          <div className="flex-1 min-w-0">
                            <div className="text-[12.5px] font-semibold text-v2-ink truncate">{tenant.name}</div>
                            <div className="text-[10.5px] text-v2-ink-muted font-mono-v2 truncate">{tenant.id}</div>
                          </div>
                          <Pill tone="cyan" size="sm">{tenantDeps.length} deploy{tenantDeps.length === 1 ? '' : 's'}</Pill>
                          <Pill tone={toneFor(tenantStatus)} size="sm" dot>{tenantStatus}</Pill>
                          <Button variant="ghost" size="xs" iconLeft={<ExternalLink size={11} />}
                            onClick={() => onOpenTenant?.({ ...tenant, orgId: row.org.id })} title="Open tenant in Deployments">
                            Open
                          </Button>
                        </div>

                        {tenantOpen && (
                          <ul className="m-0 p-0 list-none bg-v2-page/30">
                            {tenantDeps.map((d) => (
                              <li key={d.id}
                                  className="flex items-center gap-2 px-3 py-1.5 pl-14 hover:bg-v2-muted/40 transition-colors border-t border-v2-border-subtle/50">
                                <Server size={12} className="text-v2-ink-muted" />
                                <div className="flex-1 min-w-0">
                                  <div className="text-[12px] font-semibold text-v2-ink truncate">{d.agentName || d.agentId || '—'}</div>
                                  <div className="text-[10.5px] text-v2-ink-muted font-mono-v2 truncate">{d.id}</div>
                                </div>
                                <Pill tone={(d.environment || '') === 'production' ? 'danger' : 'cyan'} size="sm">
                                  {d.environment || '—'}
                                </Pill>
                                <Pill
                                  tone={d.status === 'healthy' || d.status === 'active' ? 'success'
                                        : d.status === 'critical' || d.status === 'failed' ? 'danger'
                                        : 'warning'}
                                  size="sm" dot
                                >
                                  {d.status || 'unknown'}
                                </Pill>
                                <Button variant="ghost" size="xs" iconLeft={<ExternalLink size={11} />}
                                  onClick={() => onOpenDeployment?.(d)} title="Open deployment">
                                  Open
                                </Button>
                              </li>
                            ))}
                          </ul>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════
 *  AgentAssignmentsSlideout (G359 / G379)
 *
 *  Per-agent drill-in with two lists: user/group grants + connector
 *  grants. G379 — Add buttons now toggle inline accordion panels INSIDE
 *  the slideout instead of popping a modal. The slideout owns the entire
 *  interaction, keeping with the V2 right-rail pattern. */
function AgentAssignmentsSlideout({
  agent, onClose,
  userAssignments, connectorAssignments,
  users, groups, connectors, connectorGroups,
  /* V332 — [{id, name, humanGated}] for THIS agent; owned by AssignmentsInventory. Defaulted so
   * the slideout renders even if the skills fetch is still in flight or failed. */
  agentSkills = [],
  onAddUserGroup, onAddConnector,
  onRemoveUserGroup, onRemoveConnector,
}) {
  /* G379 — Local state for the two inline add panels. Reset whenever
   * the selected agent changes so the slideout doesn't keep a stale
   * "add form open" state across drill-ins. */
  const [addUserGroupOpen, setAddUserGroupOpen] = useState(false);
  const [addConnectorOpen, setAddConnectorOpen] = useState(false);
  useEffect(() => {
    setAddUserGroupOpen(false);
    setAddConnectorOpen(false);
  }, [agent?.id]);

  if (!agent) return null;
  const labelForActor = (a) => {
    if (a.userId) {
      const u = users.find(x => x.id === a.userId);
      return u ? `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email || u.id : a.userId;
    }
    if (a.groupId) {
      const g = groups.find(x => x.id === a.groupId);
      return g ? g.name || g.id : a.groupId;
    }
    return '—';
  };
  const labelForConn = (a) => {
    if (a.connectorId) {
      const c = connectors.find(x => x.id === a.connectorId);
      return c ? c.name || c.id : a.connectorId;
    }
    if (a.connectorGroupId) {
      const cg = connectorGroups.find(x => x.id === a.connectorGroupId);
      return cg ? `${cg.name} (group)` : a.connectorGroupId;
    }
    return '—';
  };

  /* G379 — Submit wrappers close the inline panel on success so the
   * operator sees the new grant land in the list without another step. */
  const handleSubmitUserGroup = async (payload) => {
    await onAddUserGroup(payload);
    setAddUserGroupOpen(false);
  };
  const handleSubmitConnector = async (payload) => {
    await onAddConnector(payload);
    setAddConnectorOpen(false);
  };

  return (
    <Slideout
      isOpen={!!agent}
      onClose={onClose}
      width={620}
      ariaLabel={`${agent.name} access management`}
      header={
        <div>
          <div className="flex items-center gap-2 mb-2">
            <UsersIcon size={14} className="text-v2-action-strong" />
            <Pill tone="muted" size="sm">{agent.archetype || agent.type || '—'}</Pill>
          </div>
          <h2 className="text-[16px] font-bold m-0">{agent.name}</h2>
          <p className="text-[11px] text-v2-ink-muted font-mono-v2 mt-0.5">{agent.id}</p>
        </div>
      }
    >
      {/* G378 — Wrap both cards in a flex column so their headers stay
          aligned and equal-width regardless of body content. Card row
          items rounded-v2-md to match the rest of the design system. */}
      <div className="flex flex-col gap-3">
        <Card padding="sm">
          <Card.Header
            title={`User / group access · ${userAssignments.length}`}
            actions={
              <Button
                variant={addUserGroupOpen ? 'secondary' : 'primary'}
                size="xs"
                iconLeft={addUserGroupOpen ? <X size={11} /> : <UserPlus size={11} />}
                onClick={() => setAddUserGroupOpen((v) => !v)}>
                {addUserGroupOpen ? 'Close' : 'Add'}
              </Button>
            }
          />
          <Card.Body>
            <InlineAddUserGroupAccessPanel
              isOpen={addUserGroupOpen}
              onCancel={() => setAddUserGroupOpen(false)}
              users={users}
              groups={groups}
              skills={agentSkills}
              onAdd={handleSubmitUserGroup}
            />
            {userAssignments.length === 0 ? (
              <p className="text-[12.5px] text-v2-ink-muted italic m-0">No user/group grants yet.</p>
            ) : (
              <ul className="space-y-1.5 text-[12.5px] m-0 p-0 list-none">
                {userAssignments.map((a) => (
                  <li key={a.id} className="flex items-center justify-between rounded-v2-md border border-v2-border-subtle bg-v2-surface px-2.5 py-1.5">
                    <div>
                      <strong className="text-v2-ink">{labelForActor(a)}</strong>
                      {a.accessLevel && <span className="text-v2-ink-muted text-[11px]"> · {a.accessLevel}</span>}
                      {/* V332 — a grant scoped to a skill must SAY so. An "approve" row with no
                          visible scope is exactly the ambiguity ("approves what?") that made the
                          old routing unauditable. */}
                      {a.skillId && (
                        <span className="text-v2-ink-muted text-[11px]">
                          {' · '}
                          {/* Server-resolved name first (R2); local lookup only as a fallback. */}
                          {a.skillName
                            || (agentSkills.find(s => s.id === a.skillId) || {}).name
                            || 'skill'}
                        </span>
                      )}
                      <span className="text-v2-ink-muted text-[10.5px] ml-2">{a.userId ? 'user' : a.groupId ? 'group' : ''}</span>
                      {!a.skillId && String(a.accessLevel).toLowerCase() === 'approve' && (
                        <span className="text-v2-ink-muted text-[10.5px] ml-1">· all skills</span>
                      )}
                    </div>
                    <Button variant="ghost" size="xs" className="!text-v2-danger hover:!bg-v2-danger-soft"
                      onClick={() => onRemoveUserGroup(a.id)} title="Remove"><X size={12} /></Button>
                  </li>
                ))}
              </ul>
            )}
          </Card.Body>
        </Card>

        <Card padding="sm">
          <Card.Header
            title={`Connector access · ${connectorAssignments.length}`}
            actions={
              <Button
                variant={addConnectorOpen ? 'secondary' : 'primary'}
                size="xs"
                iconLeft={addConnectorOpen ? <X size={11} /> : <Plus size={11} />}
                onClick={() => setAddConnectorOpen((v) => !v)}>
                {addConnectorOpen ? 'Close' : 'Add'}
              </Button>
            }
          />
          <Card.Body>
            <InlineAddConnectorAccessPanel
              isOpen={addConnectorOpen}
              onCancel={() => setAddConnectorOpen(false)}
              connectors={connectors}
              connectorGroups={connectorGroups}
              onAdd={handleSubmitConnector}
            />
            {connectorAssignments.length === 0 ? (
              <p className="text-[12.5px] text-v2-ink-muted italic m-0">No connector access grants yet.</p>
            ) : (
              <ul className="space-y-1.5 text-[12.5px] m-0 p-0 list-none">
                {connectorAssignments.map((a) => (
                  <li key={a.id} className="flex items-center justify-between rounded-v2-md border border-v2-border-subtle bg-v2-surface px-2.5 py-1.5">
                    <div>
                      <strong className="text-v2-ink">{labelForConn(a)}</strong>
                      {a.accessLevel && <span className="text-v2-ink-muted text-[11px]"> · {a.accessLevel}</span>}
                    </div>
                    <Button variant="ghost" size="xs" className="!text-v2-danger hover:!bg-v2-danger-soft"
                      onClick={() => onRemoveConnector(a.id)} title="Remove"><X size={12} /></Button>
                  </li>
                ))}
              </ul>
            )}
          </Card.Body>
        </Card>
      </div>
    </Slideout>
  );
}

/* ════════════════════════════════════════════════════════════════════════
 *  G379 — Inline Add panels (replace modal-on-slideout pattern).
 *
 *  These panels live INSIDE the parent slideout's body, expanding
 *  accordion-style when the operator clicks the "Add" button on a card
 *  header. They share the slideout's right-rail surface instead of
 *  popping a centered modal that breaks the V2 redesign theme.
 *
 *  Each panel:
 *    - Renders nothing when isOpen=false (so it doesn't take vertical space)
 *    - On open: animated reveal of a bordered form section with its own
 *      Cancel / Grant access buttons
 *    - Resets internal form state on every open
 * */
/* V332 — Access-level vocabularies, per resource. These are NOT interchangeable and conflating
 * them is a live security bug, not a cosmetic one:
 *
 *   AGENT   (agent_user_assignments)     read | write | admin | approve
 *   WORKFLOW(workflow_user_assignments)  execute | manage | admin      ← V25 DDL + entity
 *
 * This panel is shared by the Agent and Workflow slideouts. It previously offered read/write/admin
 * to BOTH — so a "Read" grant on a workflow was stored as "read", which WorkflowAccessService
 * mapped through `default: return EXECUTE`, i.e. a read-only grant could RUN the workflow. Adding
 * `approve` to the shared list would have done the same thing (approve → default → EXECUTE),
 * handing a named approver the ability to execute the very workflow they review.
 *
 * The caller now states which vocabulary applies. `approve` + skill scoping are agent-only —
 * a workflow has no skills, and approval duty is not an access tier. */
const AGENT_ACCESS_LEVELS = [
  { value: 'read',    label: 'Read' },
  { value: 'write',   label: 'Write' },
  { value: 'admin',   label: 'Admin' },
  { value: 'approve', label: 'Approve (human gate)' },
];
const WORKFLOW_ACCESS_LEVELS = [
  { value: 'execute', label: 'Execute' },
  { value: 'manage',  label: 'Manage' },
  { value: 'admin',   label: 'Admin' },
];

function InlineAddUserGroupAccessPanel({
  isOpen, onCancel, users, groups, skills = [], onAdd,
  accessLevels = AGENT_ACCESS_LEVELS,
  /* Skill scoping only makes sense for an agent grant. */
  allowSkillScope = true,
}) {
  const [kind, setKind] = useState('user');
  const [actorId, setActorId] = useState('');
  const defaultLevel = accessLevels[0]?.value || 'read';
  const [accessLevel, setAccessLevel] = useState(defaultLevel);
  /* V332 — optional skill scope. '' = agent-wide (the original behaviour). */
  const [skillId, setSkillId] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!isOpen) return;
    setKind('user'); setActorId(''); setAccessLevel(defaultLevel); setSkillId('');
  }, [isOpen, defaultLevel]);
  if (!isOpen) return null;

  /* Skills that refuse to run without a human. These are the ones that NEED an approver named,
   * so we mark them — an operator shouldn't have to remember which skills are gated. */
  const gatedSkillIds = new Set(skills.filter(s => s.humanGated).map(s => s.id));
  const isApprove = accessLevel === 'approve';
  /* An approver grant is meaningless agent-wide-by-accident: "who approves isolateHost" is the
   * question, not "who approves everything". Require the skill so the operator makes the choice
   * explicitly rather than inheriting a silent default. */
  const canSave = !!actorId && !saving && (!isApprove || !!skillId);

  const handle = async () => {
    setSaving(true);
    try {
      const base = { accessLevel, ...(skillId ? { skillId } : {}) };
      const payload = kind === 'user'
        ? { userId: actorId, ...base }
        : { groupId: actorId, ...base };
      await onAdd(payload);
    } catch (_) {} finally { setSaving(false); }
  };
  return (
    <div className="rounded-v2-md border border-v2-action-soft bg-v2-action-soft/40 p-3 mb-2 space-y-3 text-[12.5px]">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <UserPlus size={13} className="text-v2-action-strong" />
          <span className="font-semibold text-v2-ink">Grant user / group access</span>
        </div>
        <button type="button" onClick={onCancel} aria-label="Close add form"
          className="v2-focus-ring rounded-v2-sm p-0.5 text-v2-ink-muted hover:text-v2-ink hover:bg-v2-muted">
          <X size={12} />
        </button>
      </div>
      <div className="flex items-center gap-3">
        <span className="text-[10.5px] uppercase tracking-wider text-v2-ink-muted font-semibold">Type</span>
        <label className="flex items-center gap-1.5"><input type="radio" name="actor-kind" checked={kind === 'user'} onChange={() => { setKind('user'); setActorId(''); }} /> User</label>
        <label className="flex items-center gap-1.5"><input type="radio" name="actor-kind" checked={kind === 'group'} onChange={() => { setKind('group'); setActorId(''); }} /> Group</label>
      </div>
      <label className="block">
        <span className="block text-[10.5px] uppercase tracking-wider text-v2-ink-muted font-semibold mb-1">{kind === 'user' ? 'User' : 'Group'}</span>
        <select value={actorId} onChange={(e) => setActorId(e.target.value)}
          className="v2-focus-ring w-full border border-v2-border rounded-v2-md px-2.5 py-1.5 bg-v2-surface text-[12.5px]">
          <option value="">Pick a {kind}…</option>
          {kind === 'user'
            ? users.map(u => <option key={u.id} value={u.id}>{`${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email || u.id}</option>)
            : groups.map(g => <option key={g.id} value={g.id}>{g.name || g.id}</option>)}
        </select>
      </label>
      {/* V332 — Skill scope. Blank = agent-wide (unchanged default). Picking a skill scopes the
          grant to it; combined with Approve it names the human approver for that skill's gate.
          Agent-only: a workflow has no skills. */}
      {allowSkillScope && (
        <label className="block">
          <span className="block text-[10.5px] uppercase tracking-wider text-v2-ink-muted font-semibold mb-1">
            Skill{isApprove && <span className="text-v2-danger normal-case tracking-normal font-normal"> · required for an approver</span>}
          </span>
          <select value={skillId} onChange={(e) => setSkillId(e.target.value)}
            className="v2-focus-ring w-full border border-v2-border rounded-v2-md px-2.5 py-1.5 bg-v2-surface text-[12.5px]">
            <option value="">All skills (agent-wide)</option>
            {skills.map(s => (
              <option key={s.id} value={s.id}>
                {s.name}{gatedSkillIds.has(s.id) ? ' — human-gated' : ''}
              </option>
            ))}
          </select>
        </label>
      )}
      <label className="block">
        <span className="block text-[10.5px] uppercase tracking-wider text-v2-ink-muted font-semibold mb-1">Access level</span>
        {/* Vocabulary is supplied by the caller — agent and workflow grants are stored in different
            tables with different, NON-interchangeable level sets. See the constants above. */}
        <select value={accessLevel} onChange={(e) => setAccessLevel(e.target.value)}
          className="v2-focus-ring w-full border border-v2-border rounded-v2-md px-2.5 py-1.5 bg-v2-surface text-[12.5px]">
          {accessLevels.map(l => (
            <option key={l.value} value={l.value}>{l.label}</option>
          ))}
        </select>
      </label>
      {isApprove && (
        <p className="text-[11.5px] text-v2-ink-muted leading-relaxed">
          Names the human who authorises this skill when the agent tries to run it. Grants no
          access to operate the agent. An agent with a human-gated skill and no approver cannot
          be published.
        </p>
      )}
      <div className="flex items-center justify-end gap-2 pt-1">
        <Button variant="ghost" size="sm" onClick={onCancel} disabled={saving}>Cancel</Button>
        <Button variant="primary" size="sm" onClick={handle} disabled={!canSave} iconLeft={saving ? null : <CheckCircle2 size={11} />}>
          {saving ? 'Saving…' : 'Grant access'}
        </Button>
      </div>
    </div>
  );
}

function InlineAddConnectorAccessPanel({ isOpen, onCancel, connectors, connectorGroups, onAdd }) {
  const [kind, setKind] = useState('connector');
  const [targetId, setTargetId] = useState('');
  const [accessLevel, setAccessLevel] = useState('read');
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!isOpen) return;
    setKind('connector'); setTargetId(''); setAccessLevel('read');
  }, [isOpen]);
  if (!isOpen) return null;
  const canSave = !!targetId && !saving;
  const handle = async () => {
    setSaving(true);
    try {
      const payload = kind === 'connector'
        ? { connectorId: targetId, accessLevel }
        : { connectorGroupId: targetId, accessLevel };
      await onAdd(payload);
    } catch (_) {} finally { setSaving(false); }
  };
  return (
    <div className="rounded-v2-md border border-v2-action-soft bg-v2-action-soft/40 p-3 mb-2 space-y-3 text-[12.5px]">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Layers size={13} className="text-v2-action-strong" />
          <span className="font-semibold text-v2-ink">Grant connector access</span>
        </div>
        <button type="button" onClick={onCancel} aria-label="Close add form"
          className="v2-focus-ring rounded-v2-sm p-0.5 text-v2-ink-muted hover:text-v2-ink hover:bg-v2-muted">
          <X size={12} />
        </button>
      </div>
      <div className="flex items-center gap-3">
        <span className="text-[10.5px] uppercase tracking-wider text-v2-ink-muted font-semibold">Type</span>
        <label className="flex items-center gap-1.5"><input type="radio" name="conn-kind" checked={kind === 'connector'} onChange={() => { setKind('connector'); setTargetId(''); }} /> Connector</label>
        <label className="flex items-center gap-1.5"><input type="radio" name="conn-kind" checked={kind === 'group'} onChange={() => { setKind('group'); setTargetId(''); }} /> Group</label>
      </div>
      <label className="block">
        <span className="block text-[10.5px] uppercase tracking-wider text-v2-ink-muted font-semibold mb-1">{kind === 'connector' ? 'Connector' : 'Connector group'}</span>
        <select value={targetId} onChange={(e) => setTargetId(e.target.value)}
          className="v2-focus-ring w-full border border-v2-border rounded-v2-md px-2.5 py-1.5 bg-v2-surface text-[12.5px]">
          <option value="">Pick a {kind === 'connector' ? 'connector' : 'connector group'}…</option>
          {kind === 'connector'
            ? connectors.map(c => <option key={c.id} value={c.id}>{c.name}{c.type ? ` · ${c.type}` : ''}</option>)
            : connectorGroups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
        </select>
      </label>
      <label className="block">
        <span className="block text-[10.5px] uppercase tracking-wider text-v2-ink-muted font-semibold mb-1">Access level</span>
        <select value={accessLevel} onChange={(e) => setAccessLevel(e.target.value)}
          className="v2-focus-ring w-full border border-v2-border rounded-v2-md px-2.5 py-1.5 bg-v2-surface text-[12.5px]">
          <option value="read">Read</option>
          <option value="write">Write</option>
          <option value="admin">Admin</option>
        </select>
      </label>
      <div className="flex items-center justify-end gap-2 pt-1">
        <Button variant="ghost" size="sm" onClick={onCancel} disabled={saving}>Cancel</Button>
        <Button variant="primary" size="sm" onClick={handle} disabled={!canSave} iconLeft={saving ? null : <CheckCircle2 size={11} />}>
          {saving ? 'Saving…' : 'Grant access'}
        </Button>
      </div>
    </div>
  );
}

/* ────── G368 — Workflow Assignments slideout + Agent picker modal ─── */

/* WorkflowAssignmentsSlideout — same shape as AgentAssignmentsSlideout
 * but per-workflow. Shows two lists: user/group grants and agent grants.
 * G379 — Add buttons toggle inline accordion panels inside the slideout
 * (replaces the old modal-on-slideout pattern). */
function WorkflowAssignmentsSlideout({
  workflow, onClose,
  assignments, loading,
  users, groups, agents,
  onAddUserGroup, onAddAgent,
  onRemove,
}) {
  /* G379 — Same local-state pattern as AgentAssignmentsSlideout. */
  const [addUserGroupOpen, setAddUserGroupOpen] = useState(false);
  const [addAgentOpen, setAddAgentOpen] = useState(false);
  useEffect(() => {
    setAddUserGroupOpen(false);
    setAddAgentOpen(false);
  }, [workflow?.id]);

  if (!workflow) return null;
  const userGroupAssigns = (assignments || []).filter(a => a.userId || a.groupId);
  const agentAssigns     = (assignments || []).filter(a => a.agentId);
  const labelForActor = (a) => {
    if (a.userId) {
      const u = users.find(x => x.id === a.userId);
      return u ? `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email || u.id : a.userId;
    }
    if (a.groupId) {
      const g = groups.find(x => x.id === a.groupId);
      return g ? g.name || g.id : a.groupId;
    }
    return '—';
  };
  const labelForAgent = (a) => {
    const ag = agents.find(x => x.id === a.agentId);
    return ag ? ag.name || ag.id : a.agentId;
  };

  const handleSubmitUserGroup = async (payload) => {
    await onAddUserGroup(payload);
    setAddUserGroupOpen(false);
  };
  const handleSubmitAgent = async (payload) => {
    await onAddAgent(payload);
    setAddAgentOpen(false);
  };

  return (
    <Slideout
      isOpen={!!workflow}
      onClose={onClose}
      width={620}
      ariaLabel={`${workflow.name || workflow.id} access management`}
      header={
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Activity size={14} className="text-v2-action-strong" />
            <Pill tone={workflow.status === 'active' ? 'success' : 'muted'} size="sm" dot>{workflow.status || 'unknown'}</Pill>
          </div>
          <h2 className="text-[16px] font-bold m-0">{workflow.name || workflow.id}</h2>
          <p className="text-[11px] text-v2-ink-muted font-mono-v2 mt-0.5">{workflow.id}</p>
        </div>
      }
    >
      {loading && (assignments || []).length === 0 ? (
        <p className="text-[12.5px] text-v2-ink-muted italic m-0">Loading assignments…</p>
      ) : (
        <div className="flex flex-col gap-3">
          <Card padding="sm">
            <Card.Header
              title={`User / group access · ${userGroupAssigns.length}`}
              actions={
                <Button
                  variant={addUserGroupOpen ? 'secondary' : 'primary'}
                  size="xs"
                  iconLeft={addUserGroupOpen ? <X size={11} /> : <UserPlus size={11} />}
                  onClick={() => setAddUserGroupOpen((v) => !v)}>
                  {addUserGroupOpen ? 'Close' : 'Add'}
                </Button>
              }
            />
            <Card.Body>
              {/* V332 — WORKFLOW grant. Different table, different vocabulary
                  (execute | manage | admin — V25 DDL). It previously reused the agent's
                  read/write/admin list, so a "Read" grant was stored as "read" and
                  WorkflowAccessService's `default: return EXECUTE` turned it into the right to RUN
                  the workflow. No skill scope: a workflow has no skills. */}
              <InlineAddUserGroupAccessPanel
                isOpen={addUserGroupOpen}
                onCancel={() => setAddUserGroupOpen(false)}
                users={users}
                groups={groups}
                accessLevels={WORKFLOW_ACCESS_LEVELS}
                allowSkillScope={false}
                onAdd={handleSubmitUserGroup}
              />
              {userGroupAssigns.length === 0 ? (
                <p className="text-[12.5px] text-v2-ink-muted italic m-0">No user/group grants yet.</p>
              ) : (
                <ul className="space-y-1.5 text-[12.5px] m-0 p-0 list-none">
                  {userGroupAssigns.map((a) => (
                    <li key={a.id} className="flex items-center justify-between rounded-v2-md border border-v2-border-subtle bg-v2-surface px-2.5 py-1.5">
                      <div>
                        <strong className="text-v2-ink">{labelForActor(a)}</strong>
                        {a.accessLevel && <span className="text-v2-ink-muted text-[11px]"> · {a.accessLevel}</span>}
                        <span className="text-v2-ink-muted text-[10.5px] ml-2">{a.userId ? 'user' : 'group'}</span>
                      </div>
                      <Button variant="ghost" size="xs" className="!text-v2-danger hover:!bg-v2-danger-soft"
                        onClick={() => onRemove(a.id)} title="Remove"><X size={12} /></Button>
                    </li>
                  ))}
                </ul>
              )}
            </Card.Body>
          </Card>

          <Card padding="sm">
            <Card.Header
              title={`Agent access · ${agentAssigns.length}`}
              actions={
                <Button
                  variant={addAgentOpen ? 'secondary' : 'primary'}
                  size="xs"
                  iconLeft={addAgentOpen ? <X size={11} /> : <Plus size={11} />}
                  onClick={() => setAddAgentOpen((v) => !v)}>
                  {addAgentOpen ? 'Close' : 'Add'}
                </Button>
              }
            />
            <Card.Body>
              <InlineAddAgentAccessPanel
                isOpen={addAgentOpen}
                onCancel={() => setAddAgentOpen(false)}
                agents={agents}
                onAdd={handleSubmitAgent}
              />
              {agentAssigns.length === 0 ? (
                <p className="text-[12.5px] text-v2-ink-muted italic m-0">No agent grants yet.</p>
              ) : (
                <ul className="space-y-1.5 text-[12.5px] m-0 p-0 list-none">
                  {agentAssigns.map((a) => (
                    <li key={a.id} className="flex items-center justify-between rounded-v2-md border border-v2-border-subtle bg-v2-surface px-2.5 py-1.5">
                      <div>
                        <strong className="text-v2-ink">{labelForAgent(a)}</strong>
                        {a.accessLevel && <span className="text-v2-ink-muted text-[11px]"> · {a.accessLevel}</span>}
                      </div>
                      <Button variant="ghost" size="xs" className="!text-v2-danger hover:!bg-v2-danger-soft"
                        onClick={() => onRemove(a.id)} title="Remove"><X size={12} /></Button>
                    </li>
                  ))}
                </ul>
              )}
            </Card.Body>
          </Card>
        </div>
      )}
    </Slideout>
  );
}

function InlineAddAgentAccessPanel({ isOpen, onCancel, agents, onAdd }) {
  const [agentId, setAgentId] = useState('');
  const [accessLevel, setAccessLevel] = useState('read');
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (!isOpen) return; setAgentId(''); setAccessLevel('read'); }, [isOpen]);
  if (!isOpen) return null;
  const canSave = !!agentId && !saving;
  const handle = async () => {
    setSaving(true);
    try { await onAdd({ agentId, accessLevel }); }
    catch (_) {} finally { setSaving(false); }
  };
  return (
    <div className="rounded-v2-md border border-v2-action-soft bg-v2-action-soft/40 p-3 mb-2 space-y-3 text-[12.5px]">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Activity size={13} className="text-v2-action-strong" />
          <span className="font-semibold text-v2-ink">Grant agent access</span>
        </div>
        <button type="button" onClick={onCancel} aria-label="Close add form"
          className="v2-focus-ring rounded-v2-sm p-0.5 text-v2-ink-muted hover:text-v2-ink hover:bg-v2-muted">
          <X size={12} />
        </button>
      </div>
      <label className="block">
        <span className="block text-[10.5px] uppercase tracking-wider text-v2-ink-muted font-semibold mb-1">Agent</span>
        <select value={agentId} onChange={(e) => setAgentId(e.target.value)}
          className="v2-focus-ring w-full border border-v2-border rounded-v2-md px-2.5 py-1.5 bg-v2-surface text-[12.5px]">
          <option value="">Pick an agent…</option>
          {agents.map(a => (
            <option key={a.id} value={a.id}>{a.name}{a.archetype ? ` · ${a.archetype}` : ''}</option>
          ))}
        </select>
      </label>
      <label className="block">
        <span className="block text-[10.5px] uppercase tracking-wider text-v2-ink-muted font-semibold mb-1">Access level</span>
        <select value={accessLevel} onChange={(e) => setAccessLevel(e.target.value)}
          className="v2-focus-ring w-full border border-v2-border rounded-v2-md px-2.5 py-1.5 bg-v2-surface text-[12.5px]">
          <option value="read">Read</option>
          <option value="execute">Execute</option>
          <option value="manage">Manage</option>
          <option value="admin">Admin</option>
        </select>
      </label>
      <div className="flex items-center justify-end gap-2 pt-1">
        <Button variant="ghost" size="sm" onClick={onCancel} disabled={saving}>Cancel</Button>
        <Button variant="primary" size="sm" onClick={handle} disabled={!canSave} iconLeft={saving ? null : <CheckCircle2 size={11} />}>
          {saving ? 'Saving…' : 'Grant access'}
        </Button>
      </div>
    </div>
  );
}

/* ── Escalation Chains tab (G134) ─────────────────────────────────── */

/* ════════════════════════════════════════════════════════════════════════
 *  ACCESS REVIEW (G364)
 *
 *  Production-grade rebuild of V1's Access Review tab
 *  (TeamPage.jsx 1850–2273). Compliance-focused enterprises need a
 *  read-only audit of any user's complete access surface: where they
 *  live (Org/Tenant memberships), which agents they reach, which
 *  workflows they can run, which connector groups they touch, and
 *  what their effective permissions are (role-derived).
 *
 *  Layout (matches V1):
 *
 *    ┌─────────────────┬────────────────────────────────────────────┐
 *    │ User picker     │ Selected user — collapsible audit sections │
 *    │ (search +       │                                            │
 *    │  paginated)     │  • Org / Tenant memberships                │
 *    │                 │  • Agent access      (paginated)           │
 *    │                 │  • Workflow access   (paginated)           │
 *    │                 │  • Connector groups                        │
 *    │                 │  • Effective permissions (role-derived)    │
 *    └─────────────────┴────────────────────────────────────────────┘
 *
 *  Access tracing: each row labels the SOURCE of the grant — direct
 *  assignment, group membership (with the group name), agent owner,
 *  or role-derived implicit grant. Operators reviewing access can see
 *  WHY a user has the access they have, not just WHAT.
 *
 *  Effective permissions surface the role → permission mapping the
 *  AuthContext uses, so reviewers don't have to cross-reference role
 *  docs externally.
 * ──────────────────────────────────────────────────────────────────────── */
const ROLE_LEVEL_MAP = {
  // Platform Admin (6) added — was omitted, so a PA resolved to 0 here and the
  // per-user access panel under-stated their access. Mirrors RbacService.ROLE_LEVELS.
  // (Partial map: roles not listed resolve to 0 for this display-only "via" calc.)
  'Platform Admin': 6, 'Super Admin': 5, 'Org Admin': 4, 'Tenant Admin': 3, 'Developer': 2, 'Viewer': 1,
};
const ROLE_PERMS_MAP = {
  'Super Admin':  ['* (all permissions)'],
  'Org Admin':    ['org.*', 'tenant.*', 'user.*', 'group.*', 'agent.crud', 'skill.*', 'eval.*', 'execution.*', 'phase.*', 'assignment.*', 'deployment.*', 'inference.*', 'workflow.*', 'connector.*', 'publish.*'],
  'Tenant Admin': ['user.manage/create/edit/toggle', 'group.*', 'agent.edit/read', 'skill.create/edit/push', 'eval.*', 'execution.*', 'phase.read', 'assignment.*', 'deployment.edit/read', 'inference.*', 'workflow.*', 'connector.*', 'publish.read'],
  'Developer':    ['agent.own/create/edit/read', 'skill.create/edit/push', 'eval.*', 'execution.read', 'phase.read', 'assignment.read', 'deployment.read', 'inference.read', 'workflow.*', 'connector.*', 'publish.read', 'user.read', 'group.read'],
  'Viewer':       ['agent.read', 'eval.read', 'execution.read', 'phase.read', 'assignment.read', 'inference.read', 'workflow.read', 'publish.read', 'user.read', 'group.read'],
};

/**
 * RBAC Phase 1b — Role → Rights matrix editor.
 * Platform Admin can toggle a role's permissions (grouped by domain) and Save
 * (PUT) or Reset to factory default (POST); every other role sees it read-only.
 * Wildcard roles (Super/Platform Admin) are shown but not editable. Backend
 * enforces the same gate — this only decides whether the controls are live.
 */
/**
 * apiFetch returns { ok:false, error } on a failed request INSTEAD of throwing,
 * so a write path that only relies on try/catch will report a backend rejection
 * as success. Pass every mutation response through this so failures surface.
 */
function assertOk(res) {
  if (res && res.ok === false) throw new Error(res.error || res.message || 'Request failed');
  return res;
}

function RoleRightsMatrix({ catalog, vocabulary, missingRoles, isPlatformAdmin, currentRole, addToast, onSaved }) {
  const roles = useMemo(() => (catalog || []).map((r) => r.role), [catalog]);
  const isWildcard = useCallback(
    (role) => (catalog || []).some((r) => r.role === role && (r.permissions || []).includes('*')),
    [catalog],
  );
  // G-19 (RC-90) - the checkbox vocabulary is the SERVER'S grantable
  // vocabulary (RbacService.knownPermissions(), shipped as `vocabulary` on
  // every catalog body), NOT the union of whatever the catalog rows happen to
  // hold.
  //
  // Deriving it from the rows made this editor unable to repair the damage it
  // displays. Read and write spoke different vocabularies: GET /roles renders
  // the union of DB rows, while PUT /roles/{r}/permissions validates against
  // the code-derived catalog. When RC-90 left eight roles with zero rows,
  // every token those roles alone would have contributed disappeared from the
  // grid - so the box could not be ticked, so the role could not be fixed
  // from the UI. An editor whose vocabulary is a function of the data it is
  // meant to correct is a mirror, not a control.
  //
  // The union fallback fires only when `vocabulary` is absent, i.e. a backend
  // older than this build during a rolling update. Degraded, never blank.
  const grouped = useMemo(() => {
    const all = new Set();
    if (Array.isArray(vocabulary) && vocabulary.length) {
      for (const p of vocabulary) if (p !== '*') all.add(p);
    } else {
      for (const r of catalog || []) for (const p of r.permissions || []) if (p !== '*') all.add(p);
    }
    const g = {};
    for (const p of [...all].sort()) {
      const domain = p.includes('.') ? p.slice(0, p.indexOf('.')) : 'other';
      (g[domain] = g[domain] || []).push(p);
    }
    return g;
  }, [catalog, vocabulary]);

  const [selectedRole, setSelectedRole] = useState(null);
  useEffect(() => {
    if ((selectedRole == null || !roles.includes(selectedRole)) && roles.length) {
      // Default to the VIEWER's own role so the matrix opens on the rights
      // that apply to them (e.g. a Super Admin lands on "Super Admin"), not an
      // arbitrary first-non-wildcard role. Fall back to the first concrete
      // role, then the first role, if the current role isn't in the catalog.
      const preferred = currentRole && roles.includes(currentRole) ? currentRole : null;
      setSelectedRole(preferred || roles.find((r) => !isWildcard(r)) || roles[0]);
    }
  }, [roles, selectedRole, isWildcard, currentRole]);

  const currentPerms = useMemo(() => {
    const row = (catalog || []).find((r) => r.role === selectedRole);
    return new Set((row?.permissions || []).filter((p) => p !== '*'));
  }, [catalog, selectedRole]);

  const [draft, setDraft] = useState(() => new Set());
  useEffect(() => { setDraft(new Set(currentPerms)); }, [currentPerms]);

  const [saving, setSaving] = useState(false);
  const wildcard = selectedRole ? isWildcard(selectedRole) : false;
  const canEdit = isPlatformAdmin && !wildcard;
  const dirty = draft.size !== currentPerms.size || [...draft].some((p) => !currentPerms.has(p));
  // G-20 - never let Save fire against an empty grid. If the vocabulary failed
  // to arrive, every checkbox is absent, so a save would submit an empty list
  // and silently strip the role. `dirty` cannot go true in that state today;
  // this states the invariant instead of leaving it emergent. Reset stays
  // enabled on purpose - it is the repair path for a role holding no rows.
  const vocabularyEmpty = Object.keys(grouped).length === 0;

  const toggle = (perm) => {
    if (!canEdit) return;
    setDraft((prev) => {
      const next = new Set(prev);
      if (next.has(perm)) next.delete(perm);
      else next.add(perm);
      return next;
    });
  };

  const runMutation = async (fn, okMsg) => {
    setSaving(true);
    try {
      const res = assertOk(await fn());
      onSaved?.(res && (res.data || res));
      addToast?.({ tone: 'success', title: okMsg });
    } catch (e) {
      addToast?.({ tone: 'danger', title: `Failed for ${selectedRole}`, description: e?.message });
    } finally {
      setSaving(false);
    }
  };

  if (!catalog || catalog.length === 0) return null;

  return (
    <Card padding="md">
      <Card.Header
        title="Role → Rights matrix"
        subtitle={isPlatformAdmin
          ? 'Edit what each role can do. Platform-wide — changes are sealed and apply across every customer.'
          : 'What each role can do. Read-only — only Platform Admin can change role permissions.'}
      />
      <Card.Body>
        {/* RC-90 — roles the platform never seeded. `missingRoles` is the
            server's own list (factory role with no permission rows AND no
            seed-ledger entry), not something the browser infers, so a role an
            operator deliberately emptied is NOT flagged here. This banner is
            the thing whose absence let production run for weeks with eight
            roles that could perform no action. */}
        {Array.isArray(missingRoles) && missingRoles.length > 0 && (
          <div className="bg-v2-danger-soft border border-v2-danger/30 rounded-v2-md px-3 py-2 mb-3 text-[11.5px] text-v2-danger">
            <strong>
              {missingRoles.length} {missingRoles.length === 1 ? 'role holds' : 'roles hold'} no permissions
            </strong>{' '}
            — <span className="font-mono-v2">{missingRoles.join(', ')}</span>. These were never seeded
            from the factory default, so anyone holding them can perform no action. Select each role
            below and use <em>Reset to default</em> to restore its factory grants.
          </div>
        )}
        {/* RC-87 — Selected role pill needs an unmistakable visual cue.
            Pre-RC-87 the only difference between selected and unselected
            was Pill tone (trust = light purple, muted = gray) — operators
            reported they couldn't tell which role they had picked. The
            two tones are close in lightness so the contrast was muted by
            the surrounding chip layout. We wrap the selected pill in a
            ring + slight scale + bold weight bump so the choice is
            obvious at a glance. Aria-pressed is set so screen readers
            announce the toggle state correctly. */}
        <div className="flex flex-wrap gap-1.5 mb-3">
          {roles.map((r) => {
            const isSelected = selectedRole === r;
            return (
              <button
                key={r}
                type="button"
                onClick={() => setSelectedRole(r)}
                aria-pressed={isSelected}
                aria-label={`Role ${r}${isSelected ? ' (selected)' : ''}`}
                className={
                  'border-0 bg-transparent p-0 cursor-pointer rounded-v2-pill transition-colors duration-150 ' +
                  (isSelected ? '' : 'opacity-70 hover:opacity-100')
                }
              >
                {/* Selected = SOLID brand fill + white text (filled pill),
                    not a ring/box drawn on top. Unselected = muted soft. */}
                <Pill tone="action" variant={isSelected ? 'solid' : 'soft'} size="sm">
                  <span className={isSelected ? 'font-semibold' : 'font-medium'}>
                    {r}{isWildcard(r) ? ' ✲' : ''}
                  </span>
                </Pill>
              </button>
            );
          })}
        </div>

        {wildcard ? (
          /* RC-88 — Wildcard roles (Platform Admin, Super Admin) now
              show the operator BOTH the wildcard caption AND a read-only
              breakdown of every permission the wildcard expands to.
              Pre-RC-88 only the italic caption was shown — operators
              couldn't verify what "*" actually grants, which is a real
              audit-hygiene gap (a wildcard is the most powerful grant
              in the system and ought to be inspectable). The breakdown
              reuses the same `grouped` vocabulary the editable path
              renders, so the layout is visually consistent across roles.
              Every checkbox is checked + disabled to communicate
              "yes, all of these — and they cannot be unchecked here". */
          <>
            <p className="text-[12px] text-v2-ink-secondary m-0 mb-3">
              <strong>{selectedRole}</strong> holds the <span className="font-mono-v2">*</span> wildcard —{' '}
              {selectedRole === 'Platform Admin'
                ? 'every permission, platform-wide across all customers (the only global wildcard).'
                : selectedRole === 'Super Admin'
                  ? 'every permission within its own customer(s) — customer-scoped, not platform-wide.'
                  : 'every permission.'}{' '}
              Not editable here (a wildcard has no per-permission list).
            </p>
            <div className="bg-v2-action-soft border border-v2-action/30 rounded-v2-sm px-3 py-2 mb-3 text-[11.5px] text-v2-action-strong">
              Showing every concrete permission the <span className="font-mono-v2">*</span> wildcard
              expands to, grouped by domain. All boxes are checked + disabled — the wildcard cannot be
              edited per-permission. To narrow {selectedRole}, replace the wildcard with a curated list
              via the platform-default-edit path (Platform Admin only).
            </div>
            <div className="space-y-3 opacity-90">
              {Object.keys(grouped).sort().map((domain) => (
                <div key={domain}>
                  <div className="text-[11px] font-bold uppercase tracking-wide text-v2-ink-muted mb-1">{domain}</div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-x-3 gap-y-1">
                    {grouped[domain].map((perm) => (
                      <label key={perm} className="flex items-center gap-1.5 text-[11.5px] text-v2-ink-secondary cursor-default">
                        <input type="checkbox" checked readOnly disabled />
                        <span className="truncate font-mono-v2">{perm}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <>
            {isPlatformAdmin === false && (
              <p className="text-[11.5px] text-v2-ink-muted italic mt-0 mb-2">
                Read-only — only Platform Admin can change role permissions.
              </p>
            )}
            <div className="space-y-3">
              {Object.keys(grouped).sort().map((domain) => (
                <div key={domain}>
                  <div className="text-[11px] font-bold uppercase tracking-wide text-v2-ink-muted mb-1">{domain}</div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-x-3 gap-y-1">
                    {grouped[domain].map((perm) => {
                      const on = draft.has(perm);
                      return (
                        <label key={perm}
                          className={`flex items-center gap-1.5 text-[11.5px] ${canEdit ? 'cursor-pointer' : 'cursor-default'} ${on ? 'text-v2-ink-secondary' : 'text-v2-ink-muted'}`}>
                          <input type="checkbox" checked={on} disabled={!canEdit} onChange={() => toggle(perm)} />
                          <span className="truncate font-mono-v2">{perm}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
            {isPlatformAdmin && (
              <div className="flex items-center gap-2 mt-3 pt-3 border-t border-v2-border-subtle">
                <Button variant="primary" size="sm" loading={saving} disabled={!dirty || vocabularyEmpty}
                  onClick={() => runMutation(() => api.updateRolePermissions(selectedRole, [...draft]), `Updated ${selectedRole}`)}>
                  Save changes
                </Button>
                <Button variant="ghost" size="sm" disabled={saving}
                  onClick={() => runMutation(() => api.resetRolePermissions(selectedRole), `Reset ${selectedRole} to default`)}>
                  Reset to default
                </Button>
                {dirty && <span className="text-[11px] text-v2-ink-muted">Unsaved changes</span>}
              </div>
            )}
          </>
        )}
      </Card.Body>
    </Card>
  );
}

/**
 * RBAC Phase 3 — JIT / temporary elevation. Super/Platform Admin grants a user
 * an elevated role for a bounded window (mandatory reason). Auto-expires; lists
 * and revokes active elevations.
 */
function ElevationsPanel({ canElevate, users, roleNames, addToast }) {
  const [list, setList] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ subjectUserId: '', role: '', hours: 4, reason: '', scopeKey: '' });
  const [subjectScopes, setSubjectScopes] = useState([]); // the subject's memberships
  const [busy, setBusy] = useState(false);

  // Load the selected subject's memberships so the granter can pick a scope when
  // the subject belongs to more than one (the backend requires it in that case).
  useEffect(() => {
    if (!form.subjectUserId) { setSubjectScopes([]); return; }
    let cancelled = false;
    Promise.resolve(api.getUserMemberships(form.subjectUserId)).then((r) => {
      const rows = (r && (r.data || r.items || r)) || [];
      if (!cancelled) setSubjectScopes(Array.isArray(rows) ? rows : []);
    }).catch(() => { if (!cancelled) setSubjectScopes([]); });
    return () => { cancelled = true; };
  }, [form.subjectUserId]);

  const load = useCallback(() => {
    Promise.resolve(api.listElevations())
      .then((res) => { const r = res && (res.data || res); setList(Array.isArray(r) ? r : []); })
      .catch(() => setList([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const userLabel = (id) => {
    const u = (users || []).find((x) => x.id === id);
    return u ? (u.name || u.email || id) : id;
  };

  const submit = async () => {
    setBusy(true);
    try {
      const expiresAt = new Date(Date.now() + form.hours * 3600 * 1000).toISOString();
      let scopeOrgId = null;
      let scopeTenantId = null;
      if (form.scopeKey) {
        const [o, t] = form.scopeKey.split('|');
        scopeOrgId = o || null;
        scopeTenantId = t || null;
      }
      assertOk(await api.grantElevation(
        form.subjectUserId, form.role, expiresAt, form.reason.trim(), scopeOrgId, scopeTenantId));
      addToast?.({ tone: 'success', title: 'Elevation granted' });
      setCreating(false); setForm({ subjectUserId: '', role: '', hours: 4, reason: '', scopeKey: '' }); load();
    } catch (e) {
      addToast?.({ tone: 'danger', title: 'Grant failed', description: e?.message });
    } finally { setBusy(false); }
  };

  const revoke = async (id) => {
    setBusy(true);
    try {
      assertOk(await api.revokeElevation(id));
      addToast?.({ tone: 'success', title: 'Elevation revoked' });
      load();
    } catch (e) {
      addToast?.({ tone: 'danger', title: 'Revoke failed', description: e?.message });
    } finally { setBusy(false); }
  };

  return (
    <Card padding="md">
      <Card.Header
        title="Temporary elevation (JIT)"
        subtitle="Grant a user an elevated role for a bounded window. Auto-expires; every action is sealed."
      />
      <Card.Body>
        {canElevate && (
          <div className="mb-3">
            <Button variant="primary" size="sm" onClick={() => setCreating((v) => !v)}>Grant elevation</Button>
          </div>
        )}
        {creating && canElevate && (
          <div className="mb-3 p-2 rounded border border-v2-border-subtle space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <select value={form.subjectUserId}
                onChange={(e) => setForm({ ...form, subjectUserId: e.target.value, scopeKey: '' })}
                className="text-[12px] px-2 py-1 border border-v2-border-subtle rounded">
                <option value="">Select user…</option>
                {(users || []).map((u) => <option key={u.id} value={u.id}>{u.name || u.email || u.id}</option>)}
              </select>
              <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
                className="text-[12px] px-2 py-1 border border-v2-border-subtle rounded">
                <option value="">Select role…</option>
                {(roleNames || []).map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
              <select value={form.hours} onChange={(e) => setForm({ ...form, hours: Number(e.target.value) })}
                className="text-[12px] px-2 py-1 border border-v2-border-subtle rounded">
                <option value={1}>1 hour</option>
                <option value={4}>4 hours</option>
                <option value={8}>8 hours</option>
                <option value={24}>24 hours</option>
              </select>
              {/* Scope picker — required only when the subject belongs to >1 org. */}
              {subjectScopes.length > 1 && (
                <select value={form.scopeKey} onChange={(e) => setForm({ ...form, scopeKey: e.target.value })}
                  className="text-[12px] px-2 py-1 border border-v2-border-subtle rounded">
                  <option value="">Select scope…</option>
                  {subjectScopes.map((m, i) => (
                    <option key={i} value={`${m.orgId || ''}|${m.tenantId || ''}`}>
                      {(m.orgName || m.orgId || '—')}
                      {(m.tenantName || m.tenantId) ? ` / ${m.tenantName || m.tenantId}` : ''}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })}
              placeholder="Reason (required — break-glass)"
              className="w-full text-[12px] px-2 py-1 border border-v2-border-subtle rounded" />
            <p className="text-[11px] text-v2-ink-muted m-0">
              {subjectScopes.length > 1
                ? 'This user belongs to multiple orgs — pick which scope the elevation applies in.'
                : 'Applies in the selected user’s own org/tenant scope.'}{' '}
              You can only grant a role below your own level.
            </p>
            <div className="flex items-center gap-2">
              <Button variant="primary" size="sm" loading={busy}
                disabled={!form.subjectUserId || !form.role || !form.reason.trim()
                  || (subjectScopes.length > 1 && !form.scopeKey)}
                onClick={submit}>
                Grant
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setCreating(false)}>Cancel</Button>
            </div>
          </div>
        )}
        {list == null ? (
          <p className="text-[12.5px] text-v2-ink-muted italic m-0">Loading…</p>
        ) : list.length === 0 ? (
          <p className="text-[12.5px] text-v2-ink-muted italic m-0">No active elevations.</p>
        ) : (
          <ul className="m-0 p-0 list-none space-y-2">
            {list.map((e) => (
              <li key={e.id} className="flex items-center gap-2 text-[12px]">
                <Pill tone="warning" size="sm">{e.role}</Pill>
                <span className="flex-1 min-w-0 truncate">
                  {userLabel(e.subjectUserId)}{' '}
                  <span className="text-v2-ink-muted">
                    in {e.scopeLabel || '—'} · until {e.expiresAt ? new Date(e.expiresAt).toLocaleString() : '—'} · {e.reason}
                  </span>
                </span>
                {canElevate && (
                  <Button variant="danger" size="sm" disabled={busy} onClick={() => revoke(e.id)}>Revoke</Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card.Body>
    </Card>
  );
}

/**
 * RBAC Phase 2 — org-scoped custom roles. Org Admin+ builds a role from a
 * permission subset (the backend bounds it by the creator's own grants). Lists
 * and deletes existing custom roles for the active org.
 */
function CustomRolesPanel({ canManage, addToast }) {
  const [data, setData] = useState(null); // { customRoles, roles (palette), permsRev }
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [draft, setDraft] = useState(() => new Set());
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    Promise.resolve(api.getCustomRoles())
      .then((res) => {
        const b = res && (res.data || res);
        setData(b && Array.isArray(b.customRoles) ? b : { customRoles: [], roles: [] });
      })
      .catch(() => setData({ customRoles: [], roles: [] }));
  }, []);
  useEffect(() => { load(); }, [load]);

  const palette = useMemo(() => {
    const all = new Set();
    for (const r of data?.roles || []) for (const p of r.permissions || []) if (p !== '*') all.add(p);
    const g = {};
    for (const p of [...all].sort()) {
      const dom = p.includes('.') ? p.slice(0, p.indexOf('.')) : 'other';
      (g[dom] = g[dom] || []).push(p);
    }
    return g;
  }, [data]);

  const toggle = (p) => setDraft((prev) => {
    const n = new Set(prev);
    if (n.has(p)) n.delete(p);
    else n.add(p);
    return n;
  });

  const submit = async () => {
    setBusy(true);
    try {
      assertOk(await api.createCustomRole(name.trim(), [...draft]));
      addToast?.({ tone: 'success', title: `Custom role '${name.trim()}' created` });
      setCreating(false); setName(''); setDraft(new Set()); load();
    } catch (e) {
      addToast?.({ tone: 'danger', title: 'Create failed', description: e?.message });
    } finally { setBusy(false); }
  };

  const remove = async (roleName) => {
    setBusy(true);
    try {
      assertOk(await api.deleteCustomRole(roleName));
      addToast?.({ tone: 'success', title: `Deleted '${roleName}'` });
      load();
    } catch (e) {
      addToast?.({ tone: 'danger', title: 'Delete failed', description: e?.message });
    } finally { setBusy(false); }
  };

  return (
    <Card padding="md">
      <Card.Header
        title="Custom roles"
        subtitle="Org-scoped roles built from a permission subset (bounded by your own access)."
      />
      <Card.Body>
        {canManage && (
          <div className="mb-3">
            <Button variant="primary" size="sm" onClick={() => setCreating((v) => !v)}>New custom role</Button>
          </div>
        )}
        {creating && canManage && (
          <div className="mb-3 p-2 rounded border border-v2-border-subtle space-y-2">
            <input value={name} onChange={(e) => setName(e.target.value)}
              placeholder="Role name (e.g. Read-only Auditor)"
              className="w-full text-[12px] px-2 py-1 border border-v2-border-subtle rounded" />
            <div className="space-y-2 max-h-64 overflow-auto">
              {Object.keys(palette).sort().map((dom) => (
                <div key={dom}>
                  <div className="text-[11px] font-bold uppercase tracking-wide text-v2-ink-muted mb-1">{dom}</div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-x-3 gap-y-1">
                    {palette[dom].map((p) => (
                      <label key={p} className="flex items-center gap-1.5 text-[11.5px] cursor-pointer">
                        <input type="checkbox" checked={draft.has(p)} onChange={() => toggle(p)} />
                        <span className="truncate font-mono-v2">{p}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="primary" size="sm" loading={busy} disabled={!name.trim() || draft.size === 0} onClick={submit}>
                Create role
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setCreating(false)}>Cancel</Button>
            </div>
          </div>
        )}

        {data == null ? (
          <p className="text-[12.5px] text-v2-ink-muted italic m-0">Loading…</p>
        ) : data.customRoles.length === 0 ? (
          <p className="text-[12.5px] text-v2-ink-muted italic m-0">No custom roles in this org yet.</p>
        ) : (
          <ul className="m-0 p-0 list-none space-y-2">
            {data.customRoles.map((r) => (
              <li key={r.role} className="flex items-start gap-2 text-[12px]">
                <Pill tone="trust" size="sm">{r.role}</Pill>
                <span className="flex-1 min-w-0 text-v2-ink-muted">
                  {(r.permissions || []).length} permissions: {(r.permissions || []).slice(0, 6).join(', ')}
                  {(r.permissions || []).length > 6 ? '…' : ''}
                </span>
                {canManage && (
                  <Button variant="danger" size="sm" disabled={busy} onClick={() => remove(r.role)}>Delete</Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card.Body>
    </Card>
  );
}

/**
 * RBAC Phase 4 Slice B — Access attestation / recertification.
 * Open a campaign over a scope, then certify or revoke each in-scope grant;
 * "Close & revoke uncertified" sweeps the rest. All decisions are sealed.
 */
/* ── Roles tab ──────────────────────────────────────────────────────────────
 * A discoverable home for role definition: the global Role → Rights matrix and
 * ORG-SCOPED CUSTOM ROLES (create / edit / delete). These panels previously only
 * rendered in the Users tab's right rail when NO user was selected — undiscoverable,
 * so admins could not find "create a custom role". This surfaces the SAME reusable
 * panels (no logic duplication) as a first-class tab. */
function RolesInventory() {
  const { session, user: currentUser } = useAuth();
  const { addToast } = useToast();
  const role = session?.role || currentUser?.role;
  const isPlatformAdmin = role === 'Platform Admin';
  // Mirrors CustomRolesPanel's own gate + the backend's (Org Admin+ to create a custom role).
  const canManageCustom = ['Platform Admin', 'Super Admin', 'Org Admin'].includes(role);

  const [roleCatalog, setRoleCatalog] = useState([]);
  const [roleVocabulary, setRoleVocabulary] = useState(null);
  const [missingRoles, setMissingRoles] = useState([]);

  const applyCatalog = useCallback((body) => {
    const rows = body?.roles;
    if (!Array.isArray(rows)) return;
    setRoleCatalog(rows);
    setRoleVocabulary(Array.isArray(body?.vocabulary) ? body.vocabulary : null);
    setMissingRoles(Array.isArray(body?.missingRoles) ? body.missingRoles : []);
  }, []);
  useEffect(() => {
    let cancelled = false;
    Promise.resolve(api.getRoleCatalog())
      .then((res) => { if (!cancelled) applyCatalog(res && (res.data || res)); })
      .catch(() => { /* keep empty; panels render their own fallback */ });
    return () => { cancelled = true; };
  }, [applyCatalog]);

  return (
    <div className="space-y-3">
      {/* RBAC Phase 2 — org-scoped custom roles (create / edit / delete) FIRST:
          this is the actionable panel for an org admin (where they mint the
          roles the Add/Invite pickers then offer), so it leads; the read-only
          global matrix is reference and follows. */}
      <CustomRolesPanel canManage={canManageCustom} addToast={addToast} />
      {/* RBAC Phase 1b — global Role → Rights matrix (Platform Admin writes). */}
      <RoleRightsMatrix
        catalog={roleCatalog}
        vocabulary={roleVocabulary}
        missingRoles={missingRoles}
        isPlatformAdmin={isPlatformAdmin}
        currentRole={role}
        addToast={addToast}
        onSaved={applyCatalog}
      />
    </div>
  );
}

function AttestationPanel({ isPlatformAdmin, addToast }) {
  const [campaigns, setCampaigns] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: '', scopeType: 'tenant', dueAt: '' });
  const [selectedId, setSelectedId] = useState(null);
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadCampaigns = useCallback(() => {
    Promise.resolve(api.listAttestations())
      .then((res) => { const rows = res && (res.data || res); setCampaigns(Array.isArray(rows) ? rows : []); })
      .catch(() => setCampaigns([]));
  }, []);
  useEffect(() => { loadCampaigns(); }, [loadCampaigns]);

  const loadItems = useCallback((id) => {
    setItems(null);
    Promise.resolve(api.getAttestationItems(id))
      .then((res) => { const rows = res && (res.data || res); setItems(Array.isArray(rows) ? rows : []); })
      .catch(() => setItems([]));
  }, []);

  const selectCampaign = (id) => {
    if (selectedId === id) { setSelectedId(null); setItems(null); return; }
    setSelectedId(id);
    loadItems(id);
  };

  const submitCreate = async () => {
    setBusy(true);
    try {
      assertOk(await api.createAttestation(form.name, form.scopeType, form.dueAt || null));
      addToast?.({ tone: 'success', title: 'Attestation campaign opened' });
      setCreating(false);
      setForm({ name: '', scopeType: 'tenant', dueAt: '' });
      loadCampaigns();
    } catch (e) {
      addToast?.({ tone: 'danger', title: 'Failed to open campaign', description: e?.message });
    } finally { setBusy(false); }
  };

  const decide = async (itemId, decision) => {
    setBusy(true);
    try {
      assertOk(await api.decideAttestationItem(itemId, decision, null));
      loadItems(selectedId);
      loadCampaigns();
    } catch (e) {
      addToast?.({ tone: 'danger', title: 'Action failed', description: e?.message });
    } finally { setBusy(false); }
  };

  const closeCampaign = async (id, revokeUncertified) => {
    setBusy(true);
    try {
      assertOk(await api.closeAttestation(id, revokeUncertified));
      addToast?.({ tone: 'success', title: revokeUncertified ? 'Closed — uncertified revoked' : 'Campaign closed' });
      loadCampaigns();
      if (selectedId === id) loadItems(id);
    } catch (e) {
      addToast?.({ tone: 'danger', title: 'Close failed', description: e?.message });
    } finally { setBusy(false); }
  };

  return (
    <Card padding="md">
      <Card.Header
        title="Access attestation"
        subtitle="Recertify who still needs their access — certify or revoke each grant. Revokes are sealed."
      />
      <Card.Body>
        <div className="mb-3">
          <Button variant="primary" size="sm" onClick={() => setCreating((v) => !v)}>New campaign</Button>
        </div>
        {creating && (
          <div className="mb-3 p-2 rounded border border-v2-border-subtle space-y-2">
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Campaign name (e.g. Q3 access recertification)"
              className="w-full text-[12px] px-2 py-1 border border-v2-border-subtle rounded" />
            <div className="flex flex-wrap items-center gap-2">
              <select value={form.scopeType} onChange={(e) => setForm({ ...form, scopeType: e.target.value })}
                className="text-[12px] px-2 py-1 border border-v2-border-subtle rounded">
                <option value="tenant">My tenant</option>
                <option value="org">My org</option>
                {isPlatformAdmin && <option value="platform">Platform (all)</option>}
              </select>
              <input type="date"
                onChange={(e) => setForm({ ...form, dueAt: e.target.value ? new Date(e.target.value).toISOString() : '' })}
                className="text-[12px] px-2 py-1 border border-v2-border-subtle rounded" />
              <Button variant="primary" size="sm" loading={busy} onClick={submitCreate}>Open campaign</Button>
              <Button variant="ghost" size="sm" onClick={() => setCreating(false)}>Cancel</Button>
            </div>
          </div>
        )}

        {campaigns == null ? (
          <p className="text-[12.5px] text-v2-ink-muted italic m-0">Loading…</p>
        ) : campaigns.length === 0 ? (
          <p className="text-[12.5px] text-v2-ink-muted italic m-0">No attestation campaigns yet.</p>
        ) : (
          <ul className="m-0 p-0 list-none space-y-2">
            {campaigns.map((c) => (
              <li key={c.id} className="border border-v2-border-subtle rounded">
                <div className="flex items-center gap-2 p-2 cursor-pointer" onClick={() => selectCampaign(c.id)}>
                  <Pill tone={c.status === 'open' ? 'trust' : 'muted'} size="sm">{c.status}</Pill>
                  <span className="font-semibold text-[12.5px] flex-1 min-w-0 truncate">{c.name}</span>
                  <span className="text-[11px] text-v2-ink-muted">{c.scopeType}</span>
                  <span className="text-[11px] text-v2-ink-muted">
                    {c.certified}✓ / {c.revoked}✕ / {c.pending}• of {c.total}
                  </span>
                </div>
                {selectedId === c.id && (
                  <div className="border-t border-v2-border-subtle p-2 space-y-1">
                    {items == null ? (
                      <p className="text-[11.5px] text-v2-ink-muted italic m-0">Loading items…</p>
                    ) : items.length === 0 ? (
                      <p className="text-[11.5px] text-v2-ink-muted italic m-0">No grants in scope.</p>
                    ) : items.map((it) => (
                      <div key={it.id} className="flex items-center gap-2 text-[11.5px]">
                        <Pill tone={it.decision === 'certified' ? 'success' : it.decision === 'revoked' ? 'danger' : 'muted'} size="sm">
                          {it.decision}
                        </Pill>
                        <span className="flex-1 min-w-0 truncate">
                          {it.userLabel} — <span className="font-mono-v2">{it.role}</span>{' '}
                          <span className="text-v2-ink-muted">({it.scopeLabel})</span>
                        </span>
                        {c.status === 'open' && it.decision === 'pending' && (
                          <>
                            <Button variant="ghost" size="sm" disabled={busy} onClick={() => decide(it.id, 'certified')}>Certify</Button>
                            <Button variant="danger" size="sm" disabled={busy} onClick={() => decide(it.id, 'revoked')}>Revoke</Button>
                          </>
                        )}
                      </div>
                    ))}
                    {c.status === 'open' && (
                      <div className="flex flex-wrap items-center gap-2 pt-2 mt-1 border-t border-v2-border-subtle">
                        <Button variant="ghost" size="sm" disabled={busy} onClick={() => closeCampaign(c.id, false)}>Close</Button>
                        <Button variant="danger" size="sm" disabled={busy} onClick={() => closeCampaign(c.id, true)}>
                          Close &amp; revoke uncertified
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card.Body>
    </Card>
  );
}

function AccessReviewInventory({ orgs, tenants }) {
  const navigate = useNavigate();
  const platform = usePlatform();
  const { addToast } = useToast();
  // RBAC Phase 1b — only Platform Admin may edit the global role matrix (the
  // backend enforces this too; this just decides whether to show live controls).
  const { session, user: currentUser } = useAuth();
  const isPlatformAdmin = (session?.role || currentUser?.role) === 'Platform Admin';
  // RBAC Phase 2 — Org Admin (or higher) can manage custom roles in their org.
  const canManageCustom = ['Platform Admin', 'Super Admin', 'Org Admin'].includes(session?.role || currentUser?.role);
  // RBAC Phase 3 — only Super/Platform Admin can grant temporary elevation.
  const canElevate = ['Platform Admin', 'Super Admin'].includes(session?.role || currentUser?.role);
  const {
    users = [], loadUsers,
    assignments = [], loadAssignments,
    connectorAssignments = [], loadConnectorAssignments,
    agents = [], loadAgents,
    workflows = [], loadWorkflows,
    connectorGroups = [], loadConnectorGroups,
    connectors = [], loadConnectors,
    groups: groupsList = [], loadGroups,
  } = platform;

  /* RBAC management Phase 0 — fetch the server-authoritative role→rights
   * catalog so the "Effective permissions" view is truthful (the local
   * ROLE_PERMS_MAP is stale and missing the V208 roles). Falls back to the
   * static map until the fetch resolves. Builds { role: permissions[] }. */
  const [rolePerms, setRolePerms] = useState(null);
  const [roleCatalog, setRoleCatalog] = useState([]);   // raw [{role, level, permissions[]}] for the editor
  // G-19 - the server's grantable vocabulary, so the Role->Rights grid renders
  // every permission the backend will ACCEPT rather than only the ones some
  // role currently holds. null until the first catalog body lands.
  const [roleVocabulary, setRoleVocabulary] = useState(null);
  // RC-90 - factory roles the platform never seeded (no rows, no ledger entry).
  const [missingRoles, setMissingRoles] = useState([]);
  // You can only elevate someone to a role STRICTLY BELOW your own level (Platform
  // Admin is unbounded). Mirrors the backend's grant bound so the dropdown never
  // offers a role the grant would 403 on. (Declared AFTER roleCatalog to avoid a TDZ.)
  const granterLevel = (roleCatalog.find((r) => r.role === (session?.role || currentUser?.role))?.level) ?? 0;
  const elevatableRoles = isPlatformAdmin
    ? (roleCatalog || []).map((r) => r.role)
    : (roleCatalog || []).filter((r) => (r.level ?? 99) < granterLevel).map((r) => r.role);
  const [sodConflicts, setSodConflicts] = useState(null);   // RBAC Phase 4 — SoD analytics
  const [rbacAudit, setRbacAudit] = useState(null);
  // UI_DISPLAY R6 — server-side paginated audit feed via the shared <Pagination>.
  const [rbacAuditTotal, setRbacAuditTotal] = useState(0);
  const [rbacAuditPage, setRbacAuditPage] = useState(0);
  const [rbacAuditSize, setRbacAuditSize] = useState(10);
  // Apply a { roles, permsRev } catalog body to both the editor (raw) and the
  // "Effective permissions" display map. Reused by the initial load and by the
  // editor after every save/reset (the endpoints return the fresh catalog).
  const applyCatalog = useCallback((body) => {
    const rows = body?.roles;
    if (!Array.isArray(rows)) return;
    setRoleCatalog(rows);
    // G-19 / RC-90 - the grantable vocabulary and the never-seeded-role list
    // ride along on every catalog body (GET /roles and the PUT/reset
    // responses alike), so repairing a stranded role shrinks the banner in
    // the same round trip that wrote the fix.
    setRoleVocabulary(Array.isArray(body?.vocabulary) ? body.vocabulary : null);
    setMissingRoles(Array.isArray(body?.missingRoles) ? body.missingRoles : []);
    const map = {};
    for (const r of rows) {
      const perms = (r.permissions || []).map((p) => (p === '*' ? '* (all permissions)' : p));
      map[r.role] = perms.length ? perms : ['(none)'];
    }
    setRolePerms(map);
  }, []);
  useEffect(() => {
    let cancelled = false;
    Promise.resolve(api.getRoleCatalog()).then((res) => {
      if (cancelled) return;
      applyCatalog(res && (res.data || res));
    }).catch(() => { /* keep static fallback */ });
    // RBAC Phase 4 — Segregation-of-Duties conflicts (read-only governance).
    Promise.resolve(api.getSodConflicts()).then((res) => {
      const rows = res && (res.data || res);
      if (!cancelled) setSodConflicts(Array.isArray(rows) ? rows : []);
    }).catch(() => { if (!cancelled) setSodConflicts([]); });
    return () => { cancelled = true; };
  }, [applyCatalog]);
  // RBAC management — recent role/permission seals, paginated server-side.
  useEffect(() => {
    let cancelled = false;
    Promise.resolve(api.getRbacAudit(rbacAuditPage, rbacAuditSize)).then((res) => {
      const body = res && (res.data || res);
      if (cancelled) return;
      const items = body && Array.isArray(body.items) ? body.items
        : (Array.isArray(body) ? body : []);   // tolerate legacy array shape
      setRbacAudit(items);
      setRbacAuditTotal(body && typeof body.total === 'number' ? body.total : items.length);
    }).catch(() => { if (!cancelled) { setRbacAudit([]); setRbacAuditTotal(0); } });
    return () => { cancelled = true; };
  }, [rbacAuditPage, rbacAuditSize]);

  /* Bulk-load all the join data once on mount. Each loader is a
   * memoised callback in PlatformContext so re-renders don't re-fetch. */
  useEffect(() => {
    if (typeof loadUsers === 'function') loadUsers();
    if (typeof loadAgents === 'function') loadAgents();
    if (typeof loadWorkflows === 'function') loadWorkflows();
    if (typeof loadConnectorGroups === 'function') loadConnectorGroups();
    if (typeof loadConnectors === 'function') loadConnectors();
    if (typeof loadGroups === 'function') loadGroups();
  }, [loadUsers, loadAgents, loadWorkflows, loadConnectorGroups, loadConnectors, loadGroups]);

  /* Task #116 — per-agent fan-out for assignments (same pattern as
   * AssignmentsInventory above). The Users tab slideout reads the
   * global assignments arrays to compute per-user agent/connector
   * access counts; those arrays only populate when we fan out
   * per-agent calls. The no-arg call was silently degraded by the
   * G373 UUID guard. */
  useEffect(() => {
    if (!Array.isArray(agents) || agents.length === 0) return;
    if (typeof loadAssignments !== 'function') return;
    if (typeof loadConnectorAssignments !== 'function') return;
    agents.forEach((a) => {
      if (a && a.id) {
        loadAssignments(a.id);
        loadConnectorAssignments(a.id);
      }
    });
  }, [agents, loadAssignments, loadConnectorAssignments]);

  /* User picker state. */
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [userSearch, setUserSearch] = useState('');
  const pgUsers = usePaginationState(15);
  useEffect(() => { pgUsers.setPage(0); }, [userSearch]); // eslint-disable-line

  const filteredUsers = useMemo(() => {
    const q = userSearch.trim().toLowerCase();
    if (!q) return users;
    return users.filter(u => {
      const name = `${u.firstName || ''} ${u.lastName || ''}`.toLowerCase();
      return name.includes(q)
        || (u.email || '').toLowerCase().includes(q)
        || (u.id || '').toLowerCase().includes(q);
    });
  }, [users, userSearch]);
  const visibleUsers = pgUsers.paginate(filteredUsers);

  /* Memberships lazy-load per selected user. */
  const [memberships, setMemberships] = useState([]);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(null); // membershipId pending inline confirm
  const [membershipBusy, setMembershipBusy] = useState(false);
  const reloadMemberships = useCallback(async () => {
    if (!selectedUserId) { setMemberships([]); return; }
    setLoadingDetail(true);
    try {
      const r = await api.getUserMemberships(selectedUserId);
      setMemberships(Array.isArray(r?.data) ? r.data
                     : Array.isArray(r?.items) ? r.items
                     : Array.isArray(r) ? r : []);
    } catch (err) {
      addToast({ tone: 'danger', title: 'Failed to load memberships', description: err?.message });
    } finally {
      setLoadingDetail(false);
    }
  }, [selectedUserId]);
  useEffect(() => { setConfirmRemove(null); reloadMemberships(); }, [selectedUserId, reloadMemberships]);

  // Set a grant as the user's primary/home. BE clears the prior default and
  // re-pins AppUser.tenantId (setDefaultMembership).
  const handleSetPrimaryMembership = async (m) => {
    const id = m.id || m.membershipId; if (!id) return;
    setMembershipBusy(true);
    try {
      const res = await api.setDefaultMembership(selectedUserId, id);
      if (res && res.ok === false) {
        addToast({ tone: 'danger', title: 'Could not set primary', description: res.error || res.data?.error });
        return;
      }
      addToast({ tone: 'success', title: 'Primary membership updated' });
      await reloadMemberships();
    } catch (e) {
      addToast({ tone: 'danger', title: 'Could not set primary', description: e?.message });
    } finally { setMembershipBusy(false); }
  };
  // Remove a grant. BE refuses the user's ONLY membership (409) and auto-promotes
  // a new default when the removed grant was the primary — so removing a
  // Default-Org grant a user no longer needs is safe.
  const handleRemoveMembership = async (m) => {
    const id = m.id || m.membershipId; if (!id) return;
    setMembershipBusy(true);
    try {
      const res = await api.removeUserMembership(selectedUserId, id);
      if (res && res.ok === false) {
        addToast({ tone: 'danger', title: 'Could not remove membership',
          description: res.error || res.data?.error || `HTTP ${res.status || '?'}` });
        return;
      }
      addToast({ tone: 'success', title: 'Membership removed' });
      setConfirmRemove(null);
      await reloadMemberships();
    } catch (e) {
      addToast({ tone: 'danger', title: 'Could not remove membership', description: e?.message });
    } finally { setMembershipBusy(false); }
  };

  /* RBAC Phase 4 — effective access (grants + per-permission provenance + SoD
     flags) for the selected user. Read-only governance explorer. */
  const [effectiveAccess, setEffectiveAccess] = useState(null);
  useEffect(() => {
    if (!selectedUserId) { setEffectiveAccess(null); return; }
    let cancelled = false;
    Promise.resolve(api.getEffectiveAccess(selectedUserId)).then((res) => {
      const body = res && (res.data || res);
      if (!cancelled) setEffectiveAccess(body && Array.isArray(body.permissions) ? body : null);
    }).catch(() => { if (!cancelled) setEffectiveAccess(null); });
    return () => { cancelled = true; };
  }, [selectedUserId]);

  /* Per-section expansion state. Defaults match V1: top 3 open, last
   * 2 collapsed because they're typically longer / less relevant for
   * the first scan. */
  const [sections, setSections] = useState({
    memberships: true, agents: true, workflows: true, connectorGroups: false, permissions: false,
  });
  const toggleSection = (k) => setSections(s => ({ ...s, [k]: !s[k] }));

  const selectedUser = useMemo(() => users.find(u => u.id === selectedUserId) || null, [users, selectedUserId]);
  const reviewName = selectedUser
    ? (selectedUser.name || `${selectedUser.firstName || ''} ${selectedUser.lastName || ''}`.trim() || selectedUser.email || selectedUser.id)
    : '';
  const userRole = selectedUser?.role || 'Viewer';
  const roleLevel = ROLE_LEVEL_MAP[userRole] || 0;

  /* Group memberships for the selected user (used by all access
   * computations to detect indirect/via-group grants). */
  const userGroups = useMemo(() => userGroupIds(selectedUser, groupsList), [selectedUser, groupsList]);
  const groupIdSet = useMemo(() => new Set(userGroups.map(g => g.id)), [userGroups]);

  /* Agent access — direct, group, owner, role-implicit. */
  const agentAccess = useMemo(() => {
    if (!selectedUser) return [];
    return agents.map(agent => {
      const allAssigns = assignments.filter(a => a.agentId === agent.id);
      const direct = allAssigns.find(a => a.userId === selectedUserId);
      const groupAssigns = allAssigns.filter(a => a.groupId && groupIdSet.has(a.groupId));
      const isOwner = agent.createdBy === selectedUserId || agent.ownerId === selectedUserId;
      const via = direct ? 'direct'
                : groupAssigns.length > 0 ? 'group'
                : (isOwner && roleLevel === 2) ? 'owner'
                : roleLevel >= 1 ? 'role'
                : null;
      if (!via) return null;
      const accessLevel = direct?.accessLevel
        || groupAssigns[0]?.accessLevel
        || (isOwner ? 'manage' : (roleLevel >= 4 ? 'admin' : roleLevel >= 3 ? 'manage' : 'execute'));
      return {
        agent, via, isOwner, accessLevel,
        groupNames: groupAssigns.map(g => userGroups.find(ug => ug.id === g.groupId)?.name || g.groupId),
      };
    }).filter(Boolean);
  }, [agents, assignments, selectedUser, selectedUserId, groupIdSet, userGroups, roleLevel]);

  const pgAgents = usePaginationState(10);
  const visibleAgentAccess = pgAgents.paginate(agentAccess);
  useEffect(() => { pgAgents.setPage(0); }, [selectedUserId]); // eslint-disable-line

  /* Workflow access — explicit + role-implicit. We don't have per-
   * workflow assignment data globally; use anything in `assignments`
   * carrying a workflowId. */
  const workflowAccess = useMemo(() => {
    if (!selectedUser) return [];
    const explicit = assignments.filter(a => a.workflowId && (
      a.userId === selectedUserId || (a.groupId && groupIdSet.has(a.groupId))
    ));
    const explicitIds = new Set(explicit.map(a => a.workflowId));
    const rolePart = roleLevel >= 1
      ? workflows.filter(w => !explicitIds.has(w.id)).map(w => ({
          workflowId: w.id,
          workflow: w,
          accessLevel: roleLevel >= 4 ? 'admin' : roleLevel >= 3 ? 'manage' : roleLevel >= 2 ? 'execute' : 'read',
          via: 'role',
        }))
      : [];
    const explicitPart = explicit.map(a => ({
      workflowId: a.workflowId,
      workflow: workflows.find(w => w.id === a.workflowId),
      accessLevel: a.accessLevel || 'read',
      via: a.userId === selectedUserId ? 'direct' : 'group',
      groupName: a.groupId ? (userGroups.find(g => g.id === a.groupId)?.name || a.groupId) : null,
    }));
    return [...explicitPart, ...rolePart];
  }, [assignments, workflows, selectedUser, selectedUserId, groupIdSet, userGroups, roleLevel]);

  const pgWorkflows = usePaginationState(10);
  const visibleWorkflowAccess = pgWorkflows.paginate(workflowAccess);
  useEffect(() => { pgWorkflows.setPage(0); }, [selectedUserId]); // eslint-disable-line

  /* Connector group memberships. Look for the user in cg.members or
   * cg.userIds — backend shapes vary. */
  const userConnectorGroups = useMemo(() => {
    if (!selectedUser) return [];
    return connectorGroups.filter(cg => {
      const m = Array.isArray(cg.members) ? cg.members : [];
      return m.some(x => x?.userId === selectedUserId || x === selectedUserId)
          || (Array.isArray(cg.userIds) && cg.userIds.includes(selectedUserId));
    });
  }, [connectorGroups, selectedUser, selectedUserId]);

  /* Org / tenant resolver for memberships labels. */
  const orgMap    = useMemo(() => Object.fromEntries((orgs || []).map(o => [o.id, o.name || o.id])), [orgs]);
  const tenantMap = useMemo(() => Object.fromEntries((tenants || []).map(t => [t.id, t.name || t.id])), [tenants]);

  return (
    <div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-5">
        <MetricCard label="Users in scope"   value={users.length} tone="cyan" />
        <MetricCard label="Groups"            value={groupsList.length} tone="trust" />
        <MetricCard label="Agents"            value={agents.length} tone="action" />
        <MetricCard label="Workflows"         value={workflows.length} tone="success" />
      </div>

      <div className="grid gap-4" style={{ gridTemplateColumns: '280px 1fr' }}>
        {/* ── User picker ───────────────────────────────────────────── */}
        <Card padding="none" className="self-start">
          <div className="p-3 border-b border-v2-border-subtle">
            <input type="text" value={userSearch} onChange={(e) => setUserSearch(e.target.value)}
              placeholder="Search users…"
              aria-label="Search users for access review"
              className="v2-focus-ring w-full bg-v2-surface text-v2-ink border border-v2-border rounded-v2-md px-2.5 py-1.5 text-[12px]" />
          </div>
          {filteredUsers.length === 0 ? (
            <div className="p-4 text-[12px] text-v2-ink-muted italic">No users match.</div>
          ) : (
            <ul className="m-0 p-0 list-none max-h-[600px] overflow-y-auto">
              {visibleUsers.map((u) => {
                const isSel = u.id === selectedUserId;
                const label = `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email || u.id;
                return (
                  <li key={u.id}>
                    <button
                      onClick={() => setSelectedUserId(u.id)}
                      className={'w-full text-left px-3 py-2 text-[12px] border-b border-v2-border-subtle transition-colors ' +
                        (isSel ? 'bg-v2-action-soft text-v2-action-strong font-semibold' : 'hover:bg-v2-muted text-v2-ink')}
                    >
                      <div className="truncate">{label}</div>
                      <div className="text-[10.5px] text-v2-ink-muted truncate">{u.email || u.id}</div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          {filteredUsers.length > pgUsers.pageSize && (
            <div className="border-t border-v2-border-subtle p-2">
              <Pagination totalItems={filteredUsers.length} page={pgUsers.page} pageSize={pgUsers.pageSize}
                onPageChange={pgUsers.setPage} onPageSizeChange={pgUsers.setPageSize}
                noun="users" />
            </div>
          )}
        </Card>

        {/* ── Selected user audit panel ─────────────────────────────── */}
        {!selectedUserId ? (
          <div className="space-y-3">
            <EmptyState
              icon={<Shield size={20} />}
              title="Pick a user to review"
              description="Select a user on the left to see their complete access profile — Memberships, Agent access, Workflow access, Connector groups, and Effective permissions."
            />
            {/* RBAC Phase 1b — Role → Rights matrix editor (Platform Admin writes). */}
            <RoleRightsMatrix
              catalog={roleCatalog}
              vocabulary={roleVocabulary}
              missingRoles={missingRoles}
              isPlatformAdmin={isPlatformAdmin}
              currentRole={session?.role || currentUser?.role}
              addToast={addToast}
              onSaved={applyCatalog}
            />
            {/* RBAC Phase 2 — org-scoped custom roles. */}
            <CustomRolesPanel canManage={canManageCustom} addToast={addToast} />
            {/* RBAC Phase 3 — JIT / temporary elevation. */}
            <ElevationsPanel
              canElevate={canElevate}
              users={users}
              roleNames={elevatableRoles}
              addToast={addToast}
            />
            {/* RBAC Phase 4 — Segregation-of-Duties conflicts. */}
            <Card padding="md">
              <Card.Header
                title="Segregation of Duties"
                subtitle="Users holding a conflicting combination of roles within the same scope."
              />
              <Card.Body>
                {sodConflicts == null ? (
                  <p className="text-[12.5px] text-v2-ink-muted italic m-0">Loading…</p>
                ) : sodConflicts.length === 0 ? (
                  <p className="text-[12.5px] text-v2-ink-muted italic m-0">No segregation-of-duties conflicts detected.</p>
                ) : (
                  <ul className="m-0 p-0 list-none space-y-2">
                    {sodConflicts.map((c, i) => (
                      <li key={`${c.userId}-${c.roleA}-${c.roleB}-${i}`} className="flex items-start gap-2 text-[12px]">
                        <Pill tone={c.severity === 'high' ? 'danger' : 'warning'} size="sm">{c.severity}</Pill>
                        <span className="flex-1 min-w-0">
                          <span className="font-semibold">{c.userLabel}</span>{' '}
                          — <span className="font-mono-v2">{c.roleA}</span> + <span className="font-mono-v2">{c.roleB}</span>{' '}
                          <span className="text-v2-ink-muted">in {c.scopeLabel}</span>
                          <span className="block text-v2-ink-muted text-[11px]">{c.rule}</span>
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </Card.Body>
            </Card>
            {/* RBAC Phase 4 Slice B — Access attestation / recertification. */}
            <AttestationPanel isPlatformAdmin={isPlatformAdmin} addToast={addToast} />
            {/* RBAC management Phase 0 — recent role grant/revoke/change seals,
                org-wide. Surfaces the audit chain's RBAC events (previously
                written but never visible). */}
            <Card padding="md">
              <Card.Header
                title="Recent role & permission activity"
                subtitle="Role grants, revokes, and changes — sealed to the immutable audit chain."
              />
              <Card.Body>
                {rbacAudit == null ? (
                  <p className="text-[12.5px] text-v2-ink-muted italic m-0">Loading…</p>
                ) : rbacAuditTotal === 0 ? (
                  <p className="text-[12.5px] text-v2-ink-muted italic m-0">No role changes recorded yet.</p>
                ) : (
                  <>
                    <ul className="m-0 p-0 list-none space-y-2">
                      {rbacAudit.map((e) => (
                        <li key={e.id} className="flex items-start gap-2 text-[12px]">
                          <Pill tone={/REVOKED|DENIED/.test(e.eventType) ? 'danger' : /GRANTED/.test(e.eventType) ? 'success' : 'muted'} size="sm">
                            {String(e.eventType || '').replace(/_/g, ' ')}
                          </Pill>
                          <span className="text-v2-ink-secondary flex-1 min-w-0 break-words">{e.message}</span>
                          <span className="text-v2-ink-muted whitespace-nowrap">{e.createdAt ? new Date(e.createdAt).toLocaleString() : ''}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-2">
                      <Pagination
                        totalItems={rbacAuditTotal}
                        page={rbacAuditPage}
                        pageSize={rbacAuditSize}
                        onPageChange={setRbacAuditPage}
                        onPageSizeChange={(s) => { setRbacAuditSize(s); setRbacAuditPage(0); }}
                        noun="events"
                      />
                    </div>
                  </>
                )}
              </Card.Body>
            </Card>
          </div>
        ) : loadingDetail && memberships.length === 0 ? (
          <div className="text-[13px] text-v2-ink-secondary italic px-4 py-10 text-center">Loading access data…</div>
        ) : (
          <div className="space-y-3">
            <button
              type="button"
              onClick={() => setSelectedUserId(null)}
              className="text-[12px] font-semibold text-v2-cyan hover:underline"
            >
              ← Back to overview
            </button>
            {/* User header */}
            <Card padding="md">
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-v2-md flex items-center justify-center font-bold text-[14px] bg-v2-cyan-soft text-v2-cyan shrink-0">
                  {(selectedUser?.firstName?.[0] || selectedUser?.email?.[0] || '?').toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="text-[16px] font-bold tracking-tight m-0">{reviewName}</h2>
                  <p className="text-[11.5px] text-v2-ink-muted font-mono-v2 mt-0.5 truncate">{selectedUser?.email || selectedUser?.id}</p>
                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                    <Pill tone={selectedUser?.status === 'active' ? 'success' : 'muted'} size="sm" dot>{selectedUser?.status || 'unknown'}</Pill>
                    <Pill tone="trust" size="sm">{userRole}</Pill>
                    {selectedUser?.mfaEnabled !== undefined && (
                      <Pill tone={selectedUser.mfaEnabled ? 'success' : 'warning'} size="sm" dot>
                        MFA {selectedUser.mfaEnabled ? 'on' : 'off'}
                      </Pill>
                    )}
                  </div>
                </div>
              </div>
            </Card>

            {/* Section: Memberships */}
            <ReviewSection
              title={`Org / Tenant memberships · ${memberships.length}`}
              expanded={sections.memberships}
              onToggle={() => toggleSection('memberships')}
            >
              {memberships.length === 0 ? (
                <p className="text-[12.5px] text-v2-ink-muted italic m-0">No memberships recorded.</p>
              ) : (
                <Table
                  ariaLabel="Memberships"
                  density="compact"
                  columns={[
                    { key: 'org', header: 'Org', render: (m) => orgMap[m.orgId] || m.orgName || m.orgId || '—' },
                    { key: 'tenant', header: 'Tenant', render: (m) => tenantMap[m.tenantId] || m.tenantName || m.tenantId || '—' },
                    { key: 'role', header: 'Role', render: (m) => m.role ? <Pill tone="trust" size="sm">{m.role}</Pill> : '—' },
                    // API returns `isDefault` (UserOrgRole) — the old `isPrimary`
                    // key was always undefined, so Primary never rendered.
                    { key: 'primary', header: 'Primary', render: (m) => (m.isDefault || m.isPrimary) ? <Pill tone="cyan" size="sm">Primary</Pill> : '—' },
                    { key: 'actions', header: '', render: (m) => {
                      const id = m.id || m.membershipId;
                      const isDefault = Boolean(m.isDefault || m.isPrimary);
                      const onlyOne = memberships.length <= 1;
                      if (confirmRemove && confirmRemove === id) {
                        return (
                          <div className="flex items-center gap-1.5 justify-end">
                            <span className="text-[11px] text-v2-ink-muted">Remove?</span>
                            <Button size="xs" variant="danger" disabled={membershipBusy} onClick={() => handleRemoveMembership(m)}>Yes</Button>
                            <Button size="xs" variant="ghost" disabled={membershipBusy} onClick={() => setConfirmRemove(null)}>No</Button>
                          </div>
                        );
                      }
                      return (
                        <div className="flex items-center gap-1.5 justify-end">
                          {!isDefault && (
                            <Button size="xs" variant="ghost" disabled={membershipBusy}
                              onClick={() => handleSetPrimaryMembership(m)}>Set primary</Button>
                          )}
                          <Button size="xs" variant="ghost" disabled={membershipBusy || onlyOne}
                            title={onlyOne ? 'A user must keep at least one membership'
                                   : isDefault ? 'Removing the primary promotes the next membership'
                                   : 'Remove this membership'}
                            onClick={() => setConfirmRemove(id)}>Remove</Button>
                        </div>
                      );
                    }},
                  ]}
                  rows={memberships}
                  rowKey={(m, i) => m.id || m.membershipId || `${m.orgId}:${m.tenantId}:${i}`}
                />
              )}
            </ReviewSection>

            {/* Section: Agent access */}
            <ReviewSection
              title={`Agent access · ${agentAccess.length}`}
              expanded={sections.agents}
              onToggle={() => toggleSection('agents')}
            >
              {agentAccess.length === 0 ? (
                <p className="text-[12.5px] text-v2-ink-muted italic m-0">No agent access for this user.</p>
              ) : (
                <>
                  <Table
                    ariaLabel="Agent access"
                    density="compact"
                    columns={[
                      { key: 'name', header: 'Agent', render: (r) => (
                        <div>
                          <div className="font-semibold text-v2-ink">{r.agent.name}</div>
                          {r.agent.archetype && <div className="text-[10.5px] text-v2-ink-muted">{r.agent.archetype}</div>}
                        </div>
                      )},
                      { key: 'phase', header: 'Phase', render: (r) => (
                        <Pill tone="muted" size="sm">{r.agent.phase || r.agent.lifecycleStage || '—'}</Pill>
                      )},
                      { key: 'access', header: 'Access', render: (r) => (
                        <Pill tone={r.accessLevel === 'admin' ? 'danger' : r.accessLevel === 'manage' ? 'warning' : 'cyan'} size="sm">{r.accessLevel}</Pill>
                      )},
                      { key: 'via', header: 'Via', render: (r) => {
                        if (r.via === 'direct') return <Pill tone="success" size="sm">Direct</Pill>;
                        if (r.via === 'group') return <span className="text-[11px]"><Pill tone="cyan" size="sm">Group</Pill> <span className="text-v2-ink-muted ml-1">{r.groupNames.join(', ')}</span></span>;
                        if (r.via === 'owner') return <Pill tone="action" size="sm">Owner</Pill>;
                        return <Pill tone="muted" size="sm">Role</Pill>;
                      }},
                    ]}
                    rows={visibleAgentAccess}
                    rowKey={(r) => r.agent.id}
                  />
                  {agentAccess.length > pgAgents.pageSize && (
                    <div className="mt-3">
                      <Pagination totalItems={agentAccess.length} page={pgAgents.page} pageSize={pgAgents.pageSize}
                        onPageChange={pgAgents.setPage} onPageSizeChange={pgAgents.setPageSize}
                        noun="agents" />
                    </div>
                  )}
                </>
              )}
            </ReviewSection>

            {/* Section: Workflow access */}
            <ReviewSection
              title={`Workflow access · ${workflowAccess.length}`}
              expanded={sections.workflows}
              onToggle={() => toggleSection('workflows')}
            >
              {workflowAccess.length === 0 ? (
                <p className="text-[12.5px] text-v2-ink-muted italic m-0">No workflow access for this user.</p>
              ) : (
                <>
                  <Table
                    ariaLabel="Workflow access"
                    density="compact"
                    columns={[
                      { key: 'name', header: 'Workflow', render: (r) => (
                        <span className="font-semibold text-v2-ink">{r.workflow?.name || r.workflowId}</span>
                      )},
                      { key: 'access', header: 'Access', render: (r) => (
                        <Pill tone={r.accessLevel === 'admin' ? 'danger' : r.accessLevel === 'manage' ? 'warning' : 'cyan'} size="sm">{r.accessLevel}</Pill>
                      )},
                      { key: 'via', header: 'Via', render: (r) => {
                        if (r.via === 'direct') return <Pill tone="success" size="sm">Direct</Pill>;
                        if (r.via === 'group') return <span className="text-[11px]"><Pill tone="cyan" size="sm">Group</Pill> <span className="text-v2-ink-muted ml-1">{r.groupName || ''}</span></span>;
                        return <Pill tone="muted" size="sm">Role</Pill>;
                      }},
                    ]}
                    rows={visibleWorkflowAccess}
                    rowKey={(r, i) => r.workflowId || i}
                  />
                  {workflowAccess.length > pgWorkflows.pageSize && (
                    <div className="mt-3">
                      <Pagination totalItems={workflowAccess.length} page={pgWorkflows.page} pageSize={pgWorkflows.pageSize}
                        onPageChange={pgWorkflows.setPage} onPageSizeChange={pgWorkflows.setPageSize}
                        noun="workflows" />
                    </div>
                  )}
                </>
              )}
            </ReviewSection>

            {/* Section: Connector groups */}
            <ReviewSection
              title={`Connector groups · ${userConnectorGroups.length}`}
              expanded={sections.connectorGroups}
              onToggle={() => toggleSection('connectorGroups')}
            >
              {userConnectorGroups.length === 0 ? (
                <p className="text-[12.5px] text-v2-ink-muted italic m-0">Not a member of any connector group.</p>
              ) : (
                <Table
                  ariaLabel="Connector group memberships"
                  density="compact"
                  columns={[
                    { key: 'name', header: 'Group', render: (cg) => (
                      <div>
                        <div className="font-semibold text-v2-ink">{cg.name}</div>
                        {cg.description && <div className="text-[10.5px] text-v2-ink-muted">{cg.description}</div>}
                      </div>
                    )},
                    { key: 'connectors', header: 'Connectors', render: (cg) => (
                      <Pill tone="cyan" size="sm">{(cg.connectors || []).length}</Pill>
                    )},
                  ]}
                  rows={userConnectorGroups}
                  rowKey={(cg) => cg.id}
                />
              )}
            </ReviewSection>

            {/* Section: Effective permissions */}
            <ReviewSection
              title={`Effective permissions · ${userRole}`}
              expanded={sections.permissions}
              onToggle={() => toggleSection('permissions')}
            >
              {/* RBAC Phase 4 — SoD flags for this user (if any). */}
              {effectiveAccess?.sodFlags?.length > 0 && (
                <div className="mb-3 rounded border border-v2-border-subtle p-2 space-y-1">
                  <div className="text-[11px] font-bold uppercase tracking-wide text-v2-ink-muted">
                    Segregation-of-duties flags
                  </div>
                  {effectiveAccess.sodFlags.map((c, i) => (
                    <div key={`${c.roleA}-${c.roleB}-${i}`} className="flex items-start gap-2 text-[11.5px]">
                      <Pill tone={c.severity === 'high' ? 'danger' : 'warning'} size="sm">{c.severity}</Pill>
                      <span>
                        <span className="font-mono-v2">{c.roleA}</span> + <span className="font-mono-v2">{c.roleB}</span> — {c.rule}{' '}
                        <span className="text-v2-ink-muted">({c.scopeLabel})</span>
                      </span>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-[11.5px] text-v2-ink-muted m-0 mb-2">
                Derived from the user's role(s), server-authoritative. Hover a permission to see which role grants it.
              </p>
              <div className="flex flex-wrap gap-1.5">
                {effectiveAccess?.permissions?.length
                  ? effectiveAccess.permissions.map((pp, i) => (
                      <span key={i} title={`via ${(pp.viaRoles || []).join(', ')}`}>
                        <Pill tone={pp.permission.startsWith('*') ? 'danger' : 'muted'} size="sm">{pp.permission}</Pill>
                      </span>
                    ))
                  : (((rolePerms && rolePerms[userRole]) || ROLE_PERMS_MAP[userRole] || [])).map((p, i) => (
                      <Pill key={i} tone={p.startsWith('*') ? 'danger' : 'muted'} size="sm">{p}</Pill>
                    ))}
              </div>
            </ReviewSection>
          </div>
        )}
      </div>
    </div>
  );
}

function ReviewSection({ title, expanded, onToggle, children }) {
  return (
    <Card padding="none">
      <button onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-2.5 text-[12.5px] font-semibold text-v2-ink hover:bg-v2-muted transition-colors">
        <span>{title}</span>
        <ChevronRight size={14} className={'text-v2-ink-muted transition-transform ' + (expanded ? 'rotate-90' : '')} />
      </button>
      {expanded && (
        <div className="border-t border-v2-border-subtle p-4">
          {children}
        </div>
      )}
    </Card>
  );
}

function EscalationInventory() {
  const navigate = useNavigate();
  const platform = usePlatform();
  const { addToast } = useToast();
  const { confirm, confirmModal } = useConfirm();

  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);

  /* G359 — V2-native Create/Edit modal state. Replaces the previous
   * `/team?legacy=1&action=esc-create` bounce. `editing` is the policy
   * being edited (null = creating new). */
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [igaOpen, setIgaOpen] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.ccGetEscalationPolicies?.();
      const items = Array.isArray(res?.data?.items) ? res.data.items
                  : Array.isArray(res?.items) ? res.items
                  : Array.isArray(res?.data) ? res.data
                  : Array.isArray(res) ? res : [];
      setPolicies(items);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const openCreate = () => { setEditing(null); setModalOpen(true); };
  const openEdit   = (p) => { setEditing(p);   setModalOpen(true); };
  const closeModal = () => { setModalOpen(false); setEditing(null); };
  const onSaved    = (msg) => { addToast({ tone: 'success', title: msg }); closeModal(); reload(); };

  const [facets, setFacets] = useState(() => ({ source: new Set(), scope: new Set(), status: new Set() }));
  const toggleFacet = (gKey, oKey, c) => setFacets(p => { const n = new Set(p[gKey] || []); c ? n.add(oKey) : n.delete(oKey); return { ...p, [gKey]: n }; });
  const clearFacets = () => setFacets({ source: new Set(), scope: new Set(), status: new Set() });

  const sources = useMemo(() => Array.from(new Set(policies.map(p => p.source || 'manual'))).sort(), [policies]);
  // Facet options come from reach, so the filter can actually match what the column shows. Built
  // from p.scope before, which meant the checkboxes said tenant/org/global while the values behind
  // them were DEFAULT/PRIORITY — every selection matched nothing.
  const scopes = useMemo(() => Array.from(new Set(policies.map(p => reachLabel(p.reach)))).sort(), [policies]);

  // SOURCE and SCOPE are derived from the policies actually loaded, so they only offer values that
  // exist — one MANUAL, org-wide policy yields one option in each. That is intended: a checkbox
  // matching nothing is noise, and this page already shipped a filter whose options could never
  // match (Scope was built from p.scope while the column rendered reach).
  //
  // A previous revision of this block ALSO hid any group with fewer than two options, reasoning
  // that a single-option filter cannot narrow anything. That was a regression: on a one-policy
  // estate every group has one option, so the entire rail vanished and left an empty bordered box
  // — a working control replaced by a rendering artefact. The reasoning was not wrong about a
  // single option being useless; it was wrong to act on it without checking the degenerate case
  // where the rule removes everything. Groups are shown whenever there is anything to show.
  //
  // Status stays a fixed pair. "Inactive 0" is not a dead control, it is the answer to "are there
  // any inactive policies?" — which is exactly what someone opens a filter rail to find out.
  const FACET_GROUPS = useMemo(() => ([
    { key: 'source', label: 'Source', options: sources.map(s => ({ key: s, label: s })) },
    // "Applies to" — matching the column header and the editor. The label still said "Scope" after
    // the values moved to reach, so the rail and the table named the same thing differently.
    { key: 'scope',  label: 'Applies to', options: scopes.map(s => ({ key: s, label: s })) },
    { key: 'status', label: 'Status', options: [
      { key: 'active',   label: 'Active' },
      { key: 'inactive', label: 'Inactive' },
    ]},
  ]), [sources, scopes]);

  const filtered = useMemo(() => policies.filter(p => {
    if (facets.source.size > 0 && !facets.source.has(p.source || 'manual')) return false;
    if (facets.scope.size > 0 && !facets.scope.has(reachLabel(p.reach))) return false;
    if (facets.status.size > 0 && !facets.status.has(isPolicyActive(p) ? 'active' : 'inactive')) return false;
    return true;
  }), [policies, facets]);

  /* G395 — Pagination for the escalation policies table. Mature tenants
   * with IGA sync can have 50-150 policies; without paging the table grows
   * vertically off-screen. Page resets on filter changes. */
  const pgPolicies = usePaginationState(15);
  useEffect(() => { pgPolicies.setPage(0); }, [filtered.length]); // eslint-disable-line

  const facetCounts = useMemo(() => {
    const counts = {};
    for (const g of FACET_GROUPS) {
      counts[g.key] = {};
      for (const opt of g.options) {
        counts[g.key][opt.key] = policies.filter(p => {
          for (const og of FACET_GROUPS) {
            if (og.key === g.key) continue;
            const sel = facets[og.key]; if (!sel || sel.size === 0) continue;
            const v = og.key === 'source' ? (p.source || 'manual')
                    : og.key === 'scope' ? reachLabel(p.reach)
                    : (isPolicyActive(p) ? 'active' : 'inactive');
            if (!sel.has(v)) return false;
          }
          const v = g.key === 'source' ? (p.source || 'manual')
                  : g.key === 'scope' ? reachLabel(p.reach)
                  : (isPolicyActive(p) ? 'active' : 'inactive');
          return v === opt.key;
        }).length;
      }
    }
    return counts;
  }, [policies, facets, FACET_GROUPS]);

  const facetGroupsWithCounts = useMemo(() => FACET_GROUPS.map(g => ({
    ...g, options: g.options.map(o => ({ ...o, count: facetCounts[g.key]?.[o.key] || 0, checked: facets[g.key]?.has(o.key) || false })),
  })), [facetCounts, facets, FACET_GROUPS]);

  const activeFilterChips = useMemo(() => {
    const chips = [];
    for (const g of FACET_GROUPS) for (const optKey of facets[g.key] || []) {
      const opt = g.options.find(o => o.key === optKey);
      chips.push({ groupKey: g.key, optionKey: optKey, label: `${g.label}: ${opt?.label || optKey}` });
    }
    return chips;
  }, [facets, FACET_GROUPS]);

  const [selected, setSelected] = useState(null);
  const [slideoutTab, setSlideoutTab] = useState('overview');
  const openSlideout = (row) => { setSelected(row); setSlideoutTab('overview'); };
  const closeSlideout = () => setSelected(null);

  const handleDeactivate = async (id) => {
    if (!(await confirm({ title: 'Deactivate escalation policy', message: 'Deactivate this escalation policy?', confirmLabel: 'Deactivate' }))) return;
    try { await api.ccDeleteEscalationPolicy?.(id); addToast({ tone: 'info', title: 'Policy deactivated' }); reload(); }
    catch (err) { addToast({ tone: 'danger', title: 'Deactivate failed', description: err?.message }); }
  };

  /* RC-74.13 — match the source values the backend actually writes.
   *
   * EscalationPolicy.Source defines exactly three: MANUAL, EXCEL_IMPORT, IGA_SYNC.
   * These tiles compared against 'iga' / 'IGA' / 'excel', none of which exist, so
   * "From IGA" and "From Excel" were pinned to zero forever — including on this
   * estate, where a real SCIM sync had just produced an IGA_SYNC policy sitting
   * one row below a tile insisting there were none. The SOURCE facet beside them
   * read 1, because it groups on the raw value instead of guessing at it.
   *
   * Normalised and prefix-matched rather than hardcoded to one spelling, so a
   * legacy row written as plain 'IGA' or 'EXCEL' still counts. */
  const kpis = useMemo(() => {
    const sourceOf = (p) => String(p?.source || 'MANUAL').toUpperCase();
    return {
      total:  policies.length,
      active: policies.filter(isPolicyActive).length,
      iga:    policies.filter(p => sourceOf(p).startsWith('IGA')).length,
      excel:  policies.filter(p => sourceOf(p).startsWith('EXCEL')).length,
    };
  }, [policies]);

  return (
    <div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-5">
        <MetricCard label="Total policies"  value={kpis.total} tone="cyan" />
        <MetricCard label="Active"           value={kpis.active} tone="success" />
        <MetricCard label="From IGA"         value={kpis.iga} tone="trust" />
        <MetricCard label="From Excel"       value={kpis.excel} tone="muted" />
      </div>

      {activeFilterChips.length > 0 && (
        <div className="bg-v2-surface border border-v2-border-subtle rounded-v2-md shadow-v2-sm px-4 py-2.5 mb-4 flex items-center gap-2 flex-wrap">
          <span className="text-[11px] uppercase tracking-wider text-v2-ink-muted font-semibold">Filters</span>
          {activeFilterChips.map((c) => (
            <button key={`${c.groupKey}:${c.optionKey}`} onClick={() => toggleFacet(c.groupKey, c.optionKey, false)}
              className="inline-flex items-center gap-1 text-[11.5px] font-medium bg-v2-action-soft text-v2-action-strong rounded-v2-pill px-2.5 py-0.5 hover:bg-v2-action hover:text-white">
              {c.label} ×
            </button>
          ))}
          <button onClick={clearFacets} className="ml-auto text-[11.5px] text-v2-ink-muted hover:text-v2-ink">Clear all</button>
        </div>
      )}

      <div className="flex items-center justify-end gap-2 mb-4">
        <Button variant="ghost" size="sm" iconLeft={<RefreshCw size={13} />} onClick={reload}>Refresh</Button>
        {/* G362 — Import/Sync are queued — the V2 file-upload widget
            and IGA-connector picker haven't been ported yet. Surface
            them disabled with a tooltip rather than bouncing operators
            to V1 (the explicit anti-pattern user flagged). */}
        <Button variant="secondary" size="sm" disabled
          title="Import from Excel — not yet available">
          Import from Excel
        </Button>
        <Button variant="secondary" size="sm" iconLeft={<RefreshCw size={13} />}
          onClick={() => setIgaOpen(true)}
          title="Pull users, managers and escalation chains from your IGA's SCIM directory">
          Sync from IGA
        </Button>
        <Button variant="primary" size="sm" iconLeft={<Plus size={13} />} onClick={openCreate}>Create Policy</Button>
      </div>

      <div className="grid gap-5 grid-cols-1 lg:grid-cols-[240px_1fr]">
        <FacetRail groups={facetGroupsWithCounts} onToggle={toggleFacet} ariaLabel="Escalation filters" />

        {filtered.length === 0 && !loading ? (
          <EmptyState
            icon={<AlertTriangle size={20} />}
            title={policies.length === 0 ? 'No escalation policies yet' : 'No policies match filters'}
            description={policies.length === 0
              ? 'Escalation policies route approvals when SLA is missed. Create one manually, import from Excel, or sync from your IGA.'
              : 'Adjust filters or clear them to see more.'}
            primaryAction={policies.length === 0
              ? <Button variant="primary" iconLeft={<Plus />} onClick={openCreate}>Create Policy</Button>
              : <Button variant="primary" onClick={clearFacets}>Clear filters</Button>}
          />
        ) : (
          <Table
            ariaLabel="Escalation policies"
            columns={[
              { key: 'name', header: 'Policy', sortable: true, render: (p) => (
                <div>
                  <div className="font-semibold text-v2-ink">{p.name}</div>
                  <div className="text-[10.5px] text-v2-ink-muted">{p.description?.slice(0, 60) || '—'}</div>
                </div>
              )},
              // Was `p.scope || 'tenant'` — the backend's task-MATCHING enum (DEFAULT/PRIORITY/…)
              // rendered under a heading whose filter offered tenant/org/global, with a fabricated
              // "tenant" for anything unset. Three vocabularies in one column.
              { key: 'scope', header: 'Applies to', render: (p) => <Pill tone="muted" size="sm">{reachLabel(p.reach)}</Pill> },
              { key: 'source', header: 'Source', render: (p) => <Pill tone={p.source === 'iga' || p.source === 'IGA' ? 'trust' : 'muted'} size="sm">{p.source || 'manual'}</Pill> },
              { key: 'tiers', header: 'Tiers', align: 'right', render: (p) => (
                <span className="tabular-nums text-[12px]">{(p.tiers || []).length}</span>
              )},
              { key: 'status', header: 'Status', render: (p) => (
                <Pill tone={isPolicyActive(p) ? 'success' : 'muted'} size="sm" dot>{isPolicyActive(p) ? 'active' : 'inactive'}</Pill>
              )},
              { key: 'created', header: 'Created', align: 'right', render: (p) => (
                <span className="font-mono-v2 text-[11px] text-v2-ink-muted">{p.createdAt ? new Date(p.createdAt).toLocaleDateString() : '—'}</span>
              )},
              { key: 'actions', header: '', align: 'right', render: (p) => (
                <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                  <Button variant="ghost" size="sm" onClick={() => openEdit(p)} title="Edit"><Edit2 size={13} /></Button>
                  {isPolicyActive(p) && (
                    <Button variant="ghost" size="sm" className="!text-v2-danger hover:!bg-v2-danger-soft"
                      onClick={() => handleDeactivate(p.id)} title="Deactivate"><Trash2 size={13} /></Button>
                  )}
                </div>
              )},
            ]}
            rows={pgPolicies.paginate(filtered)}
            rowKey={(p) => p.id}
            onRowClick={openSlideout}
          />
        )}
        {filtered.length > pgPolicies.pageSize && (
          <div className="mt-3">
            <Pagination
              totalItems={filtered.length}
              page={pgPolicies.page}
              pageSize={pgPolicies.pageSize}
              onPageChange={pgPolicies.setPage}
              onPageSizeChange={pgPolicies.setPageSize}
              noun="policies"
            />
          </div>
        )}
      </div>

      <Slideout
        isOpen={!!selected}
        onClose={closeSlideout}
        ariaLabel={selected ? `${selected.name} detail` : 'Policy detail'}
        width={520}
        tabs={[
          { id: 'overview', label: 'Overview' },
          { id: 'tiers',    label: 'Tiers', badge: (selected?.tiers || []).length || undefined },
          { id: 'history',  label: 'Recent escalations' },
        ]}
        activeTabId={slideoutTab}
        onTabChange={setSlideoutTab}
        header={selected && (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Pill tone={selected.active === false ? 'muted' : 'success'} size="sm" dot>{selected.active === false ? 'inactive' : 'active'}</Pill>
              <Pill tone="muted" size="sm">{reachLabel(selected.reach)}</Pill>
              <Pill tone={selected.source === 'iga' || selected.source === 'IGA' ? 'trust' : 'muted'} size="sm">{selected.source || 'manual'}</Pill>
            </div>
            <h2 className="text-[18px] font-bold tracking-tight m-0">{selected.name}</h2>
            <p className="text-[12px] text-v2-ink-secondary">{selected.description || 'No description'}</p>
            <div className="flex items-center gap-2 mt-3">
              <Button variant="primary" size="sm" iconLeft={<Edit2 size={13} />} onClick={() => { closeSlideout(); openEdit(selected); }}>Edit</Button>
              {selected.active !== false && (
                <Button variant="ghost" size="sm" className="!text-v2-danger" iconLeft={<Trash2 size={13} />} onClick={() => handleDeactivate(selected.id)}>Deactivate</Button>
              )}
            </div>
          </div>
        )}
      >
        {selected && slideoutTab === 'overview' && (
          <Card padding="sm">
            <Card.Header title="Policy overview" />
            <Card.Body>
              <dl className="grid grid-cols-2 gap-3 text-[12.5px]">
                <div className="col-span-2"><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Description</dt><dd className="text-v2-ink mt-0.5">{selected.description || '—'}</dd></div>
                <div><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Applies to</dt><dd className="text-v2-ink mt-0.5">{reachLabel(selected.reach)}</dd></div>
                <div><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Source</dt><dd className="text-v2-ink mt-0.5">{selected.source || 'manual'}</dd></div>
                <div><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Created by</dt><dd className="text-v2-ink mt-0.5">{selected.createdBy || '—'}</dd></div>
                <div><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Created</dt><dd className="text-v2-ink mt-0.5">{selected.createdAt ? new Date(selected.createdAt).toLocaleString() : '—'}</dd></div>
              </dl>
            </Card.Body>
          </Card>
        )}
        {selected && slideoutTab === 'tiers' && (
          <Card padding="sm">
            <Card.Header title="Escalation tiers" />
            <Card.Body>
              {(selected.tiers || []).length === 0 ? (
                <p className="text-v2-ink-muted text-[12.5px]">No tiers defined.</p>
              ) : (
                <ol className="space-y-2 text-[12.5px]">
                  {tiersFromBackend(selected.tiers).map((t, i) => (
                    <li key={i} className="border-l-2 border-v2-border-subtle pl-3">
                      <div className="font-semibold text-v2-ink">Tier {i + 1}: {t.name || `Level ${i + 1}`}</div>
                      <div className="text-[11.5px] text-v2-ink-muted">
                        SLA: {t.slaMinutes} min · {t.approvers.length} {t.approvers.length === 1 ? 'person' : 'people'} contacted
                      </div>
                    </li>
                  ))}
                </ol>
              )}
            </Card.Body>
          </Card>
        )}
        {selected && slideoutTab === 'history' && (
          <Card padding="sm">
            <Card.Header title="Recent escalations" />
            <Card.Body>
              <p className="text-v2-ink-muted text-[12.5px]">Escalation history will surface here when this policy triggers an SLA miss.</p>
            </Card.Body>
          </Card>
        )}
      </Slideout>

      {/* G359 — V2-native Create/Edit modal (was a legacy=1 bounce). */}
      <EscalationPolicyModal
        isOpen={modalOpen}
        onClose={closeModal}
        policy={editing}
        onSaved={onSaved}
      />

      <IgaSyncSlideout
        isOpen={igaOpen}
        onClose={() => setIgaOpen(false)}
        onSynced={reload}
      />
      {confirmModal}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════
 *  EscalationPolicyModal (G359)
 *
 *  V2-native Create/Edit modal with a tier-array editor. Replaces the
 *  `/team?legacy=1&action=esc-create` bounce that V2 used to do.
 *
 *  Fields:
 *    - name            (required)
 *    - description     (textarea)
 *    - reach           (Applies to: this tenant / whole organization / whole customer — V346)
 *    - active          (checkbox; defaults true)
 *    - tiers[]         each: { name, slaMinutes, approvers[] } where approvers is a list of
 *                      PICKED PEOPLE — { id, name, email, mode, hasManager, managerName } — chosen
 *                      through ApproverPicker (V347). It was a comma-separated free-text box; the
 *                      note here promised "a real user/group picker once we surface that
 *                      primitive", and that is what this now is. Text never worked: a USER target
 *                      must be a user id or resolveTargetFromTier falls through to the org-admin
 *                      fallback, so every approver ever typed paged the org admin.
 *                      `mode` is USER (contact them) or MANAGER_OF (contact their manager,
 *                      resolved from app_users.manager_id when the escalation fires).
 *
 *  Save calls api.ccUpdateEscalationPolicy when policy.id is present,
 *  otherwise api.ccCreateEscalationPolicy.
 * ──────────────────────────────────────────────────────────────────────── */
/**
 * Rebuild the editor's tier rows from what the backend actually stores.
 *
 * THE BUG THIS FIXES
 * ------------------
 * The form models a tier as { name, slaMinutes, approvers } — one row, many approvers. The backend
 * stores one object PER APPROVER: { tier, name, targetType, targetValue, slaHours }. Nothing
 * translated between them on the way in. The load path read `t.slaMinutes ?? t.timeoutMinutes ?? 60`
 * and `t.approvers` — none of which exist on a stored tier — so opening ANY saved policy for edit
 * showed every SLA reset to 60 minutes and every approver list empty. Saving from there wrote those
 * blanks back, or wrote zero tiers and was rejected outright. Editing a policy destroyed it.
 *
 * So this is not the rounding defect it was filed as. Rounding minutes to hours loses precision;
 * this lost the entire configuration on every edit.
 *
 * Regrouping is by the `tier` number, which is what makes several stored rows one editor row again.
 * The " · N" suffix save adds when a tier has multiple approvers is stripped so a round trip does
 * not accumulate suffixes. ROLE targets get their leading "@" back, because that is the marker the
 * save path uses to infer targetType — without it, re-saving would silently reclassify a ROLE
 * approver as a USER.
 */
/**
 * Operator-facing name for a policy's reach. Falls back to the ORG wording rather than inventing a
 * value, because V346 backfilled every pre-existing policy to ORG — so an absent reach genuinely
 * means org-wide here, and saying "tenant" (as the old column did) would have been a fresh lie.
 */
/**
 * Is this escalation policy active?
 *
 * The whole Escalation section read `p.active`. The API sends `isActive` — the entity field is
 * `Boolean isActive`, Lombok generates `getIsActive()`, Jackson names the property `isActive`. So
 * `p.active` was ALWAYS undefined, and `undefined === false` is false, which means every policy
 * rendered as active regardless of its real state: the status pill, the ACTIVE metric card and the
 * status filter could none of them ever show an inactive policy.
 *
 * It read correct on this estate only because the single policy genuinely is active — the failure
 * mode is invisible until someone deactivates one, and then the list quietly disagrees with the
 * database. The editor was fixed earlier; the list was not, which is how one field ended up spelled
 * two ways in one file.
 *
 * `active` is still accepted as a fallback so any caller that does send it keeps working.
 */
function isPolicyActive(p) {
  if (p == null) return true;
  if (p.isActive != null) return p.isActive !== false;
  return p.active !== false;
}

/**
 * How much of the estate a policy covers, in words.
 *
 * The `default:` branch used to return "Whole organization", which meant any value this build did
 * not recognise was displayed — on a governance surface, to an auditor — as a confident claim of
 * org-wide reach. That is the one answer it must never guess, and the damage was not only cosmetic:
 * this function also supplies the "Applies to" FACET, so an unrecognised policy was filed under
 * "Whole organization" and swept up by a filter for org-wide policies it may have nothing to do
 * with.
 *
 * An ABSENT value is different from an unrecognised one and is still ORG: the column is NOT NULL
 * with an ORG default (V346), and EscalationPolicy.reach initialises to Reach.ORG, so nothing
 * arriving without a reach was ever anything else.
 *
 * Anything genuinely unrecognised is named as such — matching EscalationPolicy.Reach.specificity,
 * which deliberately ranks an unknown reach last rather than folding it into a known one. The raw
 * value is shown because "which one is it" is the operator's next question; React escapes it, and
 * V346's CHECK constrains it to TENANT | ORG | GLOBAL, so this is a defensive path rather than an
 * expected one.
 */
function reachLabel(reach) {
  const raw = (reach == null || String(reach).trim() === '')
    ? 'ORG'
    : String(reach).trim().toUpperCase();
  switch (raw) {
    case 'TENANT': return 'This tenant';
    case 'ORG':    return 'Whole organization';
    case 'GLOBAL': return 'Whole customer';
    default:       return `Unrecognised scope (${raw.slice(0, 24)})`;
  }
}

/**
 * Is this stored targetValue actually a user id?
 *
 * Tiers authored before the approver picker stored FREE TEXT here — the live policy on this
 * platform stores "IGA Synch Required". Such a value can never resolve to a person: it is not a
 * user id, so EscalationService.resolveTargetFromTier falls through to the org-admin fallback, and
 * POSTing it to /approvers/resolve made that endpoint 500 on a UUID parse. Telling the two apart
 * is a regex, and doing so is what lets the editor say something TRUE about each one.
 */
const APPROVER_ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function tiersFromBackend(tiers) {
  if (!Array.isArray(tiers) || tiers.length === 0) return [];
  const byTier = new Map();
  tiers.forEach((t, i) => {
    const key = Number(t?.tier ?? i + 1);
    if (!byTier.has(key)) byTier.set(key, []);
    byTier.get(key).push(t);
  });
  return [...byTier.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([, rows]) => {
      const first = rows[0] || {};
      // Minutes if present, else the legacy hours field. A policy written before this change has
      // only slaHours, and must keep the value its author chose rather than snapping to a default.
      const minutes = first.slaMinutes != null
        ? Number(first.slaMinutes)
        : (first.slaHours != null ? Number(first.slaHours) * 60 : 60);
      return {
        name: String(first.name || '').replace(/\s*·\s*\d+$/, '').trim(),
        slaMinutes: Number.isFinite(minutes) && minutes > 0 ? minutes : 60,
        // Ids only. Display names are resolved fresh from the directory after load — never
        // denormalised into the policy, because a name copied at save time is wrong the day
        // somebody marries, changes team or leaves, and the policy would keep showing the stale
        // one with nothing to signal it.
        approvers: rows
          .filter((r) => r.targetValue)
          .map((r) => ({
            id: r.targetValue,
            name: r.targetValue,           // placeholder until resolveApprovers returns
            email: null,
            mode: r.targetType === 'MANAGER_OF' ? 'MANAGER_OF' : 'USER',
            hasManager: r.targetType === 'MANAGER_OF',
            managerName: null,
            unresolved: true,
            // A legacy free-text entry is not "someone you cannot see" — it is not a person at
            // all, and it never paged anyone but the org admin. The editor has to say which of
            // those two it is looking at, because the fix differs: one is a permissions question,
            // the other is a broken policy that needs a real person chosen.
            legacy: !APPROVER_ID_RE.test(String(r.targetValue || '')),
          })),
      };
    });
}

/**
 * IgaSyncSlideout — pull people, managers and escalation chains from an IGA's SCIM directory.
 *
 * WHY THIS EXISTS
 * ---------------
 * The button that opens this was `disabled` with the tooltip "Sync from IGA — not yet available",
 * and had been since the V2 page shipped. The capability was NOT missing: ScimService
 * .syncFromScimEndpoint pulls SCIM /Users with real pagination, reconciles departures, writes the
 * V347 directory attributes and then calls autoGenerateEscalationChains to build tiers from the
 * manager hierarchy — and POST /compliance-center/escalation-policies/scim-sync/{connectorId} has
 * exposed it the whole time. The V1 Team page even has a working affordance for it. V2 shipped a
 * dead placeholder over a working feature one page over, which reads to an operator as "IGA sync
 * is broken" when the truth is "nothing on this screen ever called it".
 *
 * So this adds no backend and no new endpoint. It calls the service that already exists, through
 * the api client function that already exists (ccTriggerScimSync), and picks the connector with
 * the EntityPicker that already exists.
 *
 * THE NO-CONNECTOR CASE IS THE POINT, NOT AN EDGE
 * -----------------------------------------------
 * On an estate with no IGA connected — which is this one — every person in the approver picker is
 * a local account and every "contact their manager" is disabled, because nothing has ever written
 * app_users.manager_id. Until now the UI said nothing about why. An operator looking at four local
 * users and a greyed-out manager toggle has no way to tell "no directory is connected" from "the
 * sync is broken", and those need completely different actions.
 *
 * So the empty case gets a real explanation and a route to the fix, rather than an empty dropdown.
 */
function IgaSyncSlideout({ isOpen, onClose, onSynced }) {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [connectors, setConnectors] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    setResult(null); setError(null); setSelectedId(null); setLoading(true);
    (async () => {
      try {
        const res = await api.getConnectors({});
        // api.getConnectors unwraps the sharing envelope and always returns an array.
        const flat = (Array.isArray(res?.data) ? res.data : []).filter(Boolean);
        // A connector can drive the pull only if it has a SCIM endpoint to pull FROM. Direction is
        // shown rather than filtered on: scim_sync_direction defaults to INBOUND, so filtering by
        // it would hide the very connector an operator just configured, and the backend validates
        // properly anyway.
        setConnectors(flat.filter((c) => String(c.scimEndpointUrl || '').trim().length > 0));
      } catch {
        // A failed lookup must not render as "no connectors" — that reads as a setup problem and
        // sends someone off to create a connector they already have.
        setError('We could not read your connectors just now. Try again in a moment.');
        setConnectors([]);
      } finally { setLoading(false); }
    })();
  }, [isOpen]);

  const runSync = async () => {
    if (!selectedId) return;
    setSyncing(true); setError(null); setResult(null);
    try {
      const res = await api.ccTriggerScimSync(selectedId);
      /* apiFetch resolves successfully on a non-2xx and reports it as {ok:false}. Celebrating
         without inspecting res.ok is how the escalation create path used to toast "saved" over a
         rejection. */
      if (res && res.ok === false) {
        const msg = res.error?.message || res.error || 'Sync failed';
        throw new Error(typeof msg === 'string' ? msg : 'Sync failed');
      }
      const body = res?.data ?? res;
      setResult(body || {});
      /* A sync that pulled an entire unscoped directory succeeded, but calling it a plain success
         buries the one fact the operator needs to act on. The backend states the warning; the
         toast changes tone and points at the panel that carries it in full. */
      addToast({
        tone: body?.scopeWarning ? 'warning' : 'success',
        title: 'Directory sync finished',
        description: body?.scopeWarning
          ? `${body?.created ?? 0} added, ${body?.updated ?? 0} updated — pulled from the whole directory. See Sync result.`
          : `${body?.created ?? 0} added, ${body?.updated ?? 0} updated.`,
      });
      onSynced?.();
    } catch (err) {
      setError(err?.message || 'Sync failed');
      addToast({ tone: 'danger', title: 'Directory sync failed', description: err?.message });
    } finally { setSyncing(false); }
  };

  const selected = connectors.find((c) => c.id === selectedId) || null;

  return (
    <Slideout
      isOpen={isOpen}
      onClose={onClose}
      width={560}
      ariaLabel="Sync escalation directory from IGA"
      header={
        <div>
          <div className="flex items-center gap-2 mb-2">
            <RefreshCw size={14} className="text-v2-action-strong" />
            <Pill tone="muted" size="sm">directory</Pill>
          </div>
          <h2 className="text-[16px] font-bold m-0">Sync from IGA</h2>
          <p className="text-[12px] text-v2-ink-secondary mt-1">
            Pulls people and their managers from your identity governance system, so escalation
            policies can name real approvers and route to a manager when one is needed.
          </p>
        </div>
      }
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={syncing}>Close</Button>
          {connectors.length > 0 && (
            <Button
              variant="primary"
              onClick={runSync}
              disabled={!selectedId || syncing}
              iconLeft={syncing ? null : <RefreshCw size={13} />}
            >
              {syncing ? 'Syncing…' : 'Run sync'}
            </Button>
          )}
        </div>
      }
    >
      <div className="space-y-4">
        {error && (
          <div className="rounded-v2-md border border-v2-danger bg-v2-danger-soft px-3 py-2 text-[12.5px] text-v2-danger">
            {error}
          </div>
        )}

        {loading ? (
          <p className="text-[12.5px] text-v2-ink-muted">Looking for connected directories…</p>
        ) : connectors.length === 0 ? (
          <EmptyState
            icon={<AlertTriangle size={20} />}
            title="No identity directory is connected yet"
            description={
              'Escalation can only name real people once a directory tells us who they are and who '
              + 'they report to. Nothing has synced into this organization, which is why everyone in '
              + 'the approver picker is marked Local and why "contact their manager" is unavailable — '
              // The vocabulary is V1's (TeamPage's IGA system selector), which is the functional
              // source of truth per Rule 1 — and it is the domain-correct list. Okta Directory and
              // Entra ID are authentication and SSO products; identity GOVERNANCE is SailPoint,
              // Saviynt, CyberArk, One Identity, and Okta's separate Identity Governance product.
              // Naming an SSO product as an IGA sends an operator to configure the wrong system.
              + 'no manager is on file for anyone. Connect an identity governance system — SailPoint, '
              + 'Saviynt, CyberArk, Okta Identity Governance or One Identity — and give it a SCIM '
              + 'endpoint URL and bearer token, then come back and run the sync.'
            }
            primaryAction={
              <Button variant="primary" iconLeft={<ExternalLink size={13} />} onClick={() => navigate('/connectors')}>
                Set up a connector
              </Button>
            }
          />
        ) : (
          <>
            <div>
              <span className="block text-[10.5px] uppercase tracking-wider text-v2-ink-muted font-semibold mb-1">
                Directory to pull from
              </span>
              <EntityPicker
                items={connectors}
                value={selectedId}
                onChange={setSelectedId}
                disabled={syncing}
                ariaLabel="Choose an IGA connector"
                placeholder="Choose a connector"
                searchPlaceholder="Search connectors"
                getId={(c) => c.id}
                getLabel={(c) => c.name || c.type || c.id}
                getBadge={(c) => c.type || null}
                getSublabel={(c) => [
                  c.scimEndpointUrl,
                  c.scimLastSyncAt ? `last synced ${new Date(c.scimLastSyncAt).toLocaleString()}` : 'never synced',
                ].filter(Boolean).join(' · ')}
                emptyMessage={(q) => (q
                  ? `No connector matches “${q}”.`
                  : 'No connector has a SCIM endpoint configured.')}
              />
            </div>

            {selected && (
              <p className="text-[12px] text-v2-ink-secondary">
                This reads people from {selected.name}, updates who reports to whom, and builds
                escalation tiers from that manager chain. It adds and updates people; it never
                deletes anyone. If the directory cannot be read all the way to the end, departures
                are left alone rather than guessed at.
              </p>
            )}

            {result && (
              <Card padding="sm">
                <Card.Header title="Sync result" />
                <Card.Body>
                  {/* Scope first: how many people arrived matters less than whether anyone chose
                      which people they should be. The backend writes this sentence for this
                      reader and it already names the remedy. */}
                  {result.scopeWarning && (
                    <p className="mb-3 text-[12px] text-v2-warning">{result.scopeWarning}</p>
                  )}
                  <dl className="grid grid-cols-2 gap-2 text-[12.5px]">
                    {[
                      ['Directory scope', {
                        FILTERED: 'Filtered',
                        FULL_DIRECTORY: 'Whole directory (chosen)',
                        UNSCOPED: 'Whole directory (not scoped)',
                      }[result.directoryScope] || 'Whole directory (not scoped)'],
                      ['People seen', result.total],
                      ['Added', result.created],
                      ['Updated', result.updated],
                      ['Marked as departed', result.deactivated],
                      ['Escalation chains built', result.escalationChainsGenerated],
                      /* The closure: people fetched because the hierarchy needed them even though
                         the pull did not select them, and the references still dangling after it. */
                      ['Managers pulled in', result.managersFetchedBeyondScope],
                      ['Manager links missing', result.managersUnresolved],
                      ['Pages fetched', result.pagesFetched],
                      ['Failed', result.failed],
                    ].map(([label, value]) => (
                      <div key={label}>
                        <dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">{label}</dt>
                        <dd className="text-v2-ink mt-0.5">{value ?? 0}</dd>
                      </div>
                    ))}
                  </dl>
                  {result.directoryScope === 'FILTERED' && (
                    <p className="mt-3 text-[12px] text-v2-ink-secondary">
                      Nobody is marked as departed on a filtered sync. Someone the filter does not
                      select has not left the company — they were simply not asked for — so this
                      run adds and updates people and leaves departures to the leaver process.
                    </p>
                  )}
                  {result.managersUnresolved > 0 && (
                    <p className="mt-3 text-[12px] text-v2-ink-secondary">
                      {result.managersUnresolved} person(s) name a manager this run could not
                      reach, even after following the reporting line {result.managerDepth ?? 0}
                      {' '}level(s) above the people it pulled. Their escalation chains stop short.
                      Raise Manager depth on this connector's directory scope, or widen the filter.
                    </p>
                  )}
                  {result.completeEnumeration === false && (
                    <p className="mt-3 text-[12px] text-v2-warning">
                      We could not read your directory all the way to the end, so nobody was marked
                      as departed on this run. Everyone we did read has been added or updated. Run
                      it again, and if it keeps stopping short, check the connector's SCIM endpoint.
                    </p>
                  )}
                </Card.Body>
              </Card>
            )}
          </>
        )}
      </div>
    </Slideout>
  );
}

function EscalationPolicyModal({ isOpen, onClose, policy, onSaved }) {
  const { addToast } = useToast();
  const isEdit = !!policy?.id;
  const [form, setForm] = useState(() => ({
    name: '', description: '', reach: 'TENANT', active: true, tiers: [],
  }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  /* Reset form whenever the modal opens (either fresh or with a policy). */
  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    if (policy) {
      const initialTiers = tiersFromBackend(policy.tiers);
      setForm({
        name: policy.name || '',
        description: policy.description || '',
        reach: (policy.reach || 'ORG').toUpperCase(),
        active: policy.isActive !== false,
        tiers: initialTiers,
      });
      // Turn the stored ids into people. Ids the caller may not see simply do not come back, and
      // stay flagged unresolved on screen — an honest "this policy names somebody you cannot be
      // shown" rather than a blank row that looks like corruption.
      // Legacy free-text entries are deliberately NOT sent. They cannot resolve by construction,
      // and sending them is what made /approvers/resolve return 500 on every open of a legacy
      // policy — three opens, three 500s, confirmed against the running stack.
      const ids = initialTiers.flatMap((t) => t.approvers.filter((a) => !a.legacy).map((a) => a.id));
      if (ids.length) {
        api.resolveApprovers(ids).then((res) => {
          const rowsById = new Map(((res?.data ?? res) || []).map((r) => [r.id, r]));
          setForm((f) => ({
            ...f,
            tiers: f.tiers.map((t) => ({
              ...t,
              approvers: t.approvers.map((a) => {
                const found = rowsById.get(a.id);
                return found
                  ? { ...a, name: found.name, email: found.email, hasManager: !!found.hasManager,
                      managerName: found.managerName || null, unresolved: false }
                  : a;
              }),
            })),
          }));
        }).catch(() => { /* leave them flagged unresolved — never silently blank a policy */ });
      }
    } else {
      setForm({ name: '', description: '', reach: 'TENANT', active: true, tiers: [] });
    }
  }, [isOpen, policy]);

  // approvers is a LIST of chosen people ({ id, name, email, mode, hasManager, managerName }),
  // which is what ApproverPicker renders and what handleSave maps into backend tiers. It was
  // seeded as '' — the scalar the free-text box used to hold — so every click of "Add tier" fed
  // a string into a component that maps over its value, and the resulting throw inside useMemo
  // took the whole page to the error boundary.
  const addTier = () => setForm(f => ({ ...f, tiers: [...f.tiers, { name: '', slaMinutes: 60, approvers: [] }] }));
  const removeTier = (idx) => setForm(f => ({ ...f, tiers: f.tiers.filter((_, i) => i !== idx) }));
  const moveTier = (idx, dir) => setForm(f => {
    const next = f.tiers.slice();
    const j = idx + dir;
    if (j < 0 || j >= next.length) return f;
    [next[idx], next[j]] = [next[j], next[idx]];
    return { ...f, tiers: next };
  });
  const updateTier = (idx, key, value) => setForm(f => ({
    ...f,
    tiers: f.tiers.map((t, i) => i === idx ? { ...t, [key]: value } : t),
  }));

  /* G375 — Validation gates the Save button so we never submit a payload
   * the backend will reject. Each tier must have at least one approver
   * (because backend requires per-tier targetType+targetValue), and the
   * policy needs at least one tier. */
  const tierValidationError = useMemo(() => {
    if (form.tiers.length === 0) return null; /* allow no-tier creation, backend will reject; surfaced as create error */
    for (let i = 0; i < form.tiers.length; i++) {
      const t = form.tiers[i];
      // Approvers are picked people, not typed text. This still read
      // `String(t.approvers).split(',')` from the free-text era: on a real selection that
      // stringifies to "[object Object]", which is non-empty, so the gate passed for the wrong
      // reason — and it would have passed a list of entries carrying no id at all, which
      // handleSave filters out, producing a policy saved with zero backend tiers.
      const approvers = Array.isArray(t.approvers) ? t.approvers.filter((a) => a && a.id) : [];
      // A legacy text entry has an `id`, so it counts as an approver to every check that only
      // looks for one — and saving would write it straight back, keeping the tier pointed at
      // nobody. Saving an untouched legacy policy is how this survived: someone edits the name,
      // hits save, and the broken approver rides along. Blocking the save is what makes the
      // problem visible at the one moment an operator is in a position to fix it.
      const legacy = approvers.filter((a) => a.legacy);
      if (legacy.length > 0) {
        // Short and actionable. The long version explained the entry's history, which is not
        // something an operator can act on — they can remove a row and pick a person.
        return `Tier ${i + 1} ("${t.name?.trim() || 'unnamed'}") has an invalid approver `
          + `(“${legacy[0].name}”). Remove it and choose a person.`;
      }
      if (approvers.length === 0) return `Tier ${i + 1} ("${t.name?.trim() || 'unnamed'}") needs at least one person to contact.`;
      const slaMinutes = Number(t.slaMinutes);
      if (!Number.isFinite(slaMinutes) || slaMinutes < 1) return `Tier ${i + 1} ("${t.name?.trim() || 'unnamed'}") needs an SLA of at least 1 minute.`;
    }
    return null;
  }, [form.tiers]);
  const canSave = form.name.trim().length > 0 && !saving && !tierValidationError;

  const handleSave = async () => {
    setSaving(true); setError(null);
    try {
      /* G375 — Translate the frontend's tier shape ({slaMinutes, approvers[]})
       * to the backend's required shape ({slaHours, targetType, targetValue}).
       *
       * Backend EscalationService.validateTiers requires each tier to carry
       *   { name, targetType: 'ROLE'|'USER'|'GROUP', targetValue, slaHours }
       * The V2 UI exposes a simpler "free-text approvers list" model, so
       * each approver entry becomes its own backend tier (one approver per
       * tier — backend semantics don't support multiple approvers per tier).
       * Suffix the tier name to disambiguate when one UI tier expands to
       * multiple backend tiers.
       *
       * targetType is no longer GUESSED. It used to be inferred from the shape of
       * a typed string — email → USER, @ or ALLCAPS → ROLE, otherwise USER — and
       * that heuristic was the bug: resolveTargetFromTier requires a USER target
       * to be a user id, so an inferred USER carrying an email or a name parsed
       * as nothing and fell through to the org-admin fallback. Every approver
       * typed into this form paged the org admin instead of the person named.
       * The picker now supplies an id and an explicit mode (them / their manager).
       *
       * reach (V346) carries how far the policy applies; scope stays DEFAULT
       * because this form offers no task-matching control. */
      const backendTiers = form.tiers.flatMap((t, tierIdx) => {
        const baseName = t.name?.trim() || `Tier ${tierIdx + 1}`;
        // Approvers are chosen people now, not typed text: each carries the id resolution needs
        // and whether to contact them or their manager.
        const approvers = Array.isArray(t.approvers) ? t.approvers.filter((a) => a && a.id) : [];
        // Minutes are what the operator typed and what we persist. slaHours is written alongside
        // as a DERIVED mirror purely so the Excel export and any pre-V346 reader keep working —
        // nothing in the backend computes a deadline from either field (RemediationSLAConfigService
        // owns deadlines), so this is fidelity, not behaviour. Rounding up means a 30-minute tier
        // never mirrors as 0 hours, which would read as "no SLA".
        const slaMinutes = Math.max(1, Number(t.slaMinutes) || 60);
        const slaHours = Math.max(1, Math.round(slaMinutes / 60));
        return approvers.map((approver, approverIdx) => ({
          tier: tierIdx + 1,
          name: approvers.length === 1 ? baseName : `${baseName} · ${approverIdx + 1}`,
          // MANAGER_OF resolves app_users.manager_id when the escalation fires, so a change of
          // manager re-routes without anyone re-saving this policy.
          targetType: approver.mode === 'MANAGER_OF' ? 'MANAGER_OF' : 'USER',
          targetValue: approver.id,
          slaMinutes,
          slaHours,
        }));
      });
      const payload = {
        name: form.name.trim(),
        description: form.description.trim() || null,
        // reach = how far this policy applies (V346). Distinct from the backend's `scope`, which
        // decides which TASKS it matches — the two were conflated, and this form's dropdown was
        // dropped on save for as long as it existed. `scope` stays DEFAULT here because this form
        // offers no task-matching control; PRIORITY/FRAMEWORK policies come from Excel/IGA import.
        reach: form.reach,
        scope: 'DEFAULT',
        isActive: form.active,
        tiers: backendTiers,
      };

      /* G375 — apiFetch returns {ok:false, error} on non-2xx; the await
       * resolves successfully so we MUST inspect res.ok before celebrating.
       * Earlier the success toast fired regardless of backend rejection. */
      const res = isEdit
        ? await api.ccUpdateEscalationPolicy?.(policy.id, payload)
        : await api.ccCreateEscalationPolicy?.(payload);
      if (res && res.ok === false) {
        const msg = res.error?.message || res.error || (isEdit ? 'Update failed' : 'Create failed');
        throw new Error(typeof msg === 'string' ? msg : (isEdit ? 'Update failed' : 'Create failed'));
      }
      onSaved(isEdit ? 'Policy updated' : 'Policy created');
    } catch (err) {
      setError(err?.message || 'Save failed');
      addToast({ tone: 'danger', title: isEdit ? 'Update failed' : 'Create failed', description: err?.message });
    } finally { setSaving(false); }
  };

  return (
    /* G378 — Modal → Slideout. Escalation tiers are best edited in a tall
       right-rail panel — modals were getting taller than the viewport on
       multi-tier policies. */
    <Slideout
      isOpen={isOpen}
      onClose={onClose}
      width={620}
      ariaLabel={isEdit ? `Edit escalation policy ${policy?.name}` : 'Create escalation policy'}
      header={
        <div>
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle size={14} className="text-v2-warning-strong" />
            <Pill tone="muted" size="sm">{isEdit ? 'edit' : 'new'}</Pill>
          </div>
          <h2 className="text-[16px] font-bold m-0">{isEdit ? `Edit policy — ${policy?.name}` : 'Create escalation policy'}</h2>
          <p className="text-[12px] text-v2-ink-secondary mt-1">Define escalation tiers: each tier maps approvers to an SLA timeout.</p>
        </div>
      }
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="primary" onClick={handleSave} disabled={!canSave} iconLeft={saving ? null : <CheckCircle2 size={13} />}>
            {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create policy'}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {error && (
          <div className="rounded-v2-sm bg-v2-danger-soft border border-v2-danger-border px-3 py-2 text-[12px] text-v2-danger">
            {error}
          </div>
        )}
        {/* G375 — Surface tier validation issues inline so Create button
            disablement isn't a mystery. */}
        {tierValidationError && !error && (
          <div className="rounded-v2-sm bg-v2-warning-soft border border-v2-warning-border px-3 py-2 text-[12px] text-v2-warning-strong">
            {tierValidationError}
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <label className="block col-span-2">
            <span className="block text-[10.5px] uppercase tracking-wider text-v2-ink-muted font-semibold mb-1">Name *</span>
            <input type="text" value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder="e.g. Tier-1 SOC SLA policy"
              className="v2-focus-ring w-full text-[12.5px] border border-v2-border rounded-v2-md px-2.5 py-1.5 bg-v2-surface" />
          </label>
          <label className="block col-span-2">
            <span className="block text-[10.5px] uppercase tracking-wider text-v2-ink-muted font-semibold mb-1">Description</span>
            <textarea value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
              rows={3}
              placeholder="What this policy enforces and when it fires."
              className="v2-focus-ring w-full text-[12.5px] leading-relaxed border border-v2-border rounded-v2-md px-2.5 py-2 bg-v2-surface resize-y" />
          </label>
          {/* Applies-to and Active share one row, and the explanation runs the full width beneath.
              Previously the select sat in column 1 with Active in column 2, so the three-line
              explanation wrapped inside a half-width column and ran directly into the checkbox —
              two unrelated controls reading as one broken block. Baseline-aligning the checkbox to
              the select rather than nudging it with padding also keeps them level at any font size. */}
          <div className="col-span-2 flex items-end gap-4">
            <label className="block flex-1 min-w-0">
              <span className="block text-[10.5px] uppercase tracking-wider text-v2-ink-muted font-semibold mb-1">Applies to</span>
              <select value={form.reach} onChange={(e) => setForm(f => ({ ...f, reach: e.target.value }))}
                className="v2-focus-ring w-full text-[12.5px] border border-v2-border rounded-v2-md px-2.5 py-1.5 bg-v2-surface">
                <option value="TENANT">This tenant</option>
                <option value="ORG">Every tenant in this organization</option>
                <option value="GLOBAL">Every organization in this customer</option>
              </select>
            </label>
            <label className="flex items-center gap-2 shrink-0 h-[30px]">
              <input type="checkbox" checked={form.active} onChange={(e) => setForm(f => ({ ...f, active: e.target.checked }))} />
              <span className="text-[12.5px]">Active</span>
            </label>
          </div>
          <p className="col-span-2 text-[11px] text-v2-ink-muted m-0 -mt-1.5">
            A narrower policy wins: a tenant policy is used ahead of an organization one, and an
            organization policy ahead of a customer-wide one. Wider choices need matching
            administrator rights.
          </p>
        </div>

        <div>
          <div className="flex items-baseline justify-between mb-2">
            <h3 className="text-[12.5px] font-semibold m-0">Escalation tiers · {form.tiers.length}</h3>
            <Button variant="ghost" size="xs" iconLeft={<Plus size={11} />} onClick={addTier}>Add tier</Button>
          </div>
          {form.tiers.length === 0 ? (
            <div className="rounded-v2-sm bg-v2-muted px-3 py-4 text-center text-[12px] text-v2-ink-muted italic">
              No tiers defined. Add a tier to set an SLA and choose who to contact when it is missed.
            </div>
          ) : (
            <ol className="space-y-2">
              {form.tiers.map((t, i) => (
                <li key={i} className="rounded-v2-sm border border-v2-border-subtle bg-v2-surface p-3 space-y-2">
                  <div className="flex items-baseline gap-2">
                    <span className="text-[10.5px] uppercase tracking-wider text-v2-ink-muted font-semibold">Tier {i + 1}</span>
                    <div className="ml-auto flex items-center gap-1">
                      <button onClick={() => moveTier(i, -1)} disabled={i === 0}
                        className="text-[11px] text-v2-ink-muted hover:text-v2-ink disabled:opacity-30" title="Move up">↑</button>
                      <button onClick={() => moveTier(i, +1)} disabled={i === form.tiers.length - 1}
                        className="text-[11px] text-v2-ink-muted hover:text-v2-ink disabled:opacity-30" title="Move down">↓</button>
                      <button onClick={() => removeTier(i)} className="text-[11px] text-v2-danger hover:bg-v2-danger-soft px-1.5 py-0.5 rounded" title="Remove tier">
                        <Trash2 size={11} />
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                    <label className="block col-span-2">
                      <span className="block text-[10px] text-v2-ink-muted mb-0.5">Tier name</span>
                      <input type="text" value={t.name} onChange={(e) => updateTier(i, 'name', e.target.value)}
                        placeholder={`Level ${i + 1}`}
                        className="v2-focus-ring w-full text-[12px] border border-v2-border rounded-v2-md px-2.5 py-1 bg-v2-surface" />
                    </label>
                    <label className="block">
                      <span className="block text-[10px] text-v2-ink-muted mb-0.5">SLA (minutes)</span>
                      <input type="number" min={1} value={t.slaMinutes} onChange={(e) => updateTier(i, 'slaMinutes', e.target.value)}
                        className="v2-focus-ring w-full text-[12px] border border-v2-border rounded-v2-md px-2.5 py-1 bg-v2-surface" />
                    </label>
                  </div>
                  {/* A <div>, NOT a <label>. The free-text input this replaced was correctly
                      wrapped in one; the picker is not an input, it is a group of buttons, and a
                      click on a button inside a <label> is ALSO delivered to the label, which
                      re-dispatches it to the first labelable descendant. The open-dropdown click
                      toggled twice and the panel never appeared — it opened and closed within the
                      same gesture, leaving no trace in the DOM or the console to explain why. */}
                  <div className="block">
                    <span className="block text-[10px] text-v2-ink-muted mb-0.5">Who to contact</span>
                    {/* Was a free-text box. Whatever was typed became the tier's targetValue, and
                        resolveTargetFromTier needs a user id — so every approver ever typed here
                        paged the org admin instead of the person named. */}
                    <ApproverPicker
                      value={t.approvers}
                      disabled={saving}
                      onChange={(next) => updateTier(i, 'approvers', next)}
                    />
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>
    </Slideout>
  );
}

/* ── Inner page component ─────────────────────────────────────────── */

function TeamPageV2Inner() {
  const navigate = useNavigate();
  const platform = usePlatform();
  const { addToast } = useToast();
  const { orgs = [], tenants = [], loadOrganizations, loadTenants } = platform;

  /* G254 — Honor `?tab=` query param so callers like AgentDetailPageV2's
   * "Manage in Teams" button can deep-link to a specific tab (e.g.
   * Assignments) instead of always landing on Users. We also persist the
   * tab choice back to the URL on change so the link stays shareable. */
  const initialTab = (() => {
    try {
      const p = new URLSearchParams(window.location.search).get('tab');
      const allowed = new Set(['users', 'groups', 'connectorGroups', 'assignments', 'escalation', 'accessReview']);
      return p && allowed.has(p) ? p : 'users';
    } catch { return 'users'; }
  })();
  const [tab, setTabState] = useState(initialTab);
  const setTab = (next) => {
    setTabState(next);
    try {
      const params = new URLSearchParams(window.location.search);
      params.set('tab', next);
      window.history.replaceState(null, '', `${window.location.pathname}?${params.toString()}`);
    } catch { /* ignore */ }
  };

  /* G211 — Outer-header Add User / Invite User buttons were still routing
   * to /team?legacy=1 even though G199 added V2 modals inside UsersInventory.
   * Wire them via a pendingAction signal so the outer buttons switch to the
   * Users tab and trigger the inventory's existing modals. */
  const [pendingUserAction, setPendingUserAction] = useState(null);
  const triggerAddUser = () => { setTab('users'); setPendingUserAction('add'); };
  const triggerInvite  = () => { setTab('users'); setPendingUserAction('invite'); };
  const consumePendingUserAction = () => setPendingUserAction(null);

  useEffect(() => {
    if (typeof loadOrganizations === 'function') loadOrganizations();
    if (typeof loadTenants === 'function') loadTenants();
  }, [loadOrganizations, loadTenants]);

  const tabs = [
    { id: 'users',           label: 'Users' },
    { id: 'roles',           label: 'Roles' },
    { id: 'groups',          label: 'Groups' },
    { id: 'connectorGroups', label: 'Connector Groups' },
    { id: 'assignments',     label: 'Assignments' },
    { id: 'escalation',      label: 'Escalation' },
    { id: 'accessReview',    label: 'Access Review' },
  ];

  return (
    <div className="min-h-full bg-v2-page text-v2-ink p-4 sm:p-7 max-w-[1500px] mx-auto">
      {/* ─── Header ───────────────────────────────────────────── */}
      <header className="flex items-start gap-4 mb-5">
        <div>
          <h1 className="text-[22px] font-bold tracking-tight m-0">Team Management</h1>
          <p className="text-[13px] text-v2-ink-secondary mt-0.5">
            Users, groups, and RBAC assignments across your organization
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Button variant="primary" size="sm" iconLeft={<Send size={13} />}
            onClick={triggerInvite}>
            Invite User
          </Button>
          <Button variant="secondary" size="sm" iconLeft={<Plus size={13} />}
            onClick={triggerAddUser}>
            Add User
          </Button>
        </div>
      </header>

      {/* ─── TabNav ───────────────────────────────────────────── */}
      <TabNav
        tabs={tabs}
        activeId={tab}
        onChange={setTab}
        ariaLabel="Team Management sections"
      />
      <div className="my-4" />

      {/* ─── Tab body ───────────────────────────────────────────
       *
       * G359 — Wired ConnectorGroupsInventory / AssignmentsInventory /
       * EscalationInventory into the tab bodies. These components
       * already existed in this file (full V2 inventory pattern with
       * FacetRail, table, slideout) but the tab bodies were rendering
       * "Open in v1" placeholders that hid the production work.
       *
       * Each inventory now ships with V2-native Create/Edit/Delete
       * modals — no more `legacy=1` bounces from inside the row
       * actions either (see the inventory component definitions for
       * the modal wiring). */}
      {tab === 'users'  && <UsersInventory orgs={orgs} tenants={tenants} pendingAction={pendingUserAction} onConsumeAction={consumePendingUserAction} onNavigateToTab={setTab} />}
      {tab === 'roles'  && <RolesInventory />}
      {tab === 'groups' && <GroupsInventory onNavigateToTab={setTab} />}
      {tab === 'connectorGroups' && <ConnectorGroupsInventory />}
      {tab === 'assignments'     && <AssignmentsInventory />}
      {tab === 'escalation'      && <EscalationInventory />}
      {/* G364 — Access Review: V1-parity per-user access audit. */}
      {tab === 'accessReview'    && <AccessReviewInventory orgs={orgs} tenants={tenants} />}
    </div>
  );
}

/* ── Resolver ─────────────────────────────────────────────────────── */
/* G365 — AssignmentsTabBounce (formerly the legacy=1 redirector for
 * /team?agent=<id>) has been deleted. With G359 + G362 the V2
 * AssignmentsInventory is feature-complete enough that operators
 * arriving with an `agent=` filter should land directly on the V2
 * tab. If a future surface needs agent-filtered assignments it should
 * read the URL param in AssignmentsInventory itself rather than
 * bouncing to legacy. */

export default function ResolvedTeamPage(props) {
  /* G108 — render v2 inventory when administer flag on; v1 fallback otherwise.
   * v1 still reachable via /team?legacy=1 for handlers we haven't fully wired
   * (group member CRUD, assignment modals, escalation policy editor). */
  const url = typeof window !== 'undefined' ? new URL(window.location.href) : null;
  const legacy = url?.searchParams?.get('legacy') === '1';
  if (legacy || !isRebirthAdministerEnabled()) return <TeamPage {...props} />;
  return <TeamPageV2Inner {...props} />;
}

export { TeamPageV2Inner };
